import { useState, useEffect, useRef, useMemo } from "react";
import { supabase } from "./supabase";

// ============================================================
// LASKY SL/TP SYNC Cloud — User Dashboard v5.5
// © 2026 Shadow Technologies. All Rights Reserved.
// v5.5: Email permanent reservation policy, indefinite data retention
//       for legal defence (PDPA 2010 Section 39), updated Section 8.7
// v5.4: Full PDPA 2010 compliance, comprehensive Privacy Policy,
//       Malaysian law citations (PDPA, CMA, CCA, Contracts Act 1950,
//       Capital Markets Act, Copyright Act, Trade Marks Act)
// v5.3: Hybrid server selection — dropdown + manual with autocomplete
// v5.2: [DEPRECATED] Admin button — REMOVED in S11 (25 Apr 2026)
//       Admin completely isolated to superadmin.shadowtechlab.com
//       Zero admin references in user-facing bundle
// v5.1: Splash screen, cache fix, full English, rebrand
// ============================================================

const API_URL = "https://api.laskysync.com";
const BRAND = "LASKY SL/TP SYNC";
const PLANS = {
  starter_7d: { label: "Starter", days: 7, price: "$15", priceNum: 15, color: "#94A3B8", desc: "7-day starter plan" },
  basic_30d: { label: "Basic", days: 30, price: "$55", priceNum: 55, color: "#22D3EE", desc: "1 month full access" },
  popular_3m: { label: "Popular", days: 90, price: "$150", priceNum: 150, color: "#A78BFA", desc: "3 months — save 9%" },
  // Lifetime: display-only entry for admin-gifted users (no price/desc to prevent source leak)
  lifetime: { label: "Lifetime", days: null, color: "#F59E0B" },
};
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const IDLE_EVENTS = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];
const REMEMBER_KEY = "laskyslsync_remember";
const MT5_CACHE_KEY = "laskyslsync_mt5_cache";

// ============================================================
// S3 PINTU BESI TEBAL (22 Apr 2026) - Password Strength Helper
// ============================================================
// Shared helper for signup + change password pages.
// Returns { score, percent, label, color, checks, allMet }
//
// Rules enforced (must match backend _helpers.js passwordSchema):
//   - Min 8 characters
//   - At least 1 uppercase (A-Z)
//   - At least 1 lowercase (a-z)
//   - At least 1 number (0-9)
//   - At least 1 symbol (non-alphanumeric)
//
// Note: Backend additionally runs zxcvbn + HIBP check on submit.
// Frontend visual may show "Strong" but backend can still reject
// with reason (dictionary word, leaked password, etc).
// ============================================================
function checkPasswordStrength(pw) {
  if (!pw) {
    return {
      score: 0, percent: 0, label: "Empty", color: "#475569",
      checks: { len: false, upper: false, lower: false, number: false, symbol: false },
      allMet: false,
    };
  }
  const checks = {
    len: pw.length >= 8,
    upper: /[A-Z]/.test(pw),
    lower: /[a-z]/.test(pw),
    number: /[0-9]/.test(pw),
    symbol: /[^A-Za-z0-9]/.test(pw),
  };
  const allMet = checks.len && checks.upper && checks.lower && checks.number && checks.symbol;

  let x = 0;
  if (checks.len) x += 20;
  if (pw.length >= 12) x += 10;
  if (pw.length >= 16) x += 10;
  if (checks.upper) x += 15;
  if (checks.lower) x += 10;
  if (checks.number) x += 15;
  if (checks.symbol) x += 20;
  const percent = Math.min(100, x);

  let label = "Weak", color = "#EF4444";
  if (allMet && percent >= 85) { label = "Strong"; color = "#22C55E"; }
  else if (allMet && percent >= 65) { label = "Good"; color = "#22D3EE"; }
  else if (percent >= 45) { label = "Medium"; color = "#F59E0B"; }

  return { score: Math.floor(percent / 25), percent, label, color, checks, allMet };
}

function getSavedCredentials() { try { const r = localStorage.getItem(REMEMBER_KEY); if (!r) return null; const p = JSON.parse(r); if (p?.email) { if (p.password) { try { localStorage.setItem(REMEMBER_KEY, JSON.stringify({ email: p.email })); } catch {} } return { email: p.email }; } } catch {} return null; }
function saveCredentials(e) { try { localStorage.setItem(REMEMBER_KEY, JSON.stringify({ email: e })); } catch {} }
function clearSavedCredentials() { try { localStorage.removeItem(REMEMBER_KEY); } catch {} }
function getCachedMT5Data(ue) { try { const r = localStorage.getItem(MT5_CACHE_KEY); if (!r) return null; const p = JSON.parse(r); if (p?.userEmail === ue && p.accounts?.length > 0) return p.accounts; } catch {} return null; }
function saveMT5Cache(ue, accs) { try { const c = accs.filter(a => a.broker && a.server); if (c.length > 0) localStorage.setItem(MT5_CACHE_KEY, JSON.stringify({ userEmail: ue, accounts: accs, cachedAt: Date.now() })); } catch {} }
function clearMT5Cache() { try { localStorage.removeItem(MT5_CACHE_KEY); } catch {} }

function CountdownTimer({ expiryDate }) {
  const [tl, setTl] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => { if (!expiryDate) return; const tick = () => { const diff = Math.max(0, new Date(expiryDate).getTime() - Date.now()); setTl({ d: Math.floor(diff / 86400000), h: Math.floor((diff % 86400000) / 3600000), m: Math.floor((diff % 3600000) / 60000), s: Math.floor((diff % 60000) / 1000) }); }; tick(); const iv = setInterval(tick, 1000); return () => clearInterval(iv); }, [expiryDate]);
  if (!expiryDate) return <span style={{ color: "#F59E0B", fontWeight: 700, fontSize: 16 }}>LIFETIME</span>;
  const expired = tl.d === 0 && tl.h === 0 && tl.m === 0 && tl.s === 0;
  if (expired) return <span style={{ color: "#EF4444", fontWeight: 700, fontSize: 16 }}>EXPIRED</span>;
  const pad = n => String(n).padStart(2, "0");
  const box = { display: "inline-flex", flexDirection: "column", alignItems: "center", padding: "6px 10px", borderRadius: 8, background: "#ffffff06", minWidth: 44 };
  return (<div style={{ display: "flex", alignItems: "center", gap: 2 }}>
    <div style={box}><span style={{ fontSize: 20, fontWeight: 800, color: "#F1F5F9", fontFamily: "'JetBrains Mono', monospace" }}>{pad(tl.d)}</span><span style={{ fontSize: 9, color: "#475569", fontWeight: 500, marginTop: 2 }}>DAYS</span></div><span style={{ color: "#334155", fontSize: 20, fontWeight: 300, padding: "0 4px" }}>:</span>
    <div style={box}><span style={{ fontSize: 20, fontWeight: 800, color: "#F1F5F9", fontFamily: "'JetBrains Mono', monospace" }}>{pad(tl.h)}</span><span style={{ fontSize: 9, color: "#475569", fontWeight: 500, marginTop: 2 }}>HRS</span></div><span style={{ color: "#334155", fontSize: 20, fontWeight: 300, padding: "0 4px" }}>:</span>
    <div style={box}><span style={{ fontSize: 20, fontWeight: 800, color: "#F1F5F9", fontFamily: "'JetBrains Mono', monospace" }}>{pad(tl.m)}</span><span style={{ fontSize: 9, color: "#475569", fontWeight: 500, marginTop: 2 }}>MIN</span></div><span style={{ color: "#334155", fontSize: 20, fontWeight: 300, padding: "0 4px" }}>:</span>
    <div style={box}><span style={{ fontSize: 20, fontWeight: 800, color: "#F1F5F9", fontFamily: "'JetBrains Mono', monospace" }}>{pad(tl.s)}</span><span style={{ fontSize: 9, color: "#475569", fontWeight: 500, marginTop: 2 }}>SEC</span></div>
  </div>);
}

function StatusDot({ on, label, color }) {
  const c = on ? (color || "#22C55E") : "#475569";
  return (<div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 14px", borderRadius: 8, background: c + "08", border: `1px solid ${c}15` }}>
    <div style={{ width: 10, height: 10, borderRadius: "50%", background: c, boxShadow: on ? `0 0 8px ${c}60` : "none", animation: on ? "pulse 2s infinite" : "none" }} />
    <span style={{ color: c, fontSize: 12, fontWeight: 600 }}>{label}</span>
    <span style={{ color: on ? "#22C55E" : "#EF4444", fontSize: 11, fontWeight: 700, marginLeft: 4 }}>{on ? "ON" : "OFF"}</span>
  </div>);
}

function BackButton({ onClick }) {
  return <button onClick={onClick} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8, marginBottom: 20, background: "#ffffff04", border: "1px solid #ffffff0a", color: "#94A3B8", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" }}
    onMouseEnter={e => { e.currentTarget.style.background = "#ffffff08"; e.currentTarget.style.color = "#E2E8F0"; }}
    onMouseLeave={e => { e.currentTarget.style.background = "#ffffff04"; e.currentTarget.style.color = "#94A3B8"; }}>← Back</button>;
}

// ============================================================
// WOLF LOGO — Reusable brand logo with cyan glow B+ animation
// Replaces legacy cyan dot+BRAND pattern across splash/login/dashboard.
// Glow: 2-layer drop-shadow (inner bright + outer halo), pulse 2.4s.
// Usage: <WolfLogo size={32} /> for inline header, <WolfLogo size={70} /> for centered card.
// ============================================================
function WolfLogo({ size = 38, style = {} }) {
  // Inject keyframes once globally on first render (idempotent — runs once total)
  useEffect(() => {
    if (document.getElementById("wolf-pulse-keyframes")) return;
    const styleTag = document.createElement("style");
    styleTag.id = "wolf-pulse-keyframes";
    styleTag.innerHTML = `
      @keyframes wolfPulse {
        0%, 100% {
          filter: drop-shadow(0 0 4px rgba(34, 211, 238, 0.75)) drop-shadow(0 0 12px rgba(34, 211, 238, 0.32));
        }
        50% {
          filter: drop-shadow(0 0 6px rgba(34, 211, 238, 1)) drop-shadow(0 0 18px rgba(34, 211, 238, 0.6));
        }
      }
    `;
    document.head.appendChild(styleTag);
  }, []);

  return (
    <img
      src="/wolf-logo.png"
      alt="LASKY"
      style={{
        width: size,
        height: "auto",
        animation: "wolfPulse 2.4s ease-in-out infinite",
        ...style,
      }}
    />
  );
}

// ============================================================
// SPLASH SCREEN — Animated intro before login
// ============================================================
function SplashScreen({ onDone }) {
  const [phase, setPhase] = useState(0); // 0=glow, 1=text, 2=tagline, 3=fade
  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 400);
    const t2 = setTimeout(() => setPhase(2), 1200);
    const t3 = setTimeout(() => setPhase(3), 2600);
    const t4 = setTimeout(() => onDone(), 3400);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
  }, []);

  // v5.5: Galaxy entrance — 120 particles, weighted color/size/animation mix
  const particles = useMemo(() => {
    const colors = [
      { c: '#22D3EE', w: 50 }, // cyan (brand)
      { c: '#F1F5F9', w: 25 }, // white (stars)
      { c: '#FDE68A', w: 15 }, // gold light
      { c: '#F59E0B', w: 10 }, // gold accent
    ];
    const pickColor = () => {
      const r = Math.random() * 100; let sum = 0;
      for (const c of colors) { sum += c.w; if (r < sum) return c.c; }
      return colors[0].c;
    };
    return [...Array(120)].map((_, i) => {
      const isBig = Math.random() < 0.15;
      const size = isBig ? (3.5 + Math.random() * 1.5) : (0.8 + Math.random() * 2);
      const color = pickColor();
      const maxOp = isBig ? (0.75 + Math.random() * 0.2) : (0.35 + Math.random() * 0.45);
      const glow = isBig ? `0 0 ${size * 3}px ${color}80, 0 0 ${size * 6}px ${color}40` : (size > 2 ? `0 0 ${size * 2}px ${color}60` : 'none');
      const b = Math.random();
      const animName = b < 0.35 ? 'driftA' : b < 0.55 ? 'driftB' : 'twinkle';
      const duration = animName === 'twinkle' ? (1.8 + Math.random() * 2.5) : animName === 'driftB' ? (6 + Math.random() * 5) : (5 + Math.random() * 4);
      const delay = Math.random() * 3;
      return { i, size, color, maxOp, glow, animName, duration, delay, left: Math.random() * 100, top: Math.random() * 100 };
    });
  }, []);

  return (
    <div style={{ minHeight: "100vh", background: "#06060B", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Outfit', sans-serif", opacity: phase === 3 ? 0 : 1, transition: "opacity 0.8s ease" }}>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />
      {/* Far ambient gold glow */}
      <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 800, height: 800, borderRadius: "50%", background: "radial-gradient(circle, #F59E0B08 0%, transparent 60%)", opacity: phase >= 1 ? 1 : 0, transition: "opacity 1.8s ease", pointerEvents: "none" }} />
      {/* Mid cyan core glow */}
      <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 500, height: 500, borderRadius: "50%", background: phase >= 0 ? "radial-gradient(circle, #22D3EE12 0%, #0891B208 30%, transparent 70%)" : "transparent", transition: "all 1.5s ease", opacity: phase >= 1 ? 1 : 0, pointerEvents: "none" }} />
      {/* Galaxy particles — 120 weighted mix */}
      <div style={{ position: "fixed", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
        {particles.map(p => (
          <div key={p.i} style={{ position: "absolute", width: p.size, height: p.size, borderRadius: "50%", background: p.color, boxShadow: p.glow, left: `${p.left}%`, top: `${p.top}%`, opacity: phase >= 1 ? p.maxOp : 0, animation: phase >= 1 ? `${p.animName} ${p.duration}s ease-in-out infinite ${p.delay}s` : "none", transition: "opacity 1.2s ease", ['--max-op']: p.maxOp }} />
        ))}
      </div>
      <div style={{ textAlign: "center", position: "relative", zIndex: 1 }}>
        {/* Wolf logo with cyan pulse glow (replaces legacy cyan dot) */}
        <div style={{ marginBottom: 28, display: "flex", justifyContent: "center", opacity: phase >= 0 ? 1 : 0, transform: phase >= 0 ? "scale(1)" : "scale(0)", transition: "all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)" }}>
          <WolfLogo size={120} />
        </div>
        {/* Brand name */}
        <div style={{ overflow: "hidden", marginBottom: 12 }}>
          <h1 style={{ fontSize: 36, fontWeight: 800, letterSpacing: 4, color: "#F1F5F9", margin: 0, opacity: phase >= 1 ? 1 : 0, transform: phase >= 1 ? "translateY(0)" : "translateY(30px)", transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1)" }}>
            {BRAND}
          </h1>
        </div>
        {/* Tagline */}
        <p style={{ fontSize: 14, fontWeight: 300, color: "#22D3EE", letterSpacing: 3, textTransform: "uppercase", margin: 0, opacity: phase >= 2 ? 1 : 0, transform: phase >= 2 ? "translateY(0)" : "translateY(15px)", transition: "all 0.6s ease" }}>
          Cloud SL/TP Sync Engine
        </p>
        {/* Loading bar with gradient cyan → cyan → gold */}
        <div style={{ width: 120, height: 2, background: "#ffffff08", borderRadius: 2, margin: "32px auto 0", overflow: "hidden" }}>
          <div style={{ height: "100%", background: "linear-gradient(90deg, #0891B2, #22D3EE, #F59E0B)", borderRadius: 2, width: phase >= 2 ? "100%" : "0%", transition: "width 1.4s ease" }} />
        </div>
      </div>
      <style>{`
        @keyframes driftA { 0%,100%{transform:translate(0,0) scale(1);opacity:var(--max-op,0.7)} 25%{transform:translate(6px,-18px) scale(1.3);opacity:0.1} 50%{transform:translate(-4px,-12px) scale(1);opacity:var(--max-op,0.7)} 75%{transform:translate(-8px,-22px) scale(0.7);opacity:0.3} }
        @keyframes driftB { 0%,100%{transform:translate(0,0) scale(1);opacity:var(--max-op,0.6)} 50%{transform:translate(-10px,-20px) scale(1.4);opacity:0.15} }
        @keyframes twinkle { 0%,100%{opacity:var(--max-op,0.8);transform:scale(1)} 50%{opacity:0.05;transform:scale(0.4)} }
        @keyframes corepulse { 0%,100%{transform:scale(1);box-shadow:0 0 30px #22D3EE80, 0 0 60px #22D3EE40, 0 0 90px #22D3EE20} 50%{transform:scale(1.15);box-shadow:0 0 40px #22D3EEB0, 0 0 80px #22D3EE60, 0 0 120px #22D3EE30} }
      `}</style>
    </div>
  );
}

// ============================================================
// LICENSE ABUSE JAIL SCREEN
// Fullscreen lockdown when user hits attempt 10.
// Shows inline ticket form (3 fields) → ThankYou → force logout.
// NO navigation, NO escape. Logout only.
// ============================================================
function LicenseAbuseJailScreen({ session, onLogout }) {
  const [view, setView] = useState("locked"); // "locked" | "form" | "thankyou"
  const [licenseKey, setLicenseKey] = useState("");
  const [description, setDescription] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const userEmail = session?.user?.email || "";

  const handleSend = async () => {
    if (!licenseKey.trim() || !description.trim() || description.trim().length < 10) {
      setSendError("Please fill in all fields (description min 10 chars).");
      return;
    }
    setSending(true); setSendError("");
    try {
      const r = await fetch(`${API_URL}/api/license/abuse-ticket`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ licenseKeyTried: licenseKey.trim(), description: description.trim() }),
      });
      const d = await r.json();
      if (!r.ok) { setSendError(d.error || "Failed to send. Please try again."); setSending(false); return; }
      setView("thankyou");
    } catch (e) {
      setSendError("Network error. Please try again.");
      setSending(false);
    }
  };

  const inputStyle = { width: "100%", padding: "12px 14px", background: "#ffffff04", border: "1px solid #ffffff0a", borderRadius: 10, color: "#E2E8F0", fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" };

  return (
    <div style={{ minHeight: "100vh", background: "#06060B", color: "#E2E8F0", fontFamily: "'Outfit','DM Sans',sans-serif", display: "flex", flexDirection: "column" }}>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />
      {/* Header — Logout only */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 28px", borderBottom: "1px solid #EF444420", background: "#06060B" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <WolfLogo size={32} />
          <span style={{ fontWeight: 700, fontSize: 15, letterSpacing: 1.5 }}>{BRAND}</span>
          <span style={{ color: "#334155", fontSize: 12, fontWeight: 300 }}>CLOUD</span>
        </div>
        <button onClick={onLogout} style={{ background: "none", border: "1px solid #334155", color: "#94A3B8", padding: "6px 14px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>Logout</button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 32 }}>
        <div style={{ maxWidth: 480, width: "100%", padding: 36, borderRadius: 20, background: "#0A0A12", border: "1px solid #EF444430" }}>

          {/* VIEW: LOCKED */}
          {view === "locked" && (<>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <div style={{ fontSize: 52, marginBottom: 16 }}>🔒</div>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: "#F1F5F9", marginBottom: 10 }}>Account Locked</h2>
              <p style={{ color: "#CBD5E1", fontSize: 13, lineHeight: 1.7, marginBottom: 8 }}>Your account has been locked due to too many failed license activation attempts.</p>
              <p style={{ color: "#CBD5E1", fontSize: 13, lineHeight: 1.7 }}>To unlock your account, submit a support ticket below. Our team will contact you by email.</p>
            </div>
            <div style={{ padding: "12px 16px", borderRadius: 10, background: "#EF444410", border: "1px solid #EF444425", marginBottom: 24, textAlign: "center" }}>
              <div style={{ color: "#FCA5A5", fontSize: 13, fontWeight: 600 }}>Access to all features has been suspended.</div>
              <div style={{ color: "#94A3B8", fontSize: 12, marginTop: 4 }}>Only support contact and logout are available.</div>
            </div>
            <button onClick={() => setView("form")} style={{ width: "100%", padding: "14px", borderRadius: 10, background: "linear-gradient(135deg, #0891B2, #22D3EE)", color: "#fff", fontSize: 14, fontWeight: 700, border: "none", cursor: "pointer", fontFamily: "inherit" }}>
              ✉ Contact Support
            </button>
          </>)}

          {/* VIEW: FORM */}
          {view === "form" && (<>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>📋</div>
              <h2 style={{ fontSize: 18, fontWeight: 800, color: "#F1F5F9", marginBottom: 6 }}>Submit Support Ticket</h2>
              <p style={{ color: "#94A3B8", fontSize: 12, lineHeight: 1.6 }}>Our team will review and contact you by email within 24 hours.</p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={{ color: "#CBD5E1", fontSize: 12, fontWeight: 600, display: "block", marginBottom: 6 }}>Your Email (auto-filled)</label>
                <input type="text" value={userEmail} readOnly style={{ ...inputStyle, color: "#94A3B8", cursor: "not-allowed" }} />
              </div>
              <div>
                <label style={{ color: "#CBD5E1", fontSize: 12, fontWeight: 600, display: "block", marginBottom: 6 }}>License Key You Tried</label>
                <input type="text" value={licenseKey} onChange={e => setLicenseKey(e.target.value.toUpperCase())} placeholder="e.g. AD-XXXX-XXXX-XXXX-XXXX" style={{ ...inputStyle, fontFamily: "monospace", letterSpacing: 2 }} />
              </div>
              <div>
                <label style={{ color: "#CBD5E1", fontSize: 12, fontWeight: 600, display: "block", marginBottom: 6 }}>Description</label>
                <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Explain your situation briefly — where you got the key, what error you saw..." rows={4} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6 }} />
              </div>
              {sendError && <div style={{ padding: "10px 14px", borderRadius: 8, background: "#EF444410", border: "1px solid #EF444430", color: "#FCA5A5", fontSize: 12 }}>{sendError}</div>}
              <button onClick={handleSend} disabled={sending} style={{ width: "100%", padding: "13px", borderRadius: 10, background: sending ? "#1E293B" : "linear-gradient(135deg, #0891B2, #22D3EE)", color: sending ? "#475569" : "#fff", fontSize: 14, fontWeight: 700, border: "none", cursor: sending ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                {sending ? "Sending..." : "Send Ticket →"}
              </button>
            </div>
          </>)}

          {/* VIEW: THANK YOU */}
          {view === "thankyou" && (<>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 52, marginBottom: 16 }}>✅</div>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: "#F1F5F9", marginBottom: 12 }}>Ticket Submitted</h2>
              <p style={{ color: "#CBD5E1", fontSize: 13, lineHeight: 1.7, marginBottom: 28 }}>Thank you. We will contact you by email. Please check your email for any update from us.</p>
              <button onClick={onLogout} style={{ width: "100%", padding: "13px", borderRadius: 10, background: "#1E293B", border: "1px solid #334155", color: "#E2E8F0", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                OKAY
              </button>
            </div>
          </>)}

        </div>
      </div>
      <style>{`@keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}

// ============================================================
// MAIN APP
// ============================================================
export default function UserDashboard() {
  const [showSplash, setShowSplash] = useState(true);
  const [page, setPage] = useState("login"); const [prevPage, setPrevPage] = useState(null);
  const [user, setUser] = useState(null); const [session, setSession] = useState(null);
  const [logoutReason, setLogoutReason] = useState(null); const [loadingAuth, setLoadingAuth] = useState(true);
  const [idleWarning, setIdleWarning] = useState(false); const [idleCountdown, setIdleCountdown] = useState(0);
  const [connectingLicenseId, setConnectingLicenseId] = useState(null);
  const [systemStatus, setSystemStatus] = useState({ slEngine: true, tpEngine: true }); // v5.5: Global kill switch status
  const [supportUnread, setSupportUnread] = useState(0); // V6.0 Phase 7 — badge count
  // Step2 MetaAPI Leak Fix: post-connect reminder popup
  const [showPostConnectReminder, setShowPostConnectReminder] = useState(false);
  const [expiredLicense, setExpiredLicense] = useState(null); // V6.0 — Hard Lock

  const nav = (p) => { setPrevPage(page); setPage(p); };
  const goBack = () => { if (prevPage) { setPage(prevPage); setPrevPage(null); } else setPage("home"); };

  // V6.0 Phase 7 — Support unread badge: poll every 60s when logged in
  useEffect(() => {
    if (!session?.access_token) return;
    const fetchUnread = async () => {
      try {
        const since = localStorage.getItem("lasky_support_last_check") || new Date(0).toISOString();
        const res = await fetch(`${API_URL}/api/support/tickets/unread-count?since=${encodeURIComponent(since)}`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (res.ok) { const d = await res.json(); setSupportUnread(d.count || 0); }
      } catch {}
    };
    fetchUnread();
    const iv = setInterval(fetchUnread, 60000);
    return () => clearInterval(iv);
  }, [session]);

  // --- C2 Race Fix: Refs mirror state so fetch interceptor closure
  // can read LATEST values (useEffect with [] captures initial state only).
  // Without refs, interceptor would always see page="login" and never
  // skip correctly when user navigates mid-verify.
  const pageRef = useRef(page);
  const pendingTwoFARef = useRef(null);
  useEffect(() => { pageRef.current = page; }, [page]);

  // --- C2: Global fetch interceptor for TWOFA_REQUIRED/EXPIRED ---
  // When backend rejects due to missing or expired 2FA session,
  // auto-signOut user and redirect to login with proper message.
  // This applies to ALL fetch() calls in the app — no need to
  // modify each one individually.
  //
  // SKIP LOGIC (race fix 20 Apr 2026): during login/2FA flow, Supabase's
  // signInWithPassword triggers onAuthStateChange → loadUserData hits API
  // which returns 403 TWOFA_REQUIRED (correctly — user hasn't entered TOTP
  // yet). Without skip, interceptor would force-logout user MID-verification.
  // Exempt pages: login flow, signup flow, active pending-2FA state.
  useEffect(() => {
    const SKIP_PAGES = new Set(["login", "login_2fa", "login_success", "agreement", "check_email"]);
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      const url = typeof args[0] === "string" ? args[0] : args[0]?.url || "";
      if (response.status === 403 && url.includes(API_URL)) {
        try {
          const clone = response.clone();
          const body = await clone.json();
          if (body?.code === "TWOFA_REQUIRED" || body?.code === "TWOFA_EXPIRED") {
            // Skip auto-logout if user is mid-login/2FA — would kick before TOTP entry
            if (SKIP_PAGES.has(pageRef.current) || pendingTwoFARef.current) {
              return response;
            }
            setTimeout(() => handleLogout(body.code.toLowerCase()), 0);
          }
        } catch { /* non-JSON 403, ignore */ }
      }
      return response;
    };
    return () => { window.fetch = originalFetch; };
  }, []);

  useEffect(() => {
    if (!session || page === "login" || page === "login_success") return;
    let it, wt, ci; const WS = 60;
    const reset = () => { if (it) clearTimeout(it); if (wt) clearTimeout(wt); if (ci) clearInterval(ci); setIdleWarning(false); setIdleCountdown(0);
      wt = setTimeout(() => { setIdleWarning(true); setIdleCountdown(WS); let r = WS; ci = setInterval(() => { r--; setIdleCountdown(r); if (r <= 0) clearInterval(ci); }, 1000); }, IDLE_TIMEOUT_MS - WS * 1000);
      it = setTimeout(() => handleLogout(true), IDLE_TIMEOUT_MS); };
    IDLE_EVENTS.forEach(e => window.addEventListener(e, reset, { passive: true })); reset();
    return () => { IDLE_EVENTS.forEach(e => window.removeEventListener(e, reset)); clearTimeout(it); clearTimeout(wt); clearInterval(ci); };
  }, [session, page]);

  // S12: Dynamic per-page browser tab title (fingerprint removal)
  // Format: "{PageName} | LASKY SL/TP SYNC" — fallback "LASKY SL/TP SYNC"
  useEffect(() => {
    const TITLE_MAP = {
      login: "Sign In",
      login_2fa: "Two-Factor Authentication",
      login_success: "Welcome Back",
      agreement: "User Agreement",
      check_email: "Verify Email",
      reset_password: "Reset Password",
      activate: "Activate License",
      connect: "Connect MT5",
      home: "Dashboard",
      history: "History",
      plans: "Plans",
      profile: "Profile",
      support: "Support",
    };
    const suffix = "LASKY SL/TP SYNC";
    const label = TITLE_MAP[page];
    document.title = label ? `${label} | ${suffix}` : suffix;
  }, [page]);

  useEffect(() => {
    let m = true; let lt = 0;
    // SECURITY (P0 auth bypass fix): Detect password recovery URL param FIRST
    // before any auth logic. URL format:
    //   /auth/reset-password?token_hash=xxx&type=recovery
    // We do NOT auto-verify the token here — that's done in ResetPasswordPage
    // after user inputs new password (atomic verifyOtp + updateUser flow).
    // This prevents the action_link auto-login bypass where merely hitting the
    // URL would create a session.
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const tokenHash = urlParams.get('token_hash');
      const tokenType = urlParams.get('type');
      const pathOk = window.location.pathname === '/auth/reset-password'
                  || window.location.pathname === '/reset-password';
      if (tokenHash && tokenType === 'recovery' && pathOk) {
        // Stash token in memory for ResetPasswordPage to consume on submit
        window.__RECOVERY_TOKEN_HASH = tokenHash;
        setPage('reset_password');
        setLoadingAuth(false);
        return; // Skip pre-flight, skip session load, skip onAuthStateChange setup
      }
    } catch { /* noop — fall through to normal auth flow */ }

    // authGate: blocks onAuthStateChange from calling loadUserData while
    // pre-flight 2FA check is running. Prevents race where getSession() path
    // detects 403 and signs out, but SIGNED_IN event fires simultaneously
    // and loadUserData fires anyway → user lands on /activate (bypass).
    let authGate = true;
    supabase.auth.getSession().then(async ({ data: { session: s } }) => {
      if (!s || !m) { if (m) { authGate = false; setLoadingAuth(false); } return; }
      // C2 BYPASS FIX (20 Apr 2026): On page refresh, Supabase restores session
      // from localStorage. If user logged in but refreshed BEFORE entering TOTP,
      // we MUST NOT proceed to loadUserData — that would leak access.
      // Pre-flight check: verify 2FA session exists for this token before any
      // protected API call.
      try {
        const r = await fetch(`${API_URL}/api/auth/2fa-required`, { headers: { Authorization: `Bearer ${s.access_token}` } });
        if (!r.ok) throw new Error("2FA check failed");
        const d = await r.json();
        if (d.required) {
          // User has 2FA ON — probe one protected endpoint to check if session verified
          const probe = await fetch(`${API_URL}/api/license/mine`, { headers: { Authorization: `Bearer ${s.access_token}` } });
          if (probe.status === 403) {
            // Token not in verified_2fa_sessions → user refreshed mid-2FA.
            // Force signOut + route to login. authGate stays TRUE so
            // onAuthStateChange(SIGNED_OUT→SIGNED_IN race) cannot fire loadUserData.
            try { await supabase.auth.signOut(); } catch {}
            setLogoutReason("twofa_required");
            setUser(null);
            setSession(null);
            setPage("login");
            setLoadingAuth(false);
            return;
          }
        }
      } catch {
        // If probe fails (network), fail closed — force re-login.
        try { await supabase.auth.signOut(); } catch {}
        setUser(null);
        setSession(null);
        setPage("login");
        setLoadingAuth(false);
        return;
      }
      // 2FA check passed → safe to load user data and open gate
      if (m) { setSession(s); lt = Date.now(); authGate = false; loadUserData(s, true); }
      if (m) setLoadingAuth(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      if (!m) return;
      // NOTE: PASSWORD_RECOVERY handler removed (23 Apr 2026) — event only fires
      // when Supabase auto-creates a session from action_link, which we no longer
      // use. Recovery is now handled via URL param detection on mount → atomic
      // verifyOtp + updateUser in ResetPasswordPage.submit(). See generateRecoveryLink().
      // If pre-flight still running or signaled 2FA-required, don't interfere.
      // Pre-flight owns the initial session resolution; this listener only
      // handles events AFTER pre-flight completes (login, logout, refresh).
      if (authGate) return;
      setSession(s);
      if (s) {
        // C2 Race Fix: signInWithPassword triggers SIGNED_IN event BEFORE user
        // enters TOTP. If we call loadUserData here, API returns 403 TWOFA_REQUIRED
        // (correctly, since verified_2fa_sessions row not created yet) and interceptor
        // would kick user. Skip loadUserData when user is mid-verify; handle2FALoginVerify
        // will call loadUserData itself after TOTP success.
        if (pendingTwoFARef.current) return;
        if (Date.now() - lt < 30000) return;
        lt = Date.now();
        loadUserData(s, false);
      } else {
        setUser(null); setPage("login");
      }
    });
    return () => { m = false; subscription.unsubscribe(); };
  }, []);

  // v5.5: Auto-refresh dashboard every 3 sec when on Home page
  // Skip when tab hidden (save backend load), pause on other pages
  // v6.4: Split into 2 tracks to avoid rate limit (60 req/min hit at edge)
  //   - Fast (3s):  /api/mt5/status      → live trading data (P/L, positions, SL/TP)
  //   - Slow (60s): /api/license/mine    → semi-static license info (key, plan, expiry)
  // Lockout status removed from polling: checked once on mount + after activate fail.
  useEffect(() => {
    if (!session || page !== "home") return;
    const iv = setInterval(() => {
      if (document.hidden) return;
      loadMT5Live(session);
    }, 3000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, page]);

  // Slow track — license data refresh every 60 sec (timer is local, not backend-bound)
  useEffect(() => {
    if (!session || page !== "home") return;
    const iv = setInterval(() => {
      if (document.hidden) return;
      loadLicenseData(session);
    }, 60000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, page]);

  // v5.5: Fetch global system status every 30 sec (Railway env-controlled)
  // Public endpoint, no auth — light call, safe to poll
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/api/system/status`);
        if (res.ok) { const data = await res.json(); setSystemStatus(data); }
      } catch {}
    };
    fetchStatus();
    const iv = setInterval(fetchStatus, 30000);
    return () => clearInterval(iv);
  }, []);

  // v6.4: SPLIT POLLING HELPERS
  //
  // loadUserData    — full load (license + lockout + mt5) — used on MOUNT only
  // loadMT5Live     — fast poll (mt5 only) — every 3s, live trading data
  // loadLicenseData — slow poll (license only) — every 60s, semi-static
  //
  // Lockout-status check stays in loadUserData (mount check) + after activation fail.
  // No more lockout polling on each cycle = saves 20 req/min.

  // Fast polling — MT5 live data only (positions, P/L, SL/TP, balance)
  const loadMT5Live = async (sess) => {
    try {
      const mr = await fetch(`${API_URL}/api/mt5/status`, { headers: { Authorization: `Bearer ${sess.access_token}` } });
      if (mr.status === 403) {
        try { await supabase.auth.signOut(); } catch {}
        setLogoutReason("twofa_required");
        setPage("login");
        return;
      }
      if (!mr.ok) return; // silent — keep displaying last good data on transient error
      const md = await mr.json();
      const conns = md.accounts || [];
      // Merge MT5 conns into existing user.accounts (preserve license info)
      setUser(prev => {
        if (!prev?.accounts) return prev;
        const merged = prev.accounts.map(a => {
          const c = conns.find(x => x.licenseId === a.license?.id || x.license?.id === a.license?.id);
          if (!c) return { ...a, connectionId: null, broker: null, server: null, mt5Account: null, connectionStatus: null, sync: null, breakdown: null, positions: [], slEnabled: false, tpEnabled: false };
          return { ...a, connectionId: c.id, broker: c.broker, server: c.server, mt5Account: c.mt5Account, connectionStatus: c.connectionStatus, sync: c.sync, breakdown: c.breakdown, positions: c.positions || [], slEnabled: c.slEnabled ?? false, tpEnabled: c.tpEnabled ?? false };
        });
        if (merged.some(a => a.broker && a.server)) saveMT5Cache(prev.email, merged);
        return { ...prev, accounts: merged };
      });
    } catch (e) {
      // Silent — keep last good data
    }
  };

  // Slow polling — license data only (key, plan, expiry, status)
  const loadLicenseData = async (sess) => {
    try {
      const lr = await fetch(`${API_URL}/api/license/mine`, { headers: { Authorization: `Bearer ${sess.access_token}` } });
      if (lr.status === 403) {
        try { await supabase.auth.signOut(); } catch {}
        setLogoutReason("twofa_required");
        setPage("login");
        return;
      }
      if (!lr.ok) return; // silent — keep last good data
      const ld = await lr.json();
      const lics = ld.licenses || [];
      // Merge license updates into existing user.accounts (preserve MT5 conn data)
      setUser(prev => {
        if (!prev?.accounts) return prev;
        const merged = lics.map(l => {
          const existing = prev.accounts.find(a => a.license?.id === l.id);
          if (existing) {
            return { ...existing, license: { id: l.id, key: l.key, plan: l.plan, status: l.status, expiry: l.expiry_date, platform: l.platform, activatedAt: l.activated_at } };
          }
          // New license added (eg activated in another tab) — minimal entry
          return { license: { id: l.id, key: l.key, plan: l.plan, status: l.status, expiry: l.expiry_date, platform: l.platform, activatedAt: l.activated_at }, connectionId: null, broker: null, server: null, mt5Account: null, connectionStatus: null, sync: null, breakdown: null, positions: [], slEnabled: false, tpEnabled: false };
        });
        return { ...prev, accounts: merged };
      });
      // V6.0 — Check for expired licenses on slow refresh
      const expired = lics.find(l => l.status === 'expired' || (l.expiry_date && new Date(l.expiry_date).getTime() <= Date.now()));
      if (expired) setExpiredLicense({ id: expired.id, key: expired.key, plan: expired.plan, status: expired.status, expiry: expired.expiry_date });
    } catch (e) {
      // Silent
    }
  };

  const loadUserData = async (sess, init = false) => {
    const ue = sess.user.email;
    try {
      const lr = await fetch(`${API_URL}/api/license/mine`, { headers: { Authorization: `Bearer ${sess.access_token}` } });
      // C2 BYPASS FIX: 403 returns valid JSON body but with error code.
      // Without this check, downstream code treats empty accounts as valid
      // and navigates user to /activate page without passing 2FA.
      if (lr.status === 403) {
        try { await supabase.auth.signOut(); } catch {}
        setLogoutReason("twofa_required");
        setPage("login");
        return;
      }
      const ld = await lr.json();

      // V6.0 Phase 2 — Check license abuse hard lock on mount only (not polling)
      try {
        const lockR = await fetch(`${API_URL}/api/license/lockout-status`, { headers: { Authorization: `Bearer ${sess.access_token}` } });
        if (lockR.ok) { const lockD = await lockR.json(); if (lockD.status === 'hard_locked') setLicenseHardLock(true); }
      } catch {} // fail silently
      const mr = await fetch(`${API_URL}/api/mt5/status`, { headers: { Authorization: `Bearer ${sess.access_token}` } });
      if (mr.status === 403) {
        try { await supabase.auth.signOut(); } catch {}
        setLogoutReason("twofa_required");
        setPage("login");
        return;
      }
      const md = await mr.json();
      // S11 (25 Apr 2026): /api/auth/check-admin call REMOVED.
      // Admin role detection no longer happens on user-facing bundle.
      // Admin uses isolated domain superadmin.shadowtechlab.com instead.
      const lics = ld.licenses || []; const conns = md.accounts || [];
      const na = lics.map(l => { const c = conns.find(x => x.licenseId === l.id || x.license?.id === l.id);
        return { license: { id: l.id, key: l.key, plan: l.plan, status: l.status, expiry: l.expiry_date, platform: l.platform, activatedAt: l.activated_at },
          connectionId: c?.id || null, broker: c?.broker || null, server: c?.server || null, mt5Account: c?.mt5Account || null, connectionStatus: c?.connectionStatus || null, sync: c?.sync || null, breakdown: c?.breakdown || null, positions: c?.positions || [], slEnabled: c?.slEnabled ?? false, tpEnabled: c?.tpEnabled ?? false }; });
      const hasConns = conns.length > 0;
      // v5.1 CACHE FIX: only restore cache if backend actually has connections
      setUser(prev => {
        let fa = na;
        if (na.some(a => a.broker && a.server)) { saveMT5Cache(ue, na); }
        else if (hasConns && prev?.accounts?.some(a => a.broker && a.server)) { fa = prev.accounts; }
        else if (hasConns) { const cc = getCachedMT5Data(ue); if (cc) fa = cc; }
        else { clearMT5Cache(); } // backend says 0 connections → clear stale cache
        return { name: sess.user.user_metadata?.name || sess.user.email?.split("@")[0] || "User", email: sess.user.email, accounts: fa, totalRenewals: prev?.totalRenewals || 0, joinedAt: sess.user.created_at };
      });
      if (init) { setPage(na.length > 0 ? "home" : "activate"); }
      // V6.0 — Check for expired licenses on load → trigger Hard Lock
      const expired = na.find(a => a.license?.status === 'expired' || (a.license?.expiry && new Date(a.license.expiry).getTime() <= Date.now()));
      if (expired) setExpiredLicense(expired.license);
    } catch (e) { console.error("Load failed:", e); const cc = getCachedMT5Data(ue);
      setUser({ name: sess.user.user_metadata?.name || sess.user.email?.split("@")[0] || "User", email: sess.user.email, accounts: cc || [], totalRenewals: 0, joinedAt: sess.user.created_at });
      if (init) setPage(cc?.length > 0 ? "home" : "activate"); }
  };

  const [pendingTwoFA, setPendingTwoFAState] = useState(null);
  // C2 Race Fix: wrap setter to keep ref in sync — so fetch interceptor
  // (closure captured at mount) always sees latest value.
  const setPendingTwoFA = (v) => { pendingTwoFARef.current = v; setPendingTwoFAState(v); };
  const [showSecurityPopup, setShowSecurityPopup] = useState(false);

  // S2 Phase 1 — Lockout state (23 Apr 2026)
  // softLock: { until: Date, remainingSeconds: number } | null
  // hardLock: { reason: string } | null
  // When set, displays SoftLockPopup or HardLockPopup over login screen.
  const [softLock, setSoftLock] = useState(null);
  const [hardLock, setHardLock] = useState(null);

  // V6.0 Phase 2 — License abuse hard lock (global, covers entire dashboard)
  const [licenseHardLock, setLicenseHardLock] = useState(false);
  // forceForgotMode: when true, AuthPage mounts in "forgot" mode (user clicked
  // Reset Password from a lockout popup). Resets on page change.
  const [forceForgotMode, setForceForgotMode] = useState(false);
  // Reset forceForgotMode flag whenever user leaves login page (clean state)
  useEffect(() => {
    if (page !== "login" && forceForgotMode) setForceForgotMode(false);
  }, [page, forceForgotMode]);

  // S2 Phase 1 — Backup codes display (after 2FA setup)
  // Array of 10 plain-text codes to show ONCE + confirm saved.
  const [backupCodesToDisplay, setBackupCodesToDisplay] = useState(null);
  const [profileScrollTo2FA, setProfileScrollTo2FA] = useState(false);

  const handleLogin = async (email, pw) => {
    // S2 Phase 1 (23 Apr 2026): Refactored to call backend /api/auth/login
    // instead of supabase.auth.signInWithPassword() directly. Backend now
    // enforces lockout (5 fails → soft 15min, 10 fails → hard requires reset).
    //
    // C2 Race Fix retained: Pre-block onAuthStateChange from firing loadUserData
    // BEFORE login completes. Without this guard, SIGNED_IN event (triggered
    // by supabase.auth.setSession below) can race ahead and trigger loadUserData
    // → 403 → /activate bypass.
    pendingTwoFARef.current = { pending: true };
    let loginOk = false;
    try {
      // -------- STEP 1: Call backend login endpoint --------
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password: pw }),
      });
      const d = await res.json();

      // -------- STEP 2: Handle lockout responses (HTTP 423) --------
      if (res.status === 423) {
        if (d.code === "ACCOUNT_HARD_LOCKED") {
          // V6.3 (1 May 2026): pass email user keyed in so HardLockPopup
          // can autofill ticket form even when session is null (login rejected).
          setHardLock({ reason: d.reason || "Account locked. Password reset required.", email });
          throw new Error("HARD_LOCKED"); // Caught below, UI shows modal
        }
        if (d.code === "ACCOUNT_SOFT_LOCKED") {
          const until = new Date(Date.now() + (d.remainingSeconds || 900) * 1000);
          setSoftLock({ until, remainingSeconds: d.remainingSeconds || 900 });
          throw new Error("SOFT_LOCKED"); // Caught below, UI shows modal
        }
      }

      // -------- STEP 3: Handle auth failures --------
      if (!res.ok || !d.success) {
        // Backend returns { remainingAttempts } on failed login
        const remaining = typeof d.remainingAttempts === "number" ? d.remainingAttempts : null;
        const hint = remaining !== null && remaining > 0
          ? ` (${remaining} ${remaining === 1 ? "attempt" : "attempts"} remaining)`
          : "";
        throw new Error((d.error || "Invalid email or password.") + hint);
      }

      // -------- STEP 4: Hydrate Supabase client with backend tokens --------
      // Backend verified creds + returned fresh session. Push tokens into
      // Supabase client so existing code (authFetch, onAuthStateChange) works.
      const { data: setData, error: setErr } = await supabase.auth.setSession({
        access_token: d.access_token,
        refresh_token: d.refresh_token,
      });
      if (setErr || !setData?.session) {
        throw new Error("Session hydration failed. Try again.");
      }
      const sess = setData.session;

      // -------- STEP 5: 2FA routing --------
      // Backend already tells us twofa_required in response.
      if (d.twofa_required) {
        // Keep guard set (upgrade placeholder to real session record)
        setPendingTwoFA({ session: sess, userName: sess.user.user_metadata?.name || sess.user.email?.split("@")[0] || "User" });
        setPage("login_2fa");
        loginOk = true;
        return;
      }

      // 2FA NOT required — clear guard so normal load can proceed
      pendingTwoFARef.current = null;
      setSession(sess); setPage("login_success");
      setUser(p => ({ ...p, name: sess.user.user_metadata?.name || sess.user.email?.split("@")[0] || "User", email: sess.user.email }));
      await new Promise(r => setTimeout(r, 1800));
      await loadUserData(sess, true);
      // Show popup: 2FA off
      setShowSecurityPopup(true);
      loginOk = true;
    } finally {
      // If login path threw (wrong pw, network, lockout, etc), clear guard so
      // user isn't locked out of retry. Guard persists only on happy path.
      if (!loginOk) pendingTwoFARef.current = null;
    }
  };

  const handle2FALoginVerify = async (code) => {
    if (!pendingTwoFA) throw new Error("No pending login.");
    const res = await fetch(`${API_URL}/api/auth/verify-2fa-login`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${pendingTwoFA.session.access_token}` },
      body: JSON.stringify({ code }),
    });
    const d = await res.json();

    // S2 Phase 1: Handle lockout responses (HTTP 423)
    if (res.status === 423) {
      if (d.code === "ACCOUNT_HARD_LOCKED") {
        // V6.3: pass email from pending session (user already passed STEP 1 password check)
        setHardLock({ reason: d.reason || "Account locked. Password reset required.", email: pendingTwoFA?.session?.user?.email || "" });
        // Also sign user out of pending 2FA session
        try { await supabase.auth.signOut(); } catch {}
        setPendingTwoFA(null);
        setPage("login");
        throw new Error("HARD_LOCKED");
      }
      if (d.code === "ACCOUNT_SOFT_LOCKED") {
        const until = new Date(Date.now() + (d.remainingSeconds || 900) * 1000);
        setSoftLock({ until, remainingSeconds: d.remainingSeconds || 900 });
        try { await supabase.auth.signOut(); } catch {}
        setPendingTwoFA(null);
        setPage("login");
        throw new Error("SOFT_LOCKED");
      }
    }

    if (!res.ok || !d.valid) {
      // Attach remainingAttempts hint if server returned it
      const remaining = typeof d.remainingAttempts === "number" ? d.remainingAttempts : null;
      const hint = remaining !== null && remaining > 0
        ? ` (${remaining} ${remaining === 1 ? "attempt" : "attempts"} remaining)`
        : "";
      throw new Error((d.error || "Invalid 2FA code.") + hint);
    }

    const sess = pendingTwoFA.session;
    setSession(sess); setPage("login_success");
    setUser(p => ({ ...p, name: sess.user.user_metadata?.name || sess.user.email?.split("@")[0] || "User", email: sess.user.email }));
    // C2 Race Fix: show splash delay BEFORE clearing pendingTwoFA, so any
    // stray onAuthStateChange during this window still skips loadUserData.
    await new Promise(r => setTimeout(r, 1800));
    // loadUserData FIRST (server has now inserted verified_2fa_sessions row,
    // so API calls will succeed), THEN clear pendingTwoFA.
    await loadUserData(sess, true);
    setPendingTwoFA(null);
  };

  // S2 Phase 1: Verify backup code as 2FA alternative
  const handle2FALoginVerifyBackup = async (backupCode) => {
    if (!pendingTwoFA) throw new Error("No pending login.");
    const res = await fetch(`${API_URL}/api/user/2fa/verify-backup`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${pendingTwoFA.session.access_token}` },
      body: JSON.stringify({ backupCode }),
    });
    const d = await res.json();

    // Handle lockout responses
    if (res.status === 423) {
      if (d.code === "ACCOUNT_HARD_LOCKED") {
        // V6.3: pass email from pending session (user already passed STEP 1 password check)
        setHardLock({ reason: d.reason || "Account locked. Password reset required.", email: pendingTwoFA?.session?.user?.email || "" });
        try { await supabase.auth.signOut(); } catch {}
        setPendingTwoFA(null);
        setPage("login");
        throw new Error("HARD_LOCKED");
      }
      if (d.code === "ACCOUNT_SOFT_LOCKED") {
        const until = new Date(Date.now() + (d.remainingSeconds || 900) * 1000);
        setSoftLock({ until, remainingSeconds: d.remainingSeconds || 900 });
        try { await supabase.auth.signOut(); } catch {}
        setPendingTwoFA(null);
        setPage("login");
        throw new Error("SOFT_LOCKED");
      }
    }

    if (!res.ok || !d.valid) {
      const remaining = typeof d.remainingAttempts === "number" ? d.remainingAttempts : null;
      const hint = remaining !== null && remaining > 0
        ? ` (${remaining} ${remaining === 1 ? "attempt" : "attempts"} remaining)`
        : "";
      throw new Error((d.error || "Invalid backup code.") + hint);
    }

    const sess = pendingTwoFA.session;
    setSession(sess); setPage("login_success");
    setUser(p => ({ ...p, name: sess.user.user_metadata?.name || sess.user.email?.split("@")[0] || "User", email: sess.user.email }));
    await new Promise(r => setTimeout(r, 1800));
    await loadUserData(sess, true);
    setPendingTwoFA(null);

    // Return remaining count so UI can warn user if low
    return { remaining: d.remaining };
  };

  const handle2FALoginCancel = async () => {
    await supabase.auth.signOut();
    setPendingTwoFA(null); setPage("login");
  };

  const handleSignup = async (email, pw, name) => {
    // v5.5: Early email availability check — runs BEFORE navigating to TnC page.
    // Prevents user wasting time scrolling TnC only to find email unavailable.
    try {
      const res = await fetch(`${API_URL}/api/auth/check-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) {
        // Rate-limited or server error — let user retry
        throw new Error(data.error || "Unable to verify email. Please try again.");
      }
      if (!data.available) {
        throw new Error("This email has already been used. Please use a different email address.");
      }
    } catch (e) {
      // Re-throw so AuthPage.submit catch block displays in form error message
      throw e;
    }
    // Email available — proceed to TnC page
    setPendingSignup({ email, pw, name });
    setPage("agreement");
  };
  const [pendingSignup, setPendingSignup] = useState(null);
  const [checkEmailAddress, setCheckEmailAddress] = useState(""); // store email for check-email page
  const handleAgreementAccept = async () => {
    if (!pendingSignup) return;
    const { email, pw, name } = pendingSignup;
    try {
      // TNC Fix (20 Apr 2026): Call backend /api/auth/signup instead of
      // supabase.auth.signUp() direct. Backend performs the signUp AND
      // inserts the PDPA TNC record into tnc_agreements table — critical
      // legal audit trail required for compliance. Frontend direct call
      // was bypassing this, leaving zero TNC records in DB.
      const res = await fetch(`${API_URL}/api/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password: pw, name }),
      });
      const data = await res.json();

      // Email availability check (v5.5) — permanent reservation policy.
      // Generic message, no enumeration details exposed to client.
      if (res.status === 409 && data.code === "EMAIL_UNAVAILABLE") {
        alert("This email has already been used.\n\nPlease use a different email address.");
        setPendingSignup(null);
        setPage("login");
        return;
      }

      if (!res.ok) throw new Error(data.error || "Signup failed.");

      // Backend always returns email_verification_required: true (C8 enforced).
      // User must click email link before they can login.
      setCheckEmailAddress(email);
      setPendingSignup(null);
      setPage("check_email");
      return data;
    } catch (e) {
      alert(e.message || "Signup failed.");
      setPage("login");
      setPendingSignup(null);
    }
  };
  const handleAgreementDecline = () => { setPendingSignup(null); setPage("login"); };
  const handleResendVerification = async (email) => {
    const { error } = await supabase.auth.resend({
      type: "signup",
      email,
      options: { emailRedirectTo: `${window.location.origin}/auth/verified` },
    });
    if (error) throw error;
  };
  const handleLogout = async (reasonOrIdle = false) => {
    // Backward compat: handleLogout(true) = idle, handleLogout() = manual
    // New: handleLogout("twofa_required") | handleLogout("twofa_expired")
    const reason = reasonOrIdle === true ? "idle"
                 : reasonOrIdle === false ? "manual"
                 : String(reasonOrIdle);
    try { await supabase.auth.signOut(); } catch {}
    setUser(null); setSession(null);
    setIdleWarning(false); setIdleCountdown(0);
    setPage("login"); setLogoutReason(reason);
  };

  const handleActivate = async (key) => {
    const res = await fetch(`${API_URL}/api/license/activate`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` }, body: JSON.stringify({ licenseKey: key }) });
    const d = await res.json();
    if (!res.ok) {
      // Pass structured error for lockout handling in ActivatePage
      const err = new Error(d.error || "Failed to activate.");
      err.code = d.code;
      err.attemptsLeft = d.attemptsLeft;
      err.warningMsg = d.warningMsg;
      err.cooldownActive = d.cooldownActive;
      err.cooldownSecondsRemaining = d.cooldownSecondsRemaining;
      err.cooldownEndsAt = d.cooldownEndsAt;
      throw err;
    }
    setUser(p => ({ ...p, accounts: [...(p.accounts || []), { license: { id: d.license.id, key: d.license.key, plan: d.license.plan, status: d.license.status, expiry: d.license.expiry, platform: d.license.platform, activatedAt: new Date().toISOString() }, connectionId: null, broker: null, server: null, mt5Account: null, connectionStatus: null, sync: null, slEnabled: false, tpEnabled: false }] }));
    setConnectingLicenseId(d.license.id); nav("connect"); return d;
  };

  const handleConnect = async (mt5, pw, broker, server, lid) => {
    const tl = lid || connectingLicenseId; if (!tl) throw new Error("No license selected.");
    const res = await fetch(`${API_URL}/api/mt5/connect`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` }, body: JSON.stringify({ licenseId: tl, mt5Login: mt5, mt5Password: pw, mt5Server: server, broker }) });
    const d = await res.json(); if (!res.ok) throw new Error(d.error || "Connection failed.");
    setUser(p => { const a = [...p.accounts]; const i = a.findIndex(x => x.license.id === tl); if (i >= 0) a[i] = { ...a[i], connectionId: d.connection?.id, mt5Account: mt5, broker: broker || "Unknown", server: server || "Unknown", connectionStatus: "deploying", sync: { running: false, positions: 0, totalSynced: 0, lastSync: null }, slEnabled: false, tpEnabled: false }; saveMT5Cache(p.email, a); return { ...p, accounts: a }; });
    setConnectingLicenseId(null); nav("home");
    // Step2: Show mandatory dormant policy reminder
    setShowPostConnectReminder(true);
  };

  const handleSystemDisconnect = (lid) => { setUser(p => { const a = [...p.accounts]; const i = a.findIndex(x => x.license.id === lid); if (i >= 0) a[i] = { ...a[i], slEnabled: false, tpEnabled: false }; return { ...p, accounts: a }; }); };

  const handleChangeMT5 = async (cid, lid) => {
    const res = await fetch(`${API_URL}/api/mt5/disconnect/${cid}`, { method: "DELETE", headers: { Authorization: `Bearer ${session.access_token}` } });
    const d = await res.json(); if (!res.ok) throw new Error(d.error || "Disconnect failed.");
    setUser(p => { const a = [...p.accounts]; const i = a.findIndex(x => x.license.id === lid); if (i >= 0) a[i] = { ...a[i], connectionId: null, broker: null, server: null, mt5Account: null, connectionStatus: null, sync: null, slEnabled: false, tpEnabled: false }; clearMT5Cache(); return { ...p, accounts: a }; });
    setConnectingLicenseId(lid); nav("connect");
  };

  const handleSwap = async (mt5, pw, broker, server, lid) => {
    const res = await fetch(`${API_URL}/api/mt5/swap`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` }, body: JSON.stringify({ licenseId: lid, mt5Login: mt5, mt5Password: pw, mt5Server: server, broker }) });
    const d = await res.json(); if (!res.ok) throw new Error(d.error || "Swap failed.");
    setUser(p => { const a = [...p.accounts]; const i = a.findIndex(x => x.license.id === lid); if (i >= 0) a[i] = { ...a[i], connectionId: d.connection?.id, mt5Account: mt5, broker: broker || "Unknown", server: server || "Unknown", connectionStatus: "deploying", sync: { running: false, positions: 0, totalSynced: 0, lastSync: null }, slEnabled: false, tpEnabled: false }; saveMT5Cache(p.email, a); return { ...p, accounts: a }; });
    setConnectingLicenseId(null); nav("home");
    // Step2: Show mandatory dormant policy reminder
    setShowPostConnectReminder(true);
  };

  const toggleSL = async (lid) => {
    const acc = user?.accounts?.find(x => x.license.id === lid);
    if (!acc) return;
    const newVal = !acc.slEnabled;
    // Optimistic UI update
    setUser(p => { const a = [...p.accounts]; const i = a.findIndex(x => x.license.id === lid); if (i >= 0) a[i] = { ...a[i], slEnabled: newVal }; return { ...p, accounts: a }; });
    try {
      const res = await fetch(`${API_URL}/api/sync/toggle`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` }, body: JSON.stringify({ licenseId: lid, slEnabled: newVal }) });
      const d = await res.json();
      if (!res.ok) { throw new Error(d.error || "Toggle failed."); }
      // Confirm with server response
      setUser(p => { const a = [...p.accounts]; const i = a.findIndex(x => x.license.id === lid); if (i >= 0) a[i] = { ...a[i], slEnabled: d.slEnabled }; return { ...p, accounts: a }; });
    } catch (e) {
      console.error("Toggle SL failed:", e);
      // Revert on failure
      setUser(p => { const a = [...p.accounts]; const i = a.findIndex(x => x.license.id === lid); if (i >= 0) a[i] = { ...a[i], slEnabled: !newVal }; return { ...p, accounts: a }; });
    }
  };
  const toggleTP = async (lid) => {
    const acc = user?.accounts?.find(x => x.license.id === lid);
    if (!acc) return;
    const newVal = !acc.tpEnabled;
    // Optimistic UI update
    setUser(p => { const a = [...p.accounts]; const i = a.findIndex(x => x.license.id === lid); if (i >= 0) a[i] = { ...a[i], tpEnabled: newVal }; return { ...p, accounts: a }; });
    try {
      const res = await fetch(`${API_URL}/api/sync/toggle`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` }, body: JSON.stringify({ licenseId: lid, tpEnabled: newVal }) });
      const d = await res.json();
      if (!res.ok) { throw new Error(d.error || "Toggle failed."); }
      // Confirm with server response
      setUser(p => { const a = [...p.accounts]; const i = a.findIndex(x => x.license.id === lid); if (i >= 0) a[i] = { ...a[i], tpEnabled: d.tpEnabled }; return { ...p, accounts: a }; });
    } catch (e) {
      console.error("Toggle TP failed:", e);
      // Revert on failure
      setUser(p => { const a = [...p.accounts]; const i = a.findIndex(x => x.license.id === lid); if (i >= 0) a[i] = { ...a[i], tpEnabled: !newVal }; return { ...p, accounts: a }; });
    }
  };

  // NOTE: handleRenew() removed on 20 Apr 2026 (C1 security fix).
  // Previous function called /api/license/renew (buggy — no payment check).
  // Will be rebuilt with proper payment gateway integration post-bank-account.

  // SPLASH SCREEN — show once on first load
  if (showSplash) return <SplashScreen onDone={() => setShowSplash(false)} />;

  if (loadingAuth) return <div style={{ minHeight: "100vh", background: "#06060B", display: "flex", alignItems: "center", justifyContent: "center" }}><div style={{ color: "#475569", fontSize: 14, fontFamily: "'Outfit', sans-serif" }}>Loading...</div></div>;
  if (page === "login") return (<>
    <AuthPage onLogin={handleLogin} onSignup={handleSignup} logoutReason={logoutReason} onClearReason={() => setLogoutReason(null)} initialMode={forceForgotMode ? "forgot" : undefined} licenseAbuseLock={hardLock?.reason === "license_abuse"} />
    {softLock && <SoftLockPopup remainingSeconds={softLock.remainingSeconds} onDismiss={() => setSoftLock(null)} onResetPassword={() => { setSoftLock(null); setForceForgotMode(true); }} />}
    {hardLock && <HardLockPopup reason={hardLock.reason} session={session} email={hardLock.email} onResetPassword={() => { setHardLock(null); setForceForgotMode(true); }} />}
  </>);
  if (page === "login_2fa") return (<>
    <Login2FAPage userName={pendingTwoFA?.userName || "User"} onVerify={handle2FALoginVerify} onVerifyBackup={handle2FALoginVerifyBackup} onCancel={handle2FALoginCancel} />
    {softLock && <SoftLockPopup remainingSeconds={softLock.remainingSeconds} onDismiss={() => setSoftLock(null)} onResetPassword={() => { setSoftLock(null); setForceForgotMode(true); }} />}
    {hardLock && <HardLockPopup reason={hardLock.reason} session={session} email={hardLock.email} onResetPassword={() => { setHardLock(null); setForceForgotMode(true); }} />}
  </>);
  if (page === "agreement") return <AgreementPage onAccept={handleAgreementAccept} onDecline={handleAgreementDecline} />;
  if (page === "check_email") return <CheckEmailPage email={checkEmailAddress} onBackToLogin={() => { setCheckEmailAddress(""); setPage("login"); }} onResendEmail={handleResendVerification} />;
  if (page === "reset_password") return <ResetPasswordPage onDone={async () => { await supabase.auth.signOut(); setPage("login"); setLogoutReason("password_reset_success"); }} />;
  if (page === "login_success") return <LoginSuccessPage userName={user?.name || "User"} />;

  const anyActive = user?.accounts?.some(a => a.slEnabled || a.tpEnabled);

  // V6.0 Phase 2 — License Abuse Hard Lock: FULLSCREEN JAIL
  if (licenseHardLock) {
    return <LicenseAbuseJailScreen session={session} onLogout={handleLogout} />;
  }

  return (
    <div style={{ minHeight: "100vh", background: "#06060B", color: "#E2E8F0", fontFamily: "'Outfit', 'DM Sans', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;700&display=swap" rel="stylesheet" />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 28px", borderBottom: "1px solid #ffffff08", background: "#06060B", gap: 20, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <WolfLogo size={32} />
          <span style={{ fontWeight: 700, fontSize: 15, letterSpacing: 1.5 }}>{BRAND}</span>
          <span style={{ color: "#334155", fontSize: 12, fontWeight: 300 }}>CLOUD</span>
        </div>

        {/* v5.5: Global engine status indicators (Railway env-controlled) */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, justifyContent: "center", minWidth: 280 }}>
          {[{ key: "slEngine", label: "SL Engine" }, { key: "tpEngine", label: "TP Engine" }].map(e => {
            const online = systemStatus[e.key] !== false;
            const color = online ? "#22C55E" : "#EF4444";
            return (
              <div key={e.key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 16px", borderRadius: 8, background: `${color}10`, border: `1px solid ${color}30` }}>
                <span style={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center", width: 10, height: 10 }}>
                  <span style={{ position: "absolute", width: 10, height: 10, borderRadius: "50%", background: color, opacity: 0.4, animation: "engpulse 1.8s ease-in-out infinite" }} />
                  <span style={{ position: "relative", width: 6, height: 6, borderRadius: "50%", background: color }} />
                </span>
                <span style={{ color: "#94A3B8", fontSize: 12, fontWeight: 500 }}>{e.label}</span>
                <span style={{ color: color, fontSize: 12, fontWeight: 700, letterSpacing: 0.5 }}>{online ? "ONLINE" : "OFFLINE"}</span>
              </div>
            );
          })}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ color: "#94A3B8", fontSize: 13 }}>{user?.name}</span>
            {session?.user?.email_confirmed_at && (
              <span title="Email verified" style={{ color: "#22C55E", fontSize: 12, fontWeight: 700 }}>✓</span>
            )}
          </div>
          {/* S11 (25 Apr 2026): Admin button REMOVED — admin uses isolated domain */}
          <button onClick={() => handleLogout()} style={{ background: "none", border: "1px solid #334155", color: "#94A3B8", padding: "6px 14px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>Logout</button>
        </div>
      </div>

      {idleWarning && (
        <div style={{ padding: "12px 28px", background: "linear-gradient(135deg, #F59E0B10, #EF444408)", borderBottom: "1px solid #F59E0B30", display: "flex", alignItems: "center", justifyContent: "space-between", animation: "fadeUp 0.3s ease" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}><span style={{ fontSize: 16 }}>⏱️</span><div><span style={{ color: "#F59E0B", fontSize: 13, fontWeight: 600 }}>Auto-logout in {idleCountdown}s — you are inactive</span><span style={{ color: "#475569", fontSize: 12, marginLeft: 8 }}>SL/TP sync continues in the cloud.</span></div></div>
          <button onClick={() => { setIdleWarning(false); setIdleCountdown(0); }} style={{ padding: "6px 16px", borderRadius: 6, background: "#F59E0B", border: "none", color: "#06060B", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>I'm still here</button>
        </div>
      )}

      {/* Tab bar — always visible so users can access Profile for 2FA even without license */}
      <div style={{ display: "flex", gap: 0, borderBottom: "1px solid #ffffff0a", padding: "0 28px" }}>
        {[{ id: "home", label: "Home" }, { id: "history", label: "History" }, { id: "plans", label: "Plans" }, { id: "profile", label: "Profile" }, { id: "support", label: "Support" }].map(t => (
          <button key={t.id} onClick={() => {
            nav(t.id);
            // Clear support badge when user opens Support page
            if (t.id === "support") {
              localStorage.setItem("lasky_support_last_check", new Date().toISOString());
              setSupportUnread(0);
            }
          }} style={{ background: "none", border: "none", borderBottom: page === t.id ? "2px solid #22D3EE" : "2px solid transparent", color: page === t.id ? "#F1F5F9" : "#94A3B8", padding: "14px 24px", cursor: "pointer", fontSize: 14, fontFamily: "inherit", fontWeight: page === t.id ? 700 : 500, transition: "all 0.2s", display: "flex", alignItems: "center", gap: 6 }}>
            {t.label}
            {t.id === "support" && supportUnread > 0 && (
              <span style={{ background: "#EF4444", color: "#fff", fontSize: 10, fontWeight: 700, borderRadius: 10, padding: "1px 6px", minWidth: 16, textAlign: "center", lineHeight: "16px" }}>
                {supportUnread > 99 ? "99+" : supportUnread}
              </span>
            )}
          </button>
        ))}
      </div>

      <div style={{ padding: "32px 28px", maxWidth: 900, margin: "0 auto" }}>
        {page === "activate" && <ActivatePage onActivate={handleActivate} onBack={goBack} hasAccounts={user?.accounts?.length > 0} sessionToken={session?.access_token} onHardLock={() => setLicenseHardLock(true)} />}
        {page === "connect" && <ConnectPage onConnect={handleConnect} onSwap={handleSwap} user={user} licenseId={connectingLicenseId} isSwap={!!(user?.accounts?.find(a => a.license.id === connectingLicenseId)?.mt5Account)} onBack={goBack} />}
        {page === "home" && <HomePage user={user} onNavigate={nav} onSystemDisconnect={handleSystemDisconnect} onChangeMT5={handleChangeMT5} onStartConnect={(l) => { setConnectingLicenseId(l); nav("connect"); }} toggleSL={toggleSL} toggleTP={toggleTP} />}
        {page === "history" && <HistoryPage session={session} />}
        {page === "plans" && <PlansPage user={user} />}
        {page === "profile" && <ProfilePage session={session} user={user} onLogout={handleLogout} scrollTo2FA={profileScrollTo2FA} onScrollConsumed={() => setProfileScrollTo2FA(false)} />}
        {page === "support" && <SupportPage session={session} />}
      </div>

      {/* V6.0 — License Hard Lock Modal (cannot dismiss) */}
      {expiredLicense && (
        <LicenseHardLockModal
          license={expiredLicense}
          onContactSupport={() => {
            // Hard lock modal triggers support modal — user needs to contact support to renew
            setExpiredLicense(null);
          }}
        />
      )}

      {showSecurityPopup && (
        <SecurityReminderPopup
          session={session}
          onEnable={() => { setShowSecurityPopup(false); setProfileScrollTo2FA(true); nav("profile"); }}
          onLater={() => { setShowSecurityPopup(false); }}
        />
      )}

      {/* Step2 MetaAPI Leak Fix: Mandatory dormant policy reminder after connect */}
      {showPostConnectReminder && (
        <PostConnectReminderPopup onClose={() => setShowPostConnectReminder(false)} />
      )}

      <div style={{ padding: "20px 28px", borderTop: "1px solid #ffffff06", background: "#06060B", marginTop: 48 }}>
        <div style={{ maxWidth: 900, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <span style={{ color: "#334155", fontSize: 11 }}>© 2026 Shadow Technologies · {BRAND} Cloud v5.5</span>
          <a href="mailto:laskysystem@gmail.com" style={{ color: "#475569", fontSize: 11, textDecoration: "none" }}>Contact: laskysystem@gmail.com</a>
        </div>
      </div>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}} @keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}} @keyframes fadeIn{from{opacity:0}to{opacity:1}} @keyframes engpulse{0%,100%{transform:scale(1);opacity:0.4}50%{transform:scale(1.6);opacity:0.1}}`}</style>
    </div>
  );
}

function CheckEmailPage({ email, onBackToLogin, onResendEmail }) {
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleResend = async () => {
    if (resendCooldown > 0 || resending) return;
    setResending(true);
    setResendMsg("");
    try {
      await onResendEmail(email);
      setResendMsg("✓ Verification email sent again. Check your inbox.");
      setResendCooldown(60);
    } catch (e) {
      setResendMsg("Failed to resend. Please try again in a moment.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#06060B", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Outfit', sans-serif", padding: 20 }}>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />
      <div style={{ position: "fixed", top: "30%", left: "50%", transform: "translate(-50%,-50%)", width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle, #22D3EE08 0%, transparent 70%)", pointerEvents: "none" }} />
      <div style={{ maxWidth: 480, width: "100%", background: "#0A0A12", border: "1px solid #1E293B", borderRadius: 16, padding: 32, position: "relative", zIndex: 1 }}>
        <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#22D3EE15", border: "2px solid #22D3EE40", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: 32 }}>📧</div>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: "#F1F5F9", marginBottom: 8, textAlign: "center" }}>Check Your Email</h2>
        <p style={{ color: "#94A3B8", fontSize: 14, lineHeight: 1.6, textAlign: "center", marginBottom: 24 }}>
          We've sent a verification link to:
        </p>
        <div style={{ background: "#ffffff06", border: "1px solid #1E293B", borderRadius: 10, padding: "12px 16px", textAlign: "center", marginBottom: 24 }}>
          <span style={{ color: "#22D3EE", fontSize: 14, fontWeight: 600, wordBreak: "break-all" }}>{email}</span>
        </div>
        <div style={{ background: "#ffffff04", borderRadius: 10, padding: 16, marginBottom: 20, border: "1px solid #1E293B" }}>
          <h3 style={{ color: "#F1F5F9", fontSize: 13, fontWeight: 600, marginBottom: 12, letterSpacing: "0.5px", textTransform: "uppercase" }}>📋 How to verify:</h3>
          <ol style={{ margin: 0, paddingLeft: 20, color: "#CBD5E1", fontSize: 13, lineHeight: 1.8 }}>
            <li>Open your email inbox</li>
            <li><strong style={{ color: "#F59E0B" }}>Check your Spam folder</strong> — our email service is new</li>
            <li>Look for email from <strong style={{ color: "#22D3EE" }}>LASKY SL/TP SYNC</strong></li>
            <li>If you see a red "<strong style={{ color: "#EF4444" }}>message might be dangerous</strong>" warning, click <strong>"Looks safe"</strong> — it's a Gmail warning for new domains like ours</li>
            <li>Click the verification link in the email</li>
          </ol>
        </div>
        <div style={{ background: "#F59E0B10", border: "1px solid #F59E0B30", borderRadius: 10, padding: 12, marginBottom: 24 }}>
          <p style={{ color: "#F59E0B", fontSize: 12, margin: 0, lineHeight: 1.5 }}>
            <strong>ℹ️ Why the spam warning?</strong><br />
            Our domain (laskysync.com) was registered a few days ago. Gmail flags new domains as a precaution. This will improve over the next few weeks as more users verify their emails.
          </p>
        </div>
        {resendMsg && (
          <div style={{ padding: "10px 14px", borderRadius: 8, background: resendMsg.startsWith("✓") ? "#22C55E15" : "#EF444415", border: `1px solid ${resendMsg.startsWith("✓") ? "#22C55E40" : "#EF444440"}`, marginBottom: 16 }}>
            <span style={{ color: resendMsg.startsWith("✓") ? "#22C55E" : "#EF4444", fontSize: 13 }}>{resendMsg}</span>
          </div>
        )}
        <button
          onClick={handleResend}
          disabled={resendCooldown > 0 || resending}
          style={{
            width: "100%",
            padding: "12px",
            background: "transparent",
            border: "1px solid #1E293B",
            borderRadius: 10,
            color: (resendCooldown > 0 || resending) ? "#475569" : "#22D3EE",
            fontSize: 13,
            fontWeight: 500,
            cursor: (resendCooldown > 0 || resending) ? "not-allowed" : "pointer",
            fontFamily: "inherit",
            marginBottom: 10,
          }}
        >
          {resending ? "Sending..." : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend Verification Email"}
        </button>
        <button
          onClick={onBackToLogin}
          style={{
            width: "100%",
            padding: "12px",
            background: "transparent",
            border: "none",
            color: "#64748B",
            fontSize: 13,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          ← Back to Login
        </button>
      </div>
    </div>
  );
}

function LoginSuccessPage({ userName }) {
  return (
    <div style={{ minHeight: "100vh", background: "#06060B", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Outfit', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />
      <div style={{ position: "fixed", top: "30%", left: "50%", transform: "translate(-50%,-50%)", width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle, #22C55E08 0%, transparent 70%)", pointerEvents: "none" }} />
      <div style={{ textAlign: "center", animation: "fadeUp 0.5s ease" }}>
        <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#22C55E15", border: "2px solid #22C55E40", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px", animation: "pulse 1.5s ease infinite" }}><span style={{ fontSize: 28, color: "#22C55E" }}>✓</span></div>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: "#F1F5F9", marginBottom: 8 }}>Login Successful!</h2>
        <p style={{ color: "#22D3EE", fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Welcome back, {userName}</p>
        <p style={{ color: "#475569", fontSize: 14, fontWeight: 300 }}>Loading your dashboard...</p>
        <div style={{ marginTop: 24, display: "flex", gap: 6, justifyContent: "center" }}>{[0, 1, 2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: "#22D3EE", opacity: 0.3, animation: `dotPulse 1.2s ease infinite ${i * 0.2}s` }} />)}</div>
      </div>
      <style>{`@keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}} @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.6}} @keyframes dotPulse{0%,100%{opacity:0.3;transform:scale(1)}50%{opacity:1;transform:scale(1.3)}}`}</style>
    </div>
  );
}

// ============================================================
// RESET PASSWORD PAGE (SECURE — atomic verify + update)
// Landed here from URL param detection on mount:
//   /auth/reset-password?token_hash=xxx&type=recovery
// Token is stashed in window.__RECOVERY_TOKEN_HASH by UserDashboard mount effect.
//
// SECURITY (P0 fix 23 Apr 2026):
// - NO auto-login — user MUST input new password before token is consumed.
// - Atomic flow: verifyOtp() → updateUser() → clear token → redirect login.
// - Token single-use: consumed on verifyOtp(), so reload/back button = dead.
// - URL params cleared after success to prevent token in browser history.
// ============================================================
function ResetPasswordPage({ onDone }) {
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const strength = checkPasswordStrength(newPw);
  const matches = newPw && newPw === confirm;
  const canSubmit = strength.allMet && matches && !busy;

  // Guard: ensure recovery token exists when user lands here.
  // If missing, user either hit this URL directly or token was already consumed.
  useEffect(() => {
    const tokenHash = window.__RECOVERY_TOKEN_HASH;
    if (!tokenHash) {
      setMsg({
        type: "error",
        text: "No recovery token found. Please click the link from your email again, or request a new password reset."
      });
    }
  }, []);

  const submit = async () => {
    setMsg(null);
    // Validate password strength BEFORE touching the token.
    // Never waste a single-use token on a password that fails validation.
    if (!strength.allMet) {
      const missing = [];
      if (!strength.checks.len) missing.push("8+ characters");
      if (!strength.checks.upper) missing.push("uppercase letter");
      if (!strength.checks.lower) missing.push("lowercase letter");
      if (!strength.checks.number) missing.push("number");
      if (!strength.checks.symbol) missing.push("symbol");
      setMsg({ type: "error", text: `Password must include: ${missing.join(", ")}.` });
      return;
    }
    if (!matches) { setMsg({ type: "error", text: "Passwords do not match." }); return; }

    const tokenHash = window.__RECOVERY_TOKEN_HASH;
    if (!tokenHash) {
      setMsg({ type: "error", text: "Invalid or missing recovery token. Please request a new reset link." });
      return;
    }

    setBusy(true);
    try {
      // STEP 1: Verify the recovery token — this creates a short-lived session
      // that allows updateUser() to run. Token is single-use and consumed here.
      const { data: verifyData, error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: 'recovery',
      });
      if (verifyError) {
        throw new Error('Recovery link is invalid or has expired. Please request a new one.');
      }
      if (!verifyData?.session) {
        throw new Error('Failed to create recovery session. Please try again.');
      }

      // STEP 2: Update password using the recovery session
      const { error: updateError } = await supabase.auth.updateUser({ password: newPw });
      if (updateError) {
        throw new Error(updateError.message || 'Failed to update password.');
      }

      // STEP 3: Clear token from memory + clear URL params (remove token from address bar)
      delete window.__RECOVERY_TOKEN_HASH;
      try { window.history.replaceState({}, document.title, '/'); } catch {}

      setMsg({ type: "success", text: "Password updated successfully. Redirecting to login..." });
      setTimeout(() => { onDone && onDone(); }, 1500);
    } catch (e) {
      setMsg({ type: "error", text: e.message || "Something went wrong. Please request a new reset link." });
      setBusy(false);
    }
  };

  const CheckItem = ({ ok, text }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: ok ? "#22C55E" : "#94A3B8", fontWeight: 500 }}>
      <span style={{ fontSize: 10 }}>{ok ? "✓" : "○"}</span>
      <span>{text}</span>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#06060B", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: "'Outfit', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />
      <div style={{ width: "100%", maxWidth: 440, background: "#0B0F1A", border: "1px solid #1E293B", borderRadius: 16, padding: 32 }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
            <WolfLogo size={70} />
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#F1F5F9", letterSpacing: "0.02em", marginBottom: 6 }}>LASKY SL/TP SYNC</div>
          <div style={{ fontSize: 12, color: "#94A3B8", fontWeight: 400 }}>Reset Your Password</div>
        </div>

        <div style={{ background: "#22D3EE10", border: "1px solid #22D3EE30", borderRadius: 10, padding: 14, marginBottom: 20 }}>
          <div style={{ fontSize: 13, color: "#E2E8F0", lineHeight: 1.6 }}>
            🔒 Create a new password for your account. Once updated, you'll be signed in with your new credentials.
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#CBD5E1", marginBottom: 6 }}>New Password</label>
            <div style={{ position: "relative" }}>
              <input
                type={showPw ? "text" : "password"}
                value={newPw}
                onChange={e => setNewPw(e.target.value)}
                placeholder="Enter new password"
                style={{ width: "100%", padding: "11px 40px 11px 14px", background: "#06060B", border: "1px solid #1E293B", borderRadius: 10, color: "#E2E8F0", fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }}
              />
              <button type="button" onClick={() => setShowPw(!showPw)} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "transparent", border: "none", color: "#64748B", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>{showPw ? "Hide" : "Show"}</button>
            </div>
            {newPw && (
              <div style={{ marginTop: 10 }}>
                <div style={{ height: 4, borderRadius: 2, background: "#1E293B", overflow: "hidden", marginBottom: 6 }}>
                  <div style={{ height: "100%", width: `${strength.percent}%`, background: strength.color, transition: "width 0.3s ease" }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 8 }}>
                  <span style={{ color: "#94A3B8", fontWeight: 500 }}>Strength:</span>
                  <span style={{ color: strength.color, fontWeight: 700 }}>{strength.label}</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
                  <CheckItem ok={strength.checks.len} text="8+ characters" />
                  <CheckItem ok={strength.checks.upper} text="Uppercase (A-Z)" />
                  <CheckItem ok={strength.checks.lower} text="Lowercase (a-z)" />
                  <CheckItem ok={strength.checks.number} text="Number (0-9)" />
                  <CheckItem ok={strength.checks.symbol} text="Symbol (!@#...)" />
                </div>
              </div>
            )}
          </div>

          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#CBD5E1", marginBottom: 6 }}>Confirm Password</label>
            <input
              type={showPw ? "text" : "password"}
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="Re-enter new password"
              style={{ width: "100%", padding: "11px 14px", background: "#06060B", border: `1px solid ${confirm && !matches ? "#EF444460" : "#1E293B"}`, borderRadius: 10, color: "#E2E8F0", fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }}
            />
            {confirm && !matches && <div style={{ marginTop: 6, fontSize: 11, color: "#EF4444", fontWeight: 500 }}>Passwords do not match</div>}
            {confirm && matches && <div style={{ marginTop: 6, fontSize: 11, color: "#22C55E", fontWeight: 500 }}>✓ Passwords match</div>}
          </div>

          {msg && (
            <div style={{ padding: 12, background: msg.type === "error" ? "#EF444415" : "#22C55E15", border: `1px solid ${msg.type === "error" ? "#EF444440" : "#22C55E40"}`, borderRadius: 10, fontSize: 13, color: msg.type === "error" ? "#FCA5A5" : "#86EFAC", fontWeight: 500 }}>
              {msg.text}
            </div>
          )}

          <button
            onClick={submit}
            disabled={!canSubmit}
            style={{ width: "100%", padding: 14, background: canSubmit ? "linear-gradient(135deg, #0891B2, #22D3EE)" : "#1E293B", border: "none", borderRadius: 10, color: canSubmit ? "#fff" : "#475569", fontSize: 14, fontWeight: 700, cursor: canSubmit ? "pointer" : "not-allowed", fontFamily: "inherit", transition: "opacity 0.2s" }}
          >
            {busy ? "Updating..." : "Update Password →"}
          </button>

          <div style={{ textAlign: "center", fontSize: 12, color: "#64748B", fontWeight: 400 }}>
            Link expires 30 minutes after it was sent
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// PUBLIC SUPPORT MODAL — System B (V6.0 Phase 3, 29 Apr 2026)
// ------------------------------------------------------------
// Login-page Contact Support form. NO LOGIN required.
// Email must be REGISTERED (debounced 800ms check on blur/typing).
// Submit via multipart/form-data with optional 5MB image attachment.
// Cloudflare Turnstile widget for bot protection.
// Honeypot field (hidden) — bots fill, humans don't.
// On success: shows ticket ID + 24h response promise.
// ============================================================
function PublicSupportModal({ isOpen, onClose }) {
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [emailRegistered, setEmailRegistered] = useState("unchecked"); // unchecked | checking | yes | no
  const [turnstileToken, setTurnstileToken] = useState("");
  const [honeypot, setHoneypot] = useState(""); // bot trap
  const [submitState, setSubmitState] = useState("idle"); // idle | sending | success | error
  const [errorMessage, setErrorMessage] = useState("");
  const [ticketId, setTicketId] = useState("");
  const turnstileWidgetIdRef = useRef(null);
  const turnstileContainerRef = useRef(null);

  // Reset all state when modal opens fresh
  useEffect(() => {
    if (isOpen) {
      setEmail(""); setSubject(""); setDescription("");
      setImageFile(null); setImagePreviewUrl("");
      setEmailRegistered("unchecked"); setTurnstileToken("");
      setHoneypot(""); setSubmitState("idle");
      setErrorMessage(""); setTicketId("");
    }
  }, [isOpen]);

  // ESC key + click outside to close (only when not sending)
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e) => {
      if (e.key === "Escape" && submitState !== "sending") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, submitState, onClose]);

  // Lock body scroll when modal open
  useEffect(() => {
    if (isOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = original; };
    }
  }, [isOpen]);

  // Cloudflare Turnstile script loader (idempotent)
  useEffect(() => {
    if (!isOpen) return;
    if (document.getElementById("cf-turnstile-script")) {
      // Script already loaded — render widget if container ready
      tryRenderTurnstile();
      return;
    }
    const script = document.createElement("script");
    script.id = "cf-turnstile-script";
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad";
    script.async = true;
    script.defer = true;
    // Global callback Cloudflare invokes when API ready
    window.onTurnstileLoad = () => tryRenderTurnstile();
    document.head.appendChild(script);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Render Turnstile widget when API + container both ready
  const tryRenderTurnstile = () => {
    if (!window.turnstile || !turnstileContainerRef.current) return;
    // Already rendered? skip
    if (turnstileWidgetIdRef.current !== null) return;
    const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || "1x00000000000000000000AA"; // fallback test key
    try {
      turnstileWidgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
        sitekey: siteKey,
        theme: "dark",
        callback: (token) => setTurnstileToken(token),
        "error-callback": () => { setTurnstileToken(""); },
        "expired-callback": () => { setTurnstileToken(""); },
      });
    } catch (e) {
      console.error("[Turnstile] render failed:", e);
    }
  };

  // Reset Turnstile widget when modal closes (so next open gets fresh challenge)
  useEffect(() => {
    if (!isOpen && turnstileWidgetIdRef.current !== null && window.turnstile) {
      try { window.turnstile.remove(turnstileWidgetIdRef.current); } catch (e) {}
      turnstileWidgetIdRef.current = null;
    }
  }, [isOpen]);

  // Debounced email registration check (800ms after typing stops)
  useEffect(() => {
    if (!email || email.length < 5 || !email.includes("@")) {
      setEmailRegistered("unchecked");
      return;
    }
    setEmailRegistered("checking");
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/check-email-registered`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: email.trim().toLowerCase() }),
        });
        if (!res.ok) {
          setEmailRegistered("unchecked");
          return;
        }
        const d = await res.json();
        setEmailRegistered(d.registered ? "yes" : "no");
      } catch (e) {
        setEmailRegistered("unchecked");
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [email]);

  // Image upload validation (client-side preview + size/type check)
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setImageFile(null); setImagePreviewUrl(""); return;
    }
    // Size: 5MB max
    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage("Image too large. Maximum 5MB allowed.");
      e.target.value = ""; // clear input
      return;
    }
    // Type whitelist
    const allowed = ["image/jpeg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      setErrorMessage("Only JPEG, PNG, or WebP images allowed.");
      e.target.value = "";
      return;
    }
    setErrorMessage("");
    setImageFile(file);
    setImagePreviewUrl(URL.createObjectURL(file));
  };

  // Cleanup preview URL on unmount/replace
  useEffect(() => {
    return () => { if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl); };
  }, [imagePreviewUrl]);

  const submitDisabled =
    submitState === "sending" ||
    !email ||
    emailRegistered !== "yes" ||
    subject.trim().length < 5 ||
    description.trim().length < 20 ||
    !turnstileToken;

  const handleSubmit = async () => {
    if (submitDisabled) return;
    setSubmitState("sending");
    setErrorMessage("");

    try {
      const formData = new FormData();
      formData.append("email", email.trim().toLowerCase());
      formData.append("subject", subject.trim());
      formData.append("description", description.trim());
      formData.append("turnstileToken", turnstileToken);
      formData.append("honeypot", honeypot);
      if (imageFile) formData.append("attachment", imageFile);

      const res = await fetch(`${API_URL}/api/public/support-ticket`, {
        method: "POST",
        body: formData, // browser auto-sets multipart/form-data with boundary
      });
      const d = await res.json();

      if (!res.ok) {
        if (res.status === 429) {
          throw new Error(d.error || "Too many tickets. Please wait before submitting another.");
        }
        if (res.status === 400 && d.error === "EMAIL_NOT_REGISTERED") {
          throw new Error(d.message || "This email is not registered.");
        }
        throw new Error(d.error || "Failed to submit ticket. Please try again.");
      }

      setTicketId(d.ticket_id || "SUPP-2026-XXXXXX");
      setSubmitState("success");
    } catch (e) {
      setErrorMessage(e.message || "Submission failed.");
      setSubmitState("error");
      // Reset Turnstile token after error so user gets fresh challenge
      setTurnstileToken("");
      if (turnstileWidgetIdRef.current !== null && window.turnstile) {
        try { window.turnstile.reset(turnstileWidgetIdRef.current); } catch (err) {}
      }
    }
  };

  if (!isOpen) return null;

  // ========== STYLE TOKENS (UI rule: NEVER use #94A3B8/#64748B/#475569 for body) ==========
  const COLORS = {
    bg: "#06060B",
    card: "#0A0A12",
    border: "#1E293B",
    accent: "#22D3EE",
    bodyText: "#E2E8F0",
    emphasisText: "#F1F5F9",
    secondaryText: "#CBD5E1",
    success: "#22C55E",
    danger: "#EF4444",
    disabled: "#475569", // ONLY for disabled states
  };

  const inputStyle = {
    width: "100%", padding: "12px 14px", background: "#ffffff04",
    border: "1px solid #ffffff0a", borderRadius: 10, color: COLORS.bodyText,
    fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box",
    transition: "border-color 0.2s",
  };

  const labelStyle = {
    color: COLORS.secondaryText, fontSize: 12, marginBottom: 6,
    display: "block", fontWeight: 500,
  };

  // ============= SUCCESS VIEW =============
  if (submitState === "success") {
    return (
      <div style={{
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        animation: "fadeUp 0.3s ease",
      }}>
        <div style={{
          width: "100%", maxWidth: 520, padding: 36, background: COLORS.card,
          border: `1px solid ${COLORS.border}`, borderRadius: 16, textAlign: "center",
          fontFamily: "'Outfit', sans-serif",
        }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
            <WolfLogo size={70} />
          </div>
          <div style={{
            fontSize: 32, marginBottom: 12, color: COLORS.success,
          }}>✓</div>
          <h2 style={{
            fontSize: 22, fontWeight: 700, color: COLORS.emphasisText,
            margin: "0 0 12px 0",
          }}>Ticket Received</h2>
          <p style={{
            color: COLORS.bodyText, fontSize: 14, lineHeight: 1.6,
            margin: "0 0 20px 0", fontWeight: 500,
          }}>
            Our support team will respond within <strong style={{ color: COLORS.emphasisText }}>24 hours</strong> to your email.
          </p>
          <div style={{
            margin: "20px 0", padding: 16, background: COLORS.bg,
            border: `1px solid ${COLORS.border}`, borderRadius: 8,
          }}>
            <div style={{ color: COLORS.secondaryText, fontSize: 12, marginBottom: 4, fontWeight: 500 }}>Your ticket ID</div>
            <div style={{
              color: COLORS.accent, fontSize: 18, fontWeight: 700,
              letterSpacing: 1, fontFamily: "monospace",
            }}>{ticketId}</div>
          </div>
          <p style={{
            color: COLORS.secondaryText, fontSize: 12, margin: "0 0 24px 0", fontWeight: 500,
          }}>Please keep this ID for reference.</p>
          <button
            onClick={onClose}
            style={{
              padding: "12px 32px", background: `linear-gradient(135deg, #0891B2, ${COLORS.accent})`,
              border: "none", borderRadius: 10, color: "#fff", fontSize: 14,
              fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
            }}
          >Close</button>
        </div>
      </div>
    );
  }

  // ============= MAIN FORM VIEW =============
  return (
    <div
      style={{
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        animation: "fadeUp 0.3s ease", overflowY: "auto",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && submitState !== "sending") onClose();
      }}
    >
      <div style={{
        width: "100%", maxWidth: 520, maxHeight: "calc(100vh - 32px)", overflowY: "auto",
        padding: 32, background: COLORS.card, border: `1px solid ${COLORS.border}`,
        borderRadius: 16, fontFamily: "'Outfit', sans-serif",
      }}>
        {/* Header with close button */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div>
            <h2 style={{
              fontSize: 20, fontWeight: 700, color: COLORS.emphasisText, margin: "0 0 4px 0",
            }}>Contact Support</h2>
            <p style={{
              color: COLORS.secondaryText, fontSize: 13, margin: 0, fontWeight: 500,
            }}>We respond within 24 hours via email.</p>
          </div>
          <button
            onClick={() => { if (submitState !== "sending") onClose(); }}
            disabled={submitState === "sending"}
            aria-label="Close"
            style={{
              background: "transparent", border: "none",
              color: COLORS.secondaryText, fontSize: 24, cursor: submitState === "sending" ? "not-allowed" : "pointer",
              padding: 0, lineHeight: 1, fontFamily: "inherit",
            }}
          >×</button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Email field */}
          <div>
            <label style={labelStyle}>Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              disabled={submitState === "sending"}
              style={inputStyle}
            />
            {emailRegistered === "checking" && (
              <div style={{ marginTop: 6, color: COLORS.secondaryText, fontSize: 11, fontWeight: 500 }}>
                Verifying email...
              </div>
            )}
            {emailRegistered === "yes" && (
              <div style={{ marginTop: 6, color: COLORS.success, fontSize: 11, fontWeight: 500 }}>
                ✓ Email verified
              </div>
            )}
            {emailRegistered === "no" && (
              <div style={{ marginTop: 6, padding: "8px 10px", borderRadius: 6, background: "#EF444410", border: "1px solid #EF444420" }}>
                <div style={{ color: "#FCA5A5", fontSize: 11, fontWeight: 500 }}>
                  This email is not registered. Please use a registered email or sign up first.
                </div>
              </div>
            )}
          </div>

          {/* Subject field */}
          <div>
            <label style={labelStyle}>Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Brief summary of the issue"
              maxLength={200}
              disabled={submitState === "sending"}
              style={inputStyle}
            />
            <div style={{
              marginTop: 4, fontSize: 10, color: COLORS.secondaryText, textAlign: "right", fontWeight: 500,
            }}>{subject.length}/200</div>
          </div>

          {/* Description field */}
          <div>
            <label style={labelStyle}>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Please describe your issue in detail (minimum 20 characters)"
              maxLength={5000}
              rows={5}
              disabled={submitState === "sending"}
              style={{ ...inputStyle, resize: "vertical", minHeight: 100, fontFamily: "inherit" }}
            />
            <div style={{
              marginTop: 4, fontSize: 10,
              color: description.length < 20 ? "#FCA5A5" : COLORS.secondaryText,
              textAlign: "right", fontWeight: 500,
            }}>
              {description.length < 20
                ? `${20 - description.length} more characters needed`
                : `${description.length}/5000`}
            </div>
          </div>

          {/* Image upload (optional) */}
          <div>
            <label style={labelStyle}>Attachment (optional)</label>
            <div style={{
              padding: 12, background: "#ffffff04", border: "1px dashed #ffffff15",
              borderRadius: 10, fontSize: 13,
            }}>
              {!imageFile ? (
                <label style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: submitState === "sending" ? "not-allowed" : "pointer",
                  color: COLORS.secondaryText, fontWeight: 500, gap: 8,
                }}>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleImageChange}
                    disabled={submitState === "sending"}
                    style={{ display: "none" }}
                  />
                  📎 Click to attach screenshot (JPEG / PNG / WebP, max 5MB)
                </label>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  {imagePreviewUrl && (
                    <img src={imagePreviewUrl} alt="preview" style={{
                      width: 48, height: 48, objectFit: "cover", borderRadius: 6,
                      border: `1px solid ${COLORS.border}`,
                    }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      color: COLORS.bodyText, fontSize: 13, fontWeight: 500,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>{imageFile.name}</div>
                    <div style={{ color: COLORS.secondaryText, fontSize: 11, fontWeight: 500 }}>
                      {(imageFile.size / 1024).toFixed(1)} KB
                    </div>
                  </div>
                  <button
                    onClick={() => { setImageFile(null); setImagePreviewUrl(""); }}
                    disabled={submitState === "sending"}
                    style={{
                      background: "transparent", border: "none", color: "#FCA5A5",
                      cursor: "pointer", fontSize: 13, fontFamily: "inherit", fontWeight: 500,
                    }}
                  >Remove</button>
                </div>
              )}
            </div>
          </div>

          {/* Honeypot — hidden from humans, bots fill */}
          <div style={{
            position: "absolute", left: "-9999px", opacity: 0,
            pointerEvents: "none", height: 0, overflow: "hidden",
          }} aria-hidden="true">
            <label>
              Website (leave blank)
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
              />
            </label>
          </div>

          {/* Cloudflare Turnstile widget */}
          <div style={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
            <div ref={turnstileContainerRef} />
          </div>

          {/* Error message */}
          {errorMessage && (
            <div style={{
              padding: "10px 14px", borderRadius: 8, background: "#EF444410",
              border: "1px solid #EF444420", color: "#FCA5A5", fontSize: 13, fontWeight: 500,
            }}>{errorMessage}</div>
          )}

          {/* Submit button */}
          <button
            onClick={handleSubmit}
            disabled={submitDisabled}
            style={{
              padding: "14px",
              background: submitDisabled
                ? COLORS.border
                : `linear-gradient(135deg, #0891B2, ${COLORS.accent})`,
              border: "none", borderRadius: 10,
              color: submitDisabled ? COLORS.disabled : "#fff",
              fontSize: 15, fontWeight: 700,
              cursor: submitDisabled ? "not-allowed" : "pointer",
              fontFamily: "inherit", marginTop: 4,
            }}
          >
            {submitState === "sending" ? "Sending..." : "Send Ticket →"}
          </button>

          <p style={{
            color: COLORS.secondaryText, fontSize: 11, textAlign: "center",
            margin: "4px 0 0 0", fontWeight: 500, lineHeight: 1.5,
          }}>
            We'll reply only to your registered email address.<br/>
            Limit: 3 tickets per hour, 10 per day.
          </p>
        </div>
      </div>
    </div>
  );
}

function AuthPage({ onLogin, onSignup, logoutReason, onClearReason, initialMode, licenseAbuseLock }) {
  const [mode, setMode] = useState(initialMode || "login"); const saved = getSavedCredentials();
  // S2 Phase 1: If parent forces forgot mode (from lockout popup), switch modes
  useEffect(() => {
    if (initialMode === "forgot" && mode !== "forgot") setMode("forgot");
  }, [initialMode]);
  const [email, setEmail] = useState(saved?.email || ""); const [password, setPassword] = useState("");
  const [name, setName] = useState(""); const [rememberMe, setRememberMe] = useState(!!saved);
  const [error, setError] = useState(""); const [success, setSuccess] = useState(""); const [loading, setLoading] = useState(false);
  const [licenseAbuseLocked, setLicenseAbuseLocked] = useState(!!licenseAbuseLock);
  // Sync from parent when hardLock changes
  const prevLicenseAbuseLock = licenseAbuseLock;
  if (licenseAbuseLock && !licenseAbuseLocked) setLicenseAbuseLocked(true);
  // V6.0 Phase 3 — Public Support Modal toggle
  const [showSupportModal, setShowSupportModal] = useState(false);
  useEffect(() => { if (logoutReason) { const t = setTimeout(() => onClearReason?.(), 8000); return () => clearTimeout(t); } }, [logoutReason]);

  const handleForgotPassword = async () => {
    setError(""); setSuccess("");
    if (!email) { setError("Enter your email address first."); return; }
    setLoading(true);
    try {
      // S2 Phase 1 (23 Apr 2026): Call backend endpoint instead of Supabase direct.
      // Backend clears lockout counters (hard_locked_at, failed_*_count) so user
      // can login again after reset. Backend then triggers Supabase reset email.
      const res = await fetch(`${API_URL}/api/auth/password-reset-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const d = await res.json();
      // V6.3 STRICT MODE (1 May 2026): backend returns 423 + code='LICENSE_ABUSE_LOCKED'
      // when the account was hard-locked for license abuse. Switch UI to red lock notice
      // (instead of green "email sent" lie). Email field stays so user sees what triggered it.
      if (res.status === 423 && d.code === "LICENSE_ABUSE_LOCKED") {
        setLicenseAbuseLocked(true);
        setLoading(false);
        return;
      }
      if (!res.ok) {
        throw new Error(d.error || "Failed to send reset email.");
      }
      // Generic success message (backend returns this whether user exists or not)
      setSuccess(d.message || "If an account exists with this email, a password reset link has been sent.");
    } catch (e) {
      setError(e.message || "Failed to send reset email.");
    }
    setLoading(false);
  };

  const submit = async () => {
    setError(""); setSuccess("");
    if (!email || !password) { setError("Please fill in all fields."); return; }

    // Signup: enforce strict password rules (must match backend)
    if (mode === "signup") {
      const pwCheck = checkPasswordStrength(password);
      if (!pwCheck.allMet) {
        const missing = [];
        if (!pwCheck.checks.len) missing.push("8+ characters");
        if (!pwCheck.checks.upper) missing.push("uppercase letter");
        if (!pwCheck.checks.lower) missing.push("lowercase letter");
        if (!pwCheck.checks.number) missing.push("number");
        if (!pwCheck.checks.symbol) missing.push("symbol");
        setError(`Password must include: ${missing.join(", ")}.`);
        return;
      }
    } else {
      // Login: just min 8 chars check (existing users may have older passwords)
      if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    }

    setLoading(true);
    try {
      if (mode === "login") {
        if (rememberMe) saveCredentials(email); else clearSavedCredentials();
        await onLogin(email, password);
      } else {
        const d = await onSignup(email, password, name);
        if (d?.user && !d.session) { setSuccess("Account created! Check your email to verify, or try logging in."); setMode("login"); }
      }
    } catch (e) {
      // Friendlier message for unverified email
      const msg = e?.message || "";
      if (/email not confirmed|not confirmed|verify.*email/i.test(msg)) {
        setError("Your email is not verified yet. Please check your inbox (and spam folder) for the verification link.");
      } else {
        setError(msg || "Something went wrong.");
      }
    }
    setLoading(false);
  };
  const is = { width: "100%", padding: "12px 16px", background: "#ffffff04", border: "1px solid #ffffff0a", borderRadius: 10, color: "#E2E8F0", fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box" };

  // Forgot password mode
  if (mode === "forgot") return (
    <div style={{ minHeight: "100vh", background: "#06060B", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Outfit', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />
      <div style={{ position: "relative", width: 400, padding: 36, background: "#0A0A12", border: `1px solid ${licenseAbuseLocked ? "#EF444440" : "#ffffff08"}`, borderRadius: 16, animation: "fadeUp 0.5s ease" }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
            <WolfLogo size={70} />
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: 2, color: "#F1F5F9", marginBottom: 6 }}>{BRAND}</div>
          <p style={{ color: "#475569", fontSize: 13, fontWeight: 300, margin: 0 }}>{licenseAbuseLocked ? "Account Locked" : "Reset Your Password"}</p>
        </div>

        {licenseAbuseLocked ? (
          /* License abuse — block reset, show support contact */
          <div style={{ textAlign: "center" }}>
            <div style={{ padding: "14px 16px", background: "#EF444410", border: "1px solid #EF444425", borderRadius: 10, marginBottom: 20 }}>
              <p style={{ color: "#FCA5A5", fontSize: 13, lineHeight: 1.6, margin: 0 }}>
                Your account has been locked due to license activation abuse. Password reset is not available for locked accounts. Please contact support to unlock your account.
              </p>
            </div>
            <button onClick={() => setShowSupportModal(true)} style={{ width: "100%", padding: "14px", background: "linear-gradient(135deg, #0891B2, #22D3EE)", border: "none", borderRadius: 10, color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", marginBottom: 12 }}>✉ Contact Support</button>
            <button onClick={() => { setMode("login"); setError(""); setSuccess(""); }} style={{ background: "none", border: "none", color: "#64748B", fontSize: 13, cursor: "pointer", fontFamily: "inherit", padding: "8px 0" }}>← Back to Login</button>
          </div>
        ) : (
          /* Normal reset flow */
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <p style={{ color: "#94A3B8", fontSize: 13, margin: 0, lineHeight: 1.6 }}>Enter your registered email address. We will send you a link to reset your password.</p>
            <div>
              <label style={{ color: "#94A3B8", fontSize: 12, marginBottom: 6, display: "block", fontWeight: 500 }}>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" onKeyDown={e => e.key === "Enter" && handleForgotPassword()} style={is} />
            </div>
            {error && <div style={{ padding: "10px 14px", borderRadius: 8, background: "#EF444410", border: "1px solid #EF444420", color: "#FCA5A5", fontSize: 13 }}>{error}</div>}
            {success && <div style={{ padding: "10px 14px", borderRadius: 8, background: "#22C55E10", border: "1px solid #22C55E20", color: "#86EFAC", fontSize: 13 }}>{success}</div>}
            <button onClick={handleForgotPassword} disabled={loading} style={{ padding: "14px", background: loading ? "#1E293B" : "linear-gradient(135deg, #0891B2, #22D3EE)", border: "none", borderRadius: 10, color: "#fff", fontSize: 15, fontWeight: 700, cursor: loading ? "wait" : "pointer", fontFamily: "inherit" }}>{loading ? "Sending..." : "Send Reset Link →"}</button>
            <button onClick={() => { setMode("login"); setError(""); setSuccess(""); }} style={{ background: "none", border: "none", color: "#64748B", fontSize: 13, cursor: "pointer", fontFamily: "inherit", padding: "8px 0" }}>← Back to Login</button>
          </div>
        )}
      </div>
      <style>{`@keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );

  return (
    <>
    <div style={{ minHeight: "100vh", background: "#06060B", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Outfit', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />
      {/* V6.0 Phase 3 — Contact Support button (fixed top-right) */}
      <button
        onClick={() => setShowSupportModal(true)}
        style={{
          position: "fixed", top: 24, right: 24, zIndex: 100,
          padding: "8px 16px",
          background: "rgba(10, 10, 18, 0.8)",
          border: "1px solid #1E293B",
          borderRadius: 8,
          color: "#94A3B8",
          fontSize: 13,
          fontWeight: 500,
          cursor: "pointer",
          fontFamily: "inherit",
          backdropFilter: "blur(8px)",
          display: "flex",
          alignItems: "center",
          gap: 6,
          transition: "color 0.2s, border-color 0.2s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = "#22D3EE"; e.currentTarget.style.borderColor = "#22D3EE40"; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = "#94A3B8"; e.currentTarget.style.borderColor = "#1E293B"; }}
      >
        <span style={{ fontSize: 12 }}>✉️</span>
        Contact Support
      </button>
      <div style={{ position: "fixed", top: "30%", left: "50%", transform: "translate(-50%,-50%)", width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle, #22D3EE06 0%, transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "relative", width: 400, padding: 36, background: "#0A0A12", border: "1px solid #ffffff08", borderRadius: 16, animation: "fadeUp 0.5s ease" }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}><div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}><WolfLogo size={70} /></div><div style={{ fontSize: 18, fontWeight: 800, letterSpacing: 2, color: "#F1F5F9", marginBottom: 6 }}>{BRAND}</div><p style={{ color: "#475569", fontSize: 13, fontWeight: 300, margin: 0 }}>Cloud SL/TP Sync Engine</p></div>
        {logoutReason === "idle" && <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 16, background: "#F59E0B08", border: "1px solid #F59E0B20", display: "flex", alignItems: "center", gap: 8 }}><span style={{ fontSize: 14 }}>⏱️</span><div><div style={{ color: "#F59E0B", fontSize: 12, fontWeight: 600 }}>Auto-logged out due to inactivity</div><div style={{ color: "#475569", fontSize: 11, marginTop: 2 }}>SL/TP sync is still running in the cloud.</div></div></div>}
        {logoutReason === "manual" && <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 16, background: "#22C55E08", border: "1px solid #22C55E20", display: "flex", alignItems: "center", gap: 8 }}><span style={{ fontSize: 14 }}>☁️</span><div><div style={{ color: "#22C55E", fontSize: 12, fontWeight: 600 }}>Logged out — SL/TP sync remains active</div><div style={{ color: "#475569", fontSize: 11, marginTop: 2 }}>Cloud engine continues monitoring 24/7.</div></div></div>}
        {logoutReason === "twofa_required" && <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 16, background: "#EF444408", border: "1px solid #EF444420", display: "flex", alignItems: "center", gap: 8 }}><span style={{ fontSize: 14 }}>🔐</span><div><div style={{ color: "#EF4444", fontSize: 12, fontWeight: 600 }}>2FA verification required</div><div style={{ color: "#475569", fontSize: 11, marginTop: 2 }}>Please log in again to verify your identity.</div></div></div>}
        {logoutReason === "twofa_expired" && <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 16, background: "#F59E0B08", border: "1px solid #F59E0B20", display: "flex", alignItems: "center", gap: 8 }}><span style={{ fontSize: 14 }}>🔐</span><div><div style={{ color: "#F59E0B", fontSize: 12, fontWeight: 600 }}>2FA session expired</div><div style={{ color: "#475569", fontSize: 11, marginTop: 2 }}>Please log in again to continue.</div></div></div>}
        {logoutReason === "password_reset_success" && <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 16, background: "#22C55E08", border: "1px solid #22C55E20", display: "flex", alignItems: "center", gap: 8 }}><span style={{ fontSize: 14 }}>✅</span><div><div style={{ color: "#22C55E", fontSize: 12, fontWeight: 600 }}>Password updated successfully</div><div style={{ color: "#E2E8F0", fontSize: 11, marginTop: 2 }}>Please log in with your new password.</div></div></div>}
        <div style={{ display: "flex", marginBottom: 28, borderRadius: 10, background: "#ffffff04", padding: 4 }}>{["login", "signup"].map(m => <button key={m} onClick={() => { setMode(m); setError(""); }} style={{ flex: 1, padding: "10px", borderRadius: 8, background: mode === m ? "#1E293B" : "transparent", border: "none", color: mode === m ? "#E2E8F0" : "#475569", cursor: "pointer", fontSize: 14, fontFamily: "inherit", fontWeight: mode === m ? 600 : 400 }}>{m === "login" ? "Login" : "Sign Up"}</button>)}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {mode === "signup" && <div><label style={{ color: "#94A3B8", fontSize: 12, marginBottom: 6, display: "block", fontWeight: 500 }}>Name</label><input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Your full name" style={is} /></div>}
          <div><label style={{ color: "#94A3B8", fontSize: 12, marginBottom: 6, display: "block", fontWeight: 500 }}>Email</label><input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" style={is} /></div>
          <div><label style={{ color: "#94A3B8", fontSize: 12, marginBottom: 6, display: "block", fontWeight: 500 }}>Password</label><input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" onKeyDown={e => e.key === "Enter" && submit()} style={is} />
            {mode === "signup" && password && (() => {
              const s = checkPasswordStrength(password);
              const CheckItem = ({ ok, text }) => (
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: ok ? "#22C55E" : "#64748B", fontWeight: 500 }}>
                  <span style={{ fontSize: 10 }}>{ok ? "✓" : "○"}</span>
                  <span>{text}</span>
                </div>
              );
              return (
                <div style={{ marginTop: 8 }}>
                  <div style={{ height: 3, borderRadius: 2, background: "#1E293B", overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${s.percent}%`, background: s.color, transition: "width 0.3s ease" }} />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                    <span style={{ color: s.color, fontSize: 11, fontWeight: 600 }}>{s.label}</span>
                    <span style={{ color: "#475569", fontSize: 10 }}>all requirements must be met</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px", marginTop: 10, padding: "10px 12px", background: "#ffffff03", border: "1px solid #ffffff06", borderRadius: 8 }}>
                    <CheckItem ok={s.checks.len} text="8+ characters" />
                    <CheckItem ok={s.checks.upper} text="Uppercase (A-Z)" />
                    <CheckItem ok={s.checks.lower} text="Lowercase (a-z)" />
                    <CheckItem ok={s.checks.number} text="Number (0-9)" />
                    <CheckItem ok={s.checks.symbol} text="Symbol (!@#$...)" />
                  </div>
                </div>
              );
            })()}
          </div>
          {mode === "login" && <div onClick={() => setRememberMe(!rememberMe)} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none", padding: "2px 0" }}><div style={{ width: 18, height: 18, borderRadius: 4, flexShrink: 0, background: rememberMe ? "#22D3EE" : "transparent", border: `1.5px solid ${rememberMe ? "#22D3EE" : "#334155"}`, display: "flex", alignItems: "center", justifyContent: "center" }}>{rememberMe && <span style={{ color: "#06060B", fontSize: 11, fontWeight: 900, lineHeight: 1 }}>✓</span>}</div><span style={{ color: "#94A3B8", fontSize: 13 }}>Remember me</span><span style={{ color: "#334155", fontSize: 11, marginLeft: "auto" }}>Save email only</span></div>}
          {mode === "login" && !licenseAbuseLocked && <div style={{ textAlign: "right", marginTop: -8 }}><button onClick={() => { setMode("forgot"); setError(""); setSuccess(""); }} style={{ background: "none", border: "none", color: "#22D3EE", fontSize: 12, cursor: "pointer", fontFamily: "inherit", padding: 0, fontWeight: 500 }}>Forgot Password?</button></div>}
          {error && <div style={{ padding: "10px 14px", borderRadius: 8, background: "#EF444410", border: "1px solid #EF444420", color: "#FCA5A5", fontSize: 13 }}>{error}</div>}
          {success && <div style={{ padding: "10px 14px", borderRadius: 8, background: "#22C55E10", border: "1px solid #22C55E20", color: "#86EFAC", fontSize: 13 }}>{success}</div>}
          <button onClick={submit} disabled={loading} style={{ padding: "14px", background: loading ? "#1E293B" : "linear-gradient(135deg, #0891B2, #22D3EE)", border: "none", borderRadius: 10, color: "#fff", fontSize: 15, fontWeight: 700, cursor: loading ? "wait" : "pointer", fontFamily: "inherit", marginTop: 4 }}>{loading ? "Loading..." : mode === "login" ? "Login →" : "Create Account →"}</button>
        </div>
        <p style={{ color: "#1E293B", fontSize: 11, textAlign: "center", marginTop: 28, fontWeight: 300 }}>{BRAND} Cloud v5.5</p>
      </div>
    </div>
    {/* V6.0 Phase 3 — Public Support Modal (renders on top when triggered) */}
    <PublicSupportModal isOpen={showSupportModal} onClose={() => setShowSupportModal(false)} />
    </>
  );
}

function ActivatePage({ onActivate, onBack, hasAccounts, sessionToken, onHardLock }) {
  const [key, setKey] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [lockState, setLockState] = useState(null); // null | { type: 'cooldown'|'hard_lock', cooldownEndsAt?, cooldownSecondsRemaining? }
  const [attemptsLeft, setAttemptsLeft] = useState(null);
  const [warningMsg, setWarningMsg] = useState(null);
  const [cooldownTimer, setCooldownTimer] = useState(0);
  const [checkingStatus, setCheckingStatus] = useState(true); // true while fetching lockout status on mount
  const [transitioning, setTransitioning] = useState(false); // true while jail screen takes over

  // On mount — check lockout status from backend (persist cooldown across refresh)
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const r = await fetch(`${API_URL}/api/license/lockout-status`, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
        if (r.ok) {
          const d = await r.json();
          if (d.status === 'hard_locked') {
            setTransitioning(true); if (onHardLock) onHardLock(); else setLockState({ type: 'hard_lock' });
          } else if (d.status === 'cooldown') {
            setLockState({ type: 'cooldown', cooldownEndsAt: d.cooldownEndsAt, cooldownSecondsRemaining: d.cooldownSecondsRemaining });
          }
        }
      } catch (e) {
        // fail silently — user can still try activate
      }
      setCheckingStatus(false);
    };
    if (sessionToken) checkStatus();
    else setCheckingStatus(false);
  }, [sessionToken]);

  // Live countdown for cooldown
  useEffect(() => {
    if (!lockState?.cooldownEndsAt) { setCooldownTimer(0); return; }
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((new Date(lockState.cooldownEndsAt) - Date.now()) / 1000));
      setCooldownTimer(remaining);
      if (remaining === 0) setLockState(null); // cooldown expired
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [lockState?.cooldownEndsAt]);

  const go = async () => {
    setError(""); setWarningMsg(null); setAttemptsLeft(null);
    const c = key.trim().toUpperCase();
    if (!c || c.length < 10) { setError("Invalid license key. Please check and try again."); return; }
    setLoading(true);
    try {
      await onActivate(c);
    } catch (e) {
      if (e.code === "LICENSE_HARD_LOCKED") {
        setTransitioning(true); if (onHardLock) onHardLock(); else setLockState({ type: "hard_lock" });
      } else if (e.code === "LICENSE_COOLDOWN" || e.cooldownActive) {
        setLockState({ type: "cooldown", cooldownEndsAt: e.cooldownEndsAt, cooldownSecondsRemaining: e.cooldownSecondsRemaining });
      } else {
        setError(e.message || "Failed to activate.");
        if (e.attemptsLeft !== undefined) setAttemptsLeft(e.attemptsLeft);
        if (e.warningMsg) setWarningMsg(e.warningMsg);
      }
    }
    setLoading(false);
  };

  const fmtCooldown = (s) => {
    const m = Math.floor(s / 60); const sec = s % 60;
    return m > 0 ? `${m}m ${sec.toString().padStart(2, "0")}s` : `${sec}s`;
  };

  // Hard lock state
  if (lockState?.type === "hard_lock") {
    return (
      <div style={{ maxWidth: 520, margin: "0 auto", animation: "fadeUp 0.4s ease" }}>
        {hasAccounts && <BackButton onClick={onBack} />}
        <div style={{ padding: 32, borderRadius: 16, background: "#0A0A12", border: "1px solid #EF444430", textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>🔒</div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: "#F1F5F9", marginBottom: 8 }}>Account Locked</h2>
          <p style={{ color: "#94A3B8", fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>
            Too many failed license activation attempts. Your account has been locked for security.
          </p>
          <div style={{ padding: "12px 16px", borderRadius: 8, background: "#EF444410", border: "1px solid #EF444420", marginBottom: 20 }}>
            <div style={{ color: "#FCA5A5", fontSize: 13 }}>Please contact support to unlock your account.</div>
          </div>
          <a href="mailto:laskysystem@gmail.com" style={{ display: "inline-block", padding: "12px 24px", borderRadius: 10, background: "linear-gradient(135deg, #0891B2, #22D3EE)", color: "#fff", fontSize: 14, fontWeight: 700, textDecoration: "none", fontFamily: "inherit" }}>
            ✉ Contact Support
          </a>
        </div>
      </div>
    );
  }

  // Cooldown state
  if (lockState?.type === "cooldown") {
    return (
      <div style={{ maxWidth: 520, margin: "0 auto", animation: "fadeUp 0.4s ease" }}>
        {hasAccounts && <BackButton onClick={onBack} />}
        <div style={{ padding: 32, borderRadius: 16, background: "#0A0A12", border: "1px solid #F9731630", textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>⏳</div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: "#F1F5F9", marginBottom: 8 }}>Cooldown Active</h2>
          <p style={{ color: "#94A3B8", fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>
            Too many failed attempts. Please wait before trying again.
          </p>
          <div style={{ padding: "20px", borderRadius: 12, background: "#F9731610", border: "1px solid #F9731630", marginBottom: 20 }}>
            <div style={{ color: "#FDBA74", fontSize: 13, marginBottom: 8 }}>Time remaining:</div>
            <div style={{ color: "#F97316", fontSize: 32, fontWeight: 800, fontFamily: "monospace" }}>
              {fmtCooldown(cooldownTimer)}
            </div>
          </div>
          <p style={{ color: "#475569", fontSize: 12, lineHeight: 1.5 }}>
            This cooldown protects your account from abuse. You can try again once the timer expires.
          </p>
        </div>
      </div>
    );
  }

  if (transitioning) return null; // jail screen taking over, hide ActivatePage instantly

  if (checkingStatus) {
    return (
      <div style={{ maxWidth: 520, margin: "0 auto", textAlign: "center", padding: 60 }}>
        <div style={{ color: "#475569", fontSize: 13 }}>Checking account status...</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 520, margin: "0 auto", animation: "fadeUp 0.4s ease" }}>
      {hasAccounts && <BackButton onClick={onBack} />}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 40 }}>{[{ label: "Activate Key", active: true }, { label: "Login MT5", active: false }, { label: "System Ready", active: false }].map((s, i) => <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}><div style={{ width: 28, height: 28, borderRadius: "50%", background: s.active ? "#22D3EE" : "#1E293B", display: "flex", alignItems: "center", justifyContent: "center", color: s.active ? "#06060B" : "#475569", fontSize: 12, fontWeight: 700 }}>{i + 1}</div><span style={{ color: s.active ? "#E2E8F0" : "#334155", fontSize: 13, fontWeight: s.active ? 600 : 400 }}>{s.label}</span>{i < 2 && <div style={{ width: 40, height: 1, background: "#1E293B" }} />}</div>)}</div>
      <div style={{ padding: 32, borderRadius: 16, background: "#0A0A12", border: "1px solid #ffffff08" }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8, color: "#F1F5F9" }}>Activate License Key</h2>
        <p style={{ color: "#94A3B8", fontSize: 14, marginBottom: 28, fontWeight: 300 }}>Enter the license key you received from your purchase.</p>
        <div style={{ marginBottom: 20 }}><input type="text" value={key} onChange={e => setKey(e.target.value.toUpperCase())} placeholder="XX-XXXX-XXXX-XXXX" onKeyDown={e => e.key === "Enter" && go()} style={{ width: "100%", padding: "16px 20px", background: "#ffffff04", border: "1px solid #ffffff0a", borderRadius: 12, color: "#22D3EE", fontSize: 18, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, letterSpacing: 3, outline: "none", boxSizing: "border-box", textAlign: "center" }} /></div>
        {error && <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 12, background: "#EF444410", border: "1px solid #EF444420", color: "#FCA5A5", fontSize: 13 }}>{error}</div>}
        {warningMsg && <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 12, background: "#F9731610", border: "1px solid #F9731630", color: "#FDBA74", fontSize: 12, fontWeight: 600 }}>⚠️ {warningMsg}</div>}
        {attemptsLeft !== null && attemptsLeft > 0 && !warningMsg && (
          <div style={{ padding: "8px 12px", borderRadius: 8, marginBottom: 12, background: "#F59E0B10", border: "1px solid #F59E0B20", color: "#FCD34D", fontSize: 12 }}>
            {attemptsLeft} attempt{attemptsLeft !== 1 ? "s" : ""} remaining before cooldown.
          </div>
        )}
        <button onClick={go} disabled={loading} style={{ width: "100%", padding: "14px", background: loading ? "#1E293B" : "linear-gradient(135deg, #0891B2, #22D3EE)", border: "none", borderRadius: 10, color: "#fff", fontSize: 15, fontWeight: 700, cursor: loading ? "wait" : "pointer", fontFamily: "inherit" }}>{loading ? "Verifying..." : "Activate →"}</button>
        <div style={{ marginTop: 24, padding: "14px 18px", borderRadius: 10, background: "#ffffff03", border: "1px solid #ffffff06" }}><div style={{ color: "#94A3B8", fontSize: 12, fontWeight: 500, marginBottom: 8 }}>Don't have a license key?</div><div style={{ color: "#475569", fontSize: 12, lineHeight: 1.6 }}>Get one from our Shopee or TikTok Shop, or contact admin via Telegram.</div></div>
      </div>
    </div>
  );
}

function ConnectPage({ onConnect, onSwap, user, licenseId, isSwap, onBack }) {
  const [ml, setMl] = useState(""); // mt5 login
  const [mp, setMp] = useState(""); // mt5 password
  const [ms, setMs] = useState(""); // selected server (final value)
  const [bs, setBs] = useState(""); // broker search text
  const [sr, setSr] = useState([]); // search results from MetaAPI
  const [sg, setSg] = useState(false); // searching indicator
  const [dd, setDd] = useState(false); // broker dropdown open
  const [sb, setSb] = useState(""); // selected broker name
  const [err, setErr] = useState(""); // error message
  const [ld, setLd] = useState(false); // loading (connect)
  const [st, setSt] = useState(null); // search timeout ref
  const [vg, setVg] = useState(false); // validating
  const [vd, setVd] = useState(false); // validated
  const [vm, setVm] = useState(""); // validation message
  const [manualMode, setManualMode] = useState(false); // manual server input mode
  const [manualInput, setManualInput] = useState(""); // manual server text
  const [showSuggestions, setShowSuggestions] = useState(false); // autocomplete dropdown

  // --- Validation Modal State (Step 2 MetaAPI Leak Fix) ---
  const [vModal, setVModal] = useState(null); // { type: 'fail'|'cooldown'|'success', attemptsUsed, attemptsMax, cooldownEndsAt, message }
  const [vTimer, setVTimer] = useState(0); // seconds remaining for cooldown countdown

  // Live timer countdown for cooldown modal
  useEffect(() => {
    if (!vModal?.cooldownEndsAt) { setVTimer(0); return; }
    const update = () => {
      const remaining = Math.max(0, Math.ceil((new Date(vModal.cooldownEndsAt) - Date.now()) / 1000));
      setVTimer(remaining);
      if (remaining === 0) { setVModal(null); }
    };
    update();
    const i = setInterval(update, 1000);
    return () => clearInterval(i);
  }, [vModal?.cooldownEndsAt]);
  const tl = user?.accounts?.find(a => a.license.id === licenseId)?.license;

  // Extract server name pattern from MetaAPI results for autocomplete
  // e.g. ["HFMarketsKE-Live10", "HFMarketsKE-Live11", "HFMarketsKE-Live15"] → prefix "HFMarketsKE-Live"
  const bg = {}; sr.forEach(r => { if (!bg[r.broker]) bg[r.broker] = []; bg[r.broker].push(r.server); });
  const ss = bg[sb] || [];

  const extractPattern = (servers) => {
    if (!servers || servers.length === 0) return null;
    // Filter only live servers (exclude demo/trial) for pattern
    const liveServers = servers.filter(s => !s.toLowerCase().includes("demo") && !s.toLowerCase().includes("trial"));
    const pool = liveServers.length > 0 ? liveServers : servers;
    if (pool.length < 1) return null;

    // Find longest common prefix among servers
    let prefix = pool[0];
    for (let i = 1; i < pool.length; i++) {
      while (pool[i].indexOf(prefix) !== 0) {
        prefix = prefix.slice(0, -1);
        if (!prefix) break;
      }
      if (!prefix) break;
    }

    // If prefix ends with a digit, strip trailing digits to get the base
    // e.g. "HFMarketsKE-Live10" common with "HFMarketsKE-Live11" → prefix "HFMarketsKE-Live1" → strip → "HFMarketsKE-Live"
    if (prefix && /\d$/.test(prefix)) {
      prefix = prefix.replace(/\d+$/, '');
    }

    // Ensure prefix is meaningful (at least 5 chars)
    return prefix && prefix.length >= 5 ? prefix : null;
  };

  const serverPattern = extractPattern(ss);

  // Generate autocomplete suggestions based on manual input + pattern
  const getAutoSuggestions = () => {
    if (!manualInput || !serverPattern) return [];
    const input = manualInput.trim();
    const suggestions = [];

    // If user typed just a number, combine with pattern
    if (/^\d+$/.test(input)) {
      suggestions.push(serverPattern + input);
    }
    // If user typed partial text that matches pattern start
    else if (serverPattern.toLowerCase().startsWith(input.toLowerCase())) {
      // Suggest the pattern as-is (user still typing)
      suggestions.push(serverPattern);
    }
    // If user typed something that extends the pattern
    else if (input.toLowerCase().startsWith(serverPattern.toLowerCase())) {
      suggestions.push(input);
    }
    // If user typed "Live7" or "-Live7" etc, combine with base
    else {
      // Try to detect if user typed a suffix like "Live7", "-Live7", etc
      const baseParts = serverPattern.split('-');
      const basePrefix = baseParts.length > 1 ? baseParts[0] + '-' : serverPattern;
      if (input.startsWith('-')) {
        suggestions.push(baseParts[0] + input);
      } else {
        suggestions.push(serverPattern + input);
      }
    }

    // Also add suggestions from existing server list that partially match
    ss.forEach(s => {
      if (s.toLowerCase().includes(input.toLowerCase()) && !suggestions.includes(s)) {
        suggestions.push(s);
      }
    });

    return suggestions.slice(0, 5); // max 5 suggestions
  };

  const autoSuggestions = manualMode ? getAutoSuggestions() : [];

  // Broker search handler
  const hbs = (v) => {
    setBs(v); setSb(""); setMs(""); setSr([]); setManualMode(false); setManualInput("");
    if (st) clearTimeout(st);
    if (v.length < 2) { setDd(false); return; }
    setDd(true);
    const to = setTimeout(async () => {
      setSg(true);
      try {
        const s = await (await import("./supabase")).supabase.auth.getSession();
        const tk = s.data.session?.access_token;
        const r = await fetch(`${API_URL}/api/mt5/servers?query=${encodeURIComponent(v)}`, { headers: { Authorization: `Bearer ${tk}` } });
        const d = await r.json();
        if (d.results) setSr(d.results);
      } catch (e) {}
      setSg(false);
    }, 500);
    setSt(to);
  };

  const pb = (b) => { setSb(b); setBs(b); setDd(false); setMs(""); setManualMode(false); setManualInput(""); };

  // Select server from dropdown
  const pickServer = (sv) => { setMs(sv); setVd(false); setVm(""); setManualMode(false); setManualInput(""); };

  // Select server from autocomplete suggestion
  const pickSuggestion = (sv) => { setMs(sv); setManualInput(sv); setShowSuggestions(false); setVd(false); setVm(""); };

  // Toggle manual mode
  const enterManualMode = () => { setManualMode(true); setMs(""); setManualInput(""); setVd(false); setVm(""); };
  const exitManualMode = () => { setManualMode(false); setManualInput(""); setMs(""); setVd(false); setVm(""); };

  // Manual input change — also set ms for validation
  const handleManualInput = (v) => {
    setManualInput(v);
    setMs(v.trim());
    setVd(false); setVm("");
    setShowSuggestions(v.trim().length > 0);
  };

  // Validate (v6 - rate-limit aware + modal UX)
  const hv = async () => {
    setErr(""); setVm(""); setVd(false);
    if (!ml) { setErr("Please enter your MT5 Login."); return; }
    if (!mp) { setErr("Please enter your MT5 Password."); return; }
    if (!sb) { setErr("Please select your broker."); return; }
    if (!ms) { setErr(manualMode ? "Please type your server name." : "Please select a server."); return; }
    setVg(true);
    try {
      const s = await (await import("./supabase")).supabase.auth.getSession();
      const tk = s.data.session?.access_token;
      const r = await fetch(`${API_URL}/api/mt5/validate`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` }, body: JSON.stringify({ mt5Login: ml, mt5Password: mp, mt5Server: ms }) });
      const d = await r.json();

      // 429 or cooldownActive → show cooldown modal
      if (r.status === 429 || d.cooldownActive) {
        setVModal({
          type: "cooldown",
          attemptsUsed: d.attemptsUsed || 10,
          attemptsMax: d.attemptsMax || 10,
          cooldownEndsAt: d.cooldownEndsAt,
          title: d.error === "Cooldown still active." ? "Cooldown Still Active" : "Validation Limit Reached",
        });
      } else if (d.valid) {
        setVm(d.message || "Credentials valid!"); setVd(true);
        setVModal({ type: "success", message: d.message || "Your MT5 credentials have been successfully validated." });
      } else {
        // Failed attempt within limit
        setVModal({
          type: "fail",
          attemptsUsed: d.attemptsUsed || 1,
          attemptsMax: d.attemptsMax || 10,
          reason: d.error || "Invalid MT5 credentials.",
        });
      }
    } catch (e) { setErr("Validation failed. Please check your connection."); }
    setVg(false);
  };

  // Connect
  const hc = async () => {
    setErr("");
    if (!ml || !mp || !sb || !ms) { setErr("Please fill in all fields."); return; }
    setLd(true);
    try { if (isSwap) await onSwap(ml, mp, sb, ms, licenseId); else await onConnect(ml, mp, sb, ms, licenseId); }
    catch (e) { setErr(e.message || "Connection failed."); }
    setLd(false);
  };

  const is = { width: "100%", padding: "14px 16px", background: "#ffffff04", border: "1px solid #ffffff0a", borderRadius: 10, color: "#E2E8F0", fontSize: 15, fontFamily: "inherit", outline: "none", boxSizing: "border-box" };

  return (
    <div style={{ maxWidth: 560, margin: "0 auto", animation: "fadeUp 0.4s ease" }}>
      <BackButton onClick={onBack} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 40 }}>{[{ label: "Activate Key", done: true }, { label: isSwap ? "Change MT5" : "Login MT5", active: true }, { label: "System Ready", active: false }].map((s, i) => <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}><div style={{ width: 28, height: 28, borderRadius: "50%", background: s.done ? "#22C55E" : s.active ? "#22D3EE" : "#1E293B", display: "flex", alignItems: "center", justifyContent: "center", color: (s.done || s.active) ? "#06060B" : "#475569", fontSize: 12, fontWeight: 700 }}>{s.done ? "✓" : i + 1}</div><span style={{ color: (s.done || s.active) ? "#E2E8F0" : "#334155", fontSize: 13, fontWeight: s.active ? 600 : 400 }}>{s.label}</span>{i < 2 && <div style={{ width: 40, height: 1, background: "#1E293B" }} />}</div>)}</div>
      <div style={{ padding: 32, borderRadius: 16, background: "#0A0A12", border: "1px solid #ffffff08" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 6, marginBottom: 20, background: "#22C55E10", border: "1px solid #22C55E20" }}><span style={{ color: "#22C55E", fontSize: 12 }}>✓</span><span style={{ color: "#22C55E", fontSize: 12, fontWeight: 600 }}>License: {tl?.key || "—"}</span></div>
        {isSwap && <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 20, background: "#F59E0B08", border: "1px solid #F59E0B20" }}><div style={{ color: "#F59E0B", fontSize: 12, fontWeight: 600 }}>Change MT5 Account</div><div style={{ color: "#475569", fontSize: 11, marginTop: 2 }}>Your current connection will be removed and replaced.</div></div>}
        <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8, color: "#F1F5F9" }}>{isSwap ? "Change MT5 Account" : "Login MT5 Account"}</h2>
        <p style={{ color: "#94A3B8", fontSize: 14, marginBottom: 28, fontWeight: 300 }}>{isSwap ? "Enter new MT5 credentials. Current connection will be disconnected." : "Enter your MT5 trading account credentials. All data is encrypted."}</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {/* MT5 Login */}
          <div><label style={{ color: "#94A3B8", fontSize: 13, marginBottom: 6, display: "block", fontWeight: 500 }}>MT5 Login (Account Number)</label><input type="text" value={ml} onChange={e => { setMl(e.target.value); setVd(false); setVm(""); }} placeholder="501234" style={is} /></div>

          {/* MT5 Password */}
          <div><label style={{ color: "#94A3B8", fontSize: 13, marginBottom: 6, display: "block", fontWeight: 500 }}>MT5 Password (Investor or Master)</label><input type="password" value={mp} onChange={e => { setMp(e.target.value); setVd(false); setVm(""); }} placeholder="••••••••" style={is} /></div>

          {/* ====== INFO BOX — How To Find Your Server Name ====== */}
          <div style={{ padding: "18px 20px", borderRadius: 10, background: "#0891B206", border: "1px solid #0891B215" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <span style={{ fontSize: 17 }}>ℹ️</span>
              <span style={{ color: "#22D3EE", fontSize: 15, fontWeight: 700 }}>How To Find Your Server Name</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, color: "#E2E8F0", fontSize: 14, lineHeight: 1.7 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <span style={{ color: "#94A3B8", flexShrink: 0, marginTop: 2 }}>•</span>
                <span>Select your <b style={{ color: "#F1F5F9" }}>broker</b> below, then choose your <b style={{ color: "#F1F5F9" }}>server</b> from the dropdown list</span>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <span style={{ color: "#94A3B8", flexShrink: 0, marginTop: 2 }}>•</span>
                <span>If your server is <b style={{ color: "#F59E0B" }}>NOT in the list</b>, click <b style={{ color: "#F59E0B" }}>"My server is not in this list"</b> button below the server list to <b style={{ color: "#F59E0B" }}>type it manually</b></span>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <span style={{ color: "#94A3B8", flexShrink: 0, marginTop: 2 }}>•</span>
                <span>Server name must be <b style={{ color: "#EF4444" }}>100% exact</b> — including <b style={{ color: "#F1F5F9" }}>uppercase/lowercase</b>, <b style={{ color: "#F1F5F9" }}>dashes (-)</b>, <b style={{ color: "#F1F5F9" }}>spaces</b>, and <b style={{ color: "#F1F5F9" }}>numbers</b></span>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <span style={{ color: "#94A3B8", flexShrink: 0, marginTop: 2 }}>•</span>
                <span><b style={{ color: "#EF4444" }}>Wrong spelling or spacing</b> will cause your MT5 account to <b style={{ color: "#EF4444" }}>fail connection</b>, and our system <b style={{ color: "#EF4444" }}>will not detect your account</b></span>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <span style={{ color: "#94A3B8", flexShrink: 0, marginTop: 2 }}>•</span>
                <span>Find your server name from:</span>
              </div>
              <div style={{ paddingLeft: 18, display: "flex", flexDirection: "column", gap: 5 }}>
                <span>→ Your <b style={{ color: "#F1F5F9" }}>broker's website</b> (account dashboard)</span>
                <span>→ <b style={{ color: "#F1F5F9" }}>Registration email</b> from your broker</span>
                <span>→ Contact your <b style={{ color: "#F1F5F9" }}>broker's Customer Support</b> if you can't find it</span>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <span style={{ color: "#94A3B8", flexShrink: 0, marginTop: 2 }}>•</span>
                <span>Always click <b style={{ color: "#22D3EE" }}>Validate</b> before connecting to confirm everything is correct</span>
              </div>
            </div>
          </div>

          {/* ====== BROKER SEARCH ====== */}
          <div style={{ position: "relative", zIndex: 20 }}><label style={{ color: "#94A3B8", fontSize: 13, marginBottom: 6, display: "block", fontWeight: 500 }}>Broker</label>
            <div style={{ position: "relative" }}><input type="text" value={bs} onChange={e => hbs(e.target.value)} onFocus={() => { if (sr.length > 0) setDd(true); }} placeholder="Search broker... (e.g. Exness, ICMarkets, HFM)" style={{ ...is, paddingRight: 40, background: sb ? "#22D3EE06" : "#ffffff04", border: `1px solid ${sb ? "#22D3EE25" : dd ? "#22D3EE30" : "#ffffff0a"}`, borderRadius: dd ? "10px 10px 0 0" : 10, color: sb ? "#22D3EE" : "#E2E8F0", fontWeight: sb ? 600 : 400 }} />
              <span onClick={() => { if (sb) { setSb(""); setBs(""); setMs(""); setSr([]); setManualMode(false); setManualInput(""); } else setDd(!dd); }} style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", color: "#475569", cursor: "pointer", fontSize: sb ? 16 : 12, userSelect: "none" }}>{sb ? "✕" : sg ? "⟳" : "▼"}</span></div>
            {dd && <div style={{ position: "absolute", top: "100%", left: 0, right: 0, maxHeight: 240, overflowY: "auto", background: "#0D0D16", border: "1px solid #22D3EE20", borderTop: "none", borderRadius: "0 0 10px 10px", zIndex: 100 }}>
              {sg ? <div style={{ padding: 16, textAlign: "center", color: "#22D3EE", fontSize: 14 }}>Searching...</div> : Object.keys(bg).length === 0 ? <div style={{ padding: 16, textAlign: "center", color: "#475569", fontSize: 14 }}>{bs.length >= 2 ? "Broker not found." : "Type at least 2 characters."}</div>
                : Object.entries(bg).map(([b, svs]) => <div key={b} onClick={() => pb(b)} style={{ padding: "14px 16px", cursor: "pointer", color: "#CBD5E1", fontSize: 15, display: "flex", justifyContent: "space-between" }} onMouseEnter={e => e.currentTarget.style.background = "#ffffff06"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}><span style={{ fontWeight: 500 }}>{b}</span><span style={{ color: "#334155", fontSize: 12 }}>{svs.length} servers</span></div>)}
            </div>}
          </div>

          {/* ====== SERVER SELECTION — Hybrid: Dropdown + Manual ====== */}
          {sb && ss.length > 0 && !manualMode && (
            <div style={{ animation: "fadeUp 0.3s ease" }}>
              <label style={{ color: "#94A3B8", fontSize: 13, marginBottom: 8, display: "block", fontWeight: 500 }}>Server — <span style={{ color: "#22D3EE" }}>{sb}</span></label>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: 6, borderRadius: 10, background: "#ffffff02", border: "1px solid #ffffff08" }}>
                {ss.map(sv => { const dm = sv.toLowerCase().includes("demo") || sv.toLowerCase().includes("trial"); const sl = ms === sv;
                  return <button key={sv} onClick={() => pickServer(sv)} style={{ padding: "14px 16px", borderRadius: 8, background: sl ? "#22D3EE10" : "transparent", border: sl ? "1px solid #22D3EE30" : "1px solid transparent", color: sl ? "#22D3EE" : "#CBD5E1", cursor: "pointer", fontSize: 14, fontFamily: "inherit", fontWeight: sl ? 600 : 400, display: "flex", alignItems: "center", justifyContent: "space-between", textAlign: "left" }}
                    onMouseEnter={e => { if (!sl) e.currentTarget.style.background = "#ffffff04"; }} onMouseLeave={e => { if (!sl) e.currentTarget.style.background = "transparent"; }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}><div style={{ width: 20, height: 20, borderRadius: "50%", border: sl ? "none" : "1.5px solid #334155", background: sl ? "#22D3EE" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{sl && <span style={{ color: "#06060B", fontSize: 11, fontWeight: 900 }}>✓</span>}</div><span>{sv}</span></div>
                    {dm && <span style={{ padding: "3px 10px", borderRadius: 4, background: "#F59E0B12", color: "#F59E0B", fontSize: 11, fontWeight: 700 }}>DEMO</span>}
                  </button>; })}
              </div>

              {/* "My server is not listed" button */}
              <button onClick={enterManualMode} style={{ width: "100%", marginTop: 10, padding: "12px 16px", borderRadius: 8, background: "#F59E0B06", border: "1px solid #F59E0B15", color: "#F59E0B", cursor: "pointer", fontSize: 13, fontFamily: "inherit", fontWeight: 600, textAlign: "center" }}
                onMouseEnter={e => { e.currentTarget.style.background = "#F59E0B10"; e.currentTarget.style.borderColor = "#F59E0B30"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#F59E0B06"; e.currentTarget.style.borderColor = "#F59E0B15"; }}>
                ⚠ My server is not in this list — Type manually
              </button>
            </div>
          )}

          {/* ====== MANUAL SERVER INPUT with Autocomplete ====== */}
          {sb && manualMode && (
            <div style={{ animation: "fadeUp 0.3s ease" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <label style={{ color: "#94A3B8", fontSize: 13, fontWeight: 500 }}>Server — <span style={{ color: "#F59E0B" }}>Manual Entry</span></label>
                <button onClick={exitManualMode} style={{ background: "none", border: "none", color: "#22D3EE", fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 500, padding: 0 }}>← Back to list</button>
              </div>

              {/* Pattern hint */}
              {serverPattern && (
                <div style={{ padding: "10px 14px", borderRadius: 6, marginBottom: 8, background: "#22D3EE06", border: "1px solid #22D3EE10" }}>
                  <span style={{ color: "#64748B", fontSize: 12 }}>Server pattern detected: </span>
                  <span style={{ color: "#22D3EE", fontSize: 14, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>{serverPattern}</span>
                  <span style={{ color: "#64748B", fontSize: 12 }}> + your number</span>
                </div>
              )}

              {/* Manual input with autocomplete */}
              <div style={{ position: "relative" }}>
                <input
                  type="text"
                  value={manualInput}
                  onChange={e => handleManualInput(e.target.value)}
                  onFocus={() => { if (manualInput.trim()) setShowSuggestions(true); }}
                  onBlur={() => { setTimeout(() => setShowSuggestions(false), 200); }}
                  placeholder={serverPattern ? `e.g. ${serverPattern}7 or just type 7` : "Type full server name..."}
                  style={{ ...is, border: ms && manualInput ? "1px solid #22D3EE25" : "1px solid #ffffff0a", color: ms && manualInput ? "#22D3EE" : "#E2E8F0" }}
                />
                {/* Autocomplete suggestions dropdown */}
                {showSuggestions && autoSuggestions.length > 0 && (
                  <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#0D0D16", border: "1px solid #22D3EE20", borderTop: "none", borderRadius: "0 0 10px 10px", zIndex: 100, overflow: "hidden" }}>
                    <div style={{ padding: "8px 14px", color: "#475569", fontSize: 11, fontWeight: 600, borderBottom: "1px solid #ffffff06" }}>SUGGESTIONS — click to select</div>
                    {autoSuggestions.map((sug, i) => (
                      <div key={i} onClick={() => pickSuggestion(sug)}
                        style={{ padding: "12px 16px", cursor: "pointer", color: "#CBD5E1", fontSize: 14, fontFamily: "'JetBrains Mono', monospace", display: "flex", alignItems: "center", gap: 8 }}
                        onMouseEnter={e => e.currentTarget.style.background = "#ffffff06"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <span style={{ color: "#22D3EE", fontSize: 12 }}>→</span>
                        <span>{sug}</span>
                        {ss.includes(sug) && <span style={{ padding: "2px 8px", borderRadius: 3, background: "#22C55E12", color: "#22C55E", fontSize: 10, fontWeight: 700, marginLeft: "auto" }}>KNOWN</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Show current manual selection */}
              {ms && manualInput && (
                <div style={{ marginTop: 8, padding: "10px 14px", borderRadius: 6, background: "#22D3EE06", border: "1px solid #22D3EE15", display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: "#22D3EE", fontSize: 13, fontWeight: 600 }}>Selected:</span>
                  <span style={{ color: "#E2E8F0", fontSize: 14, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>{ms}</span>
                </div>
              )}

              {/* Reminder for manual entry */}
              <div style={{ marginTop: 8, padding: "10px 14px", borderRadius: 6, background: "#EF444406", border: "1px solid #EF444410" }}>
                <span style={{ color: "#FCA5A5", fontSize: 12 }}>⚠ Make sure the server name is <b>exactly</b> as shown on your broker — click <b>Validate</b> to check before connecting.</span>
              </div>
            </div>
          )}

          {/* Error / Success messages */}
          {err && <div style={{ padding: "12px 16px", borderRadius: 8, background: "#EF444410", border: "1px solid #EF444420", color: "#FCA5A5", fontSize: 14 }}>{err}</div>}
          {vm && <div style={{ padding: "14px 18px", borderRadius: 8, background: "#22C55E10", border: "1px solid #22C55E25", color: "#86EFAC", fontSize: 14, fontWeight: 500, display: "flex", alignItems: "center", gap: 8 }}><span style={{ fontSize: 18 }}>✓</span>{vm}</div>}

          {/* Validate button */}
          {!vd && <button onClick={hv} disabled={vg || !ml || !mp || !ms} style={{ width: "100%", padding: "16px", background: "#1E293B", border: (ml && mp && ms) ? "1px solid #22D3EE30" : "1px solid #ffffff08", borderRadius: 10, color: (ml && mp && ms) ? "#22D3EE" : "#475569", fontSize: 15, fontWeight: 600, cursor: vg ? "wait" : (ml && mp && ms) ? "pointer" : "default", fontFamily: "inherit", marginTop: 4 }}>{vg ? "Validating..." : "Validate MT5 Credentials"}</button>}

          {/* Connect button */}
          <button onClick={hc} disabled={ld || !vd} style={{ width: "100%", padding: "16px", background: ld ? "#1E293B" : vd ? "linear-gradient(135deg, #0891B2, #22D3EE)" : "#1E293B40", border: "none", borderRadius: 10, color: vd ? "#fff" : "#47556980", fontSize: 16, fontWeight: 700, cursor: ld ? "wait" : vd ? "pointer" : "not-allowed", fontFamily: "inherit", marginTop: 4, opacity: vd ? 1 : 0.5 }}>{ld ? (isSwap ? "Changing..." : "Connecting...") : isSwap ? "Change & Connect →" : "Login & Connect →"}</button>
        </div>
        <div style={{ marginTop: 24, padding: "14px 18px", borderRadius: 10, background: "#ffffff03", border: "1px solid #ffffff06", display: "flex", gap: 10, alignItems: "flex-start" }}><span style={{ color: "#22D3EE", fontSize: 16, flexShrink: 0 }}>🔒</span><div style={{ color: "#475569", fontSize: 12, lineHeight: 1.6 }}>Your credentials are encrypted with AES-256. We cannot see your password. You can delete your data at any time.</div></div>
      </div>
      {dd && <div onClick={() => setDd(false)} style={{ position: "fixed", inset: 0, zIndex: 10 }} />}

      {/* Validation Modal — Step 2 MetaAPI Leak Fix */}
      {vModal && <ValidationModal modal={vModal} timer={vTimer} onClose={() => setVModal(null)} />}
    </div>
  );
}

// ============================================================
// VALIDATION MODAL — Professional English UX (Step 2)
// Shows: attempt counter, cooldown timer, success confirmation
// ============================================================
function ValidationModal({ modal, timer, onClose }) {
  const { type, attemptsUsed = 0, attemptsMax = 10, reason, message, title } = modal;

  // Progress bar color based on attempts
  const getProgressColor = () => {
    if (attemptsUsed >= 10) return "#EF4444"; // red
    if (attemptsUsed >= 9) return "#F97316"; // orange
    if (attemptsUsed >= 6) return "#F59E0B"; // yellow
    return "#22C55E"; // green
  };

  const formatTimer = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return { mm: String(m).padStart(2, "0"), ss: String(s).padStart(2, "0") };
  };

  const t = formatTimer(timer);
  const isCooldown = type === "cooldown";
  const isSuccess = type === "success";
  const isFail = type === "fail";

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
      animation: "fadeIn 0.2s ease", padding: 20,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        maxWidth: 480, width: "100%",
        background: "#0A0A12", border: "1px solid #1E293B", borderRadius: 16,
        padding: 32, position: "relative",
        animation: "fadeUp 0.3s ease",
      }}>
        {/* Close button */}
        <button onClick={onClose} style={{
          position: "absolute", top: 16, right: 16, background: "none", border: "none",
          color: "#64748B", fontSize: 20, cursor: "pointer", fontFamily: "inherit",
          width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center",
          borderRadius: 6,
        }}>✕</button>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 10,
            background: isSuccess ? "#22C55E15" : isCooldown ? "#EF444415" : "#F59E0B15",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 22,
          }}>
            {isSuccess ? "✓" : isCooldown ? "🔒" : "⚠️"}
          </div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#F1F5F9" }}>
            {isSuccess ? "Credentials Verified" : isCooldown ? (title || "Validation Limit Reached") : "Validation Failed"}
          </h2>
        </div>

        {/* Body */}
        {isSuccess && (
          <>
            <p style={{ color: "#94A3B8", fontSize: 14, lineHeight: 1.6, margin: "0 0 24px 0" }}>
              {message}
            </p>
            <p style={{ color: "#64748B", fontSize: 13, margin: "0 0 24px 0" }}>
              You may now proceed to connect.
            </p>
          </>
        )}

        {isFail && (
          <>
            <p style={{ color: "#E2E8F0", fontSize: 14, lineHeight: 1.6, margin: "0 0 8px 0" }}>
              Invalid MT5 credentials detected.
            </p>
            <p style={{ color: "#94A3B8", fontSize: 13, margin: "0 0 20px 0" }}>
              Reason: {reason}
            </p>

            {/* Progress bar */}
            <div style={{ marginBottom: 20, padding: 16, background: "#06060B", borderRadius: 10, border: "1px solid #1E293B" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <span style={{ color: "#94A3B8", fontSize: 13, fontWeight: 500 }}>Attempts used:</span>
                <span style={{ color: getProgressColor(), fontSize: 14, fontWeight: 700, fontFamily: "monospace" }}>{attemptsUsed} / {attemptsMax}</span>
              </div>
              <div style={{ height: 6, background: "#1E293B", borderRadius: 3, overflow: "hidden" }}>
                <div style={{
                  width: `${Math.min(100, (attemptsUsed / attemptsMax) * 100)}%`,
                  height: "100%",
                  background: getProgressColor(),
                  transition: "width 0.3s, background 0.3s",
                  borderRadius: 3,
                }} />
              </div>
            </div>

            <div style={{ padding: "12px 14px", background: "#22D3EE08", border: "1px solid #22D3EE20", borderRadius: 8, marginBottom: 20 }}>
              <div style={{ color: "#22D3EE", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>ⓘ Security Notice</div>
              <div style={{ color: "#94A3B8", fontSize: 12, lineHeight: 1.5 }}>
                For security reasons, validation is limited to 10 failed attempts per 30 minutes.
              </div>
            </div>

            <p style={{ color: "#64748B", fontSize: 13, margin: "0 0 20px 0", lineHeight: 1.5 }}>
              Please verify your login, password, and server name, then try again.
            </p>
          </>
        )}

        {isCooldown && (
          <>
            <p style={{ color: "#E2E8F0", fontSize: 14, lineHeight: 1.6, margin: "0 0 20px 0" }}>
              {modal.title === "Cooldown Still Active"
                ? "You have reached the maximum number of failed validation attempts."
                : "You have reached the maximum number of failed validation attempts."}
            </p>

            {/* Attempts bar — full red */}
            <div style={{ marginBottom: 20, padding: 16, background: "#06060B", borderRadius: 10, border: "1px solid #1E293B" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <span style={{ color: "#94A3B8", fontSize: 13, fontWeight: 500 }}>Attempts used:</span>
                <span style={{ color: "#EF4444", fontSize: 14, fontWeight: 700, fontFamily: "monospace" }}>{attemptsUsed} / {attemptsMax} (FULL)</span>
              </div>
              <div style={{ height: 6, background: "#1E293B", borderRadius: 3, overflow: "hidden" }}>
                <div style={{ width: "100%", height: "100%", background: "#EF4444", borderRadius: 3 }} />
              </div>
            </div>

            {/* Timer */}
            <div style={{
              padding: 24, background: "#EF444408", borderRadius: 12, border: "1px solid #EF444420",
              textAlign: "center", marginBottom: 20,
            }}>
              <div style={{ color: "#94A3B8", fontSize: 13, marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                <span style={{ fontSize: 16 }}>⏱</span>
                {modal.title === "Cooldown Still Active" ? "Please try again in:" : "Cooldown active"}
              </div>
              <div style={{
                fontSize: 48, fontWeight: 700, color: "#F1F5F9", fontFamily: "monospace",
                letterSpacing: 2, lineHeight: 1,
                animation: timer < 300 ? "pulse 1s ease-in-out infinite" : "none",
              }}>
                {t.mm}<span style={{ color: "#EF4444" }}>:</span>{t.ss}
              </div>
              <div style={{ display: "flex", gap: 32, justifyContent: "center", marginTop: 6 }}>
                <span style={{ color: "#475569", fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>minutes</span>
                <span style={{ color: "#475569", fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>seconds</span>
              </div>
            </div>

            <div style={{ padding: "12px 14px", background: "#22D3EE08", border: "1px solid #22D3EE20", borderRadius: 8, marginBottom: 20 }}>
              <div style={{ color: "#22D3EE", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>ⓘ Security Notice</div>
              <div style={{ color: "#94A3B8", fontSize: 12, lineHeight: 1.5 }}>
                {modal.title === "Cooldown Still Active"
                  ? "This cooldown helps protect your account and our infrastructure from excessive validation requests. If you believe your credentials are correct, please contact support."
                  : "For your account security, further validation attempts are temporarily suspended. You may navigate away from this page — the cooldown will continue running in the background."}
              </div>
            </div>
          </>
        )}

        {/* Action button */}
        <button onClick={onClose} style={{
          width: "100%", padding: "14px", borderRadius: 10,
          background: isSuccess ? "linear-gradient(135deg, #0891B2, #22D3EE)" : "#1E293B",
          border: isSuccess ? "none" : "1px solid #334155",
          color: isSuccess ? "#fff" : "#E2E8F0",
          fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
        }}>
          {isSuccess ? "Proceed" : "OK"}
        </button>
      </div>
      <style>{`@keyframes fadeIn{from{opacity:0}to{opacity:1}} @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.6}}`}</style>
    </div>
  );
}

// ============================================================
// v5.5: LIVE POSITIONS TABLE — MT5-broker-style table
// Shows live open positions for demo video + marketing
// Props: positions[] — raw positions array from /api/mt5/status
// ============================================================
function LivePositionsTable({ positions }) {
  const [limit, setLimit] = useState(10);
  const list = Array.isArray(positions) ? positions : [];
  const total = list.length;
  const visible = limit === "all" ? list : list.slice(0, Number(limit));
  const totalPL = list.reduce((s, p) => s + (Number(p.profit) || 0), 0);
  const fmtPrice = v => { const n = Number(v); if (!n) return "—"; return n.toFixed(n > 100 ? 2 : 5).replace(/\.?0+$/, "") || n.toFixed(2); };
  const fmtTime = iso => { if (!iso) return "—"; try { const d = new Date(iso); return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:${String(d.getSeconds()).padStart(2, "0")}`; } catch { return "—"; } };
  const fmtPL = v => { const n = Number(v) || 0; return { text: n >= 0 ? `+${n.toFixed(2)}` : n.toFixed(2), color: n > 0 ? "#22C55E" : n < 0 ? "#EF4444" : "#94A3B8" }; };

  if (total === 0) {
    return (
      <div style={{ marginBottom: 16, padding: "16px 14px", borderRadius: 10, background: "#05050A", border: "1px solid #ffffff06", textAlign: "center" }}>
        <div style={{ color: "#475569", fontSize: 12 }}>No open positions</div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 16, borderRadius: 10, background: "#05050A", border: "1px solid #ffffff06", overflow: "hidden" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderBottom: "1px solid #ffffff06" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#22C55E" }} />
          <span style={{ color: "#F1F5F9", fontSize: 12, fontWeight: 600 }}>Live Positions</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: "#64748B", fontSize: 10 }}>Show</span>
          <select value={limit} onChange={e => setLimit(e.target.value === "all" ? "all" : Number(e.target.value))} style={{ padding: "3px 6px", borderRadius: 5, background: "#0A0A12", border: "1px solid #ffffff0a", color: "#CBD5E1", fontSize: 10, fontFamily: "inherit", cursor: "pointer" }}>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value="all">All</option>
          </select>
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5, fontFamily: "'JetBrains Mono', monospace" }}>
          <thead>
            <tr style={{ background: "#0A0A12" }}>
              <th style={{ padding: "7px 9px", textAlign: "left", color: "#64748B", fontWeight: 500, borderBottom: "1px solid #ffffff08", fontFamily: "inherit", fontSize: 10 }}>Symbol</th>
              <th style={{ padding: "7px 9px", textAlign: "left", color: "#64748B", fontWeight: 500, borderBottom: "1px solid #ffffff08", fontFamily: "inherit", fontSize: 10 }}>Ticket</th>
              <th style={{ padding: "7px 9px", textAlign: "left", color: "#64748B", fontWeight: 500, borderBottom: "1px solid #ffffff08", fontFamily: "inherit", fontSize: 10 }}>Time</th>
              <th style={{ padding: "7px 9px", textAlign: "left", color: "#64748B", fontWeight: 500, borderBottom: "1px solid #ffffff08", fontFamily: "inherit", fontSize: 10 }}>Type</th>
              <th style={{ padding: "7px 9px", textAlign: "right", color: "#64748B", fontWeight: 500, borderBottom: "1px solid #ffffff08", fontFamily: "inherit", fontSize: 10 }}>Vol</th>
              <th style={{ padding: "7px 9px", textAlign: "right", color: "#64748B", fontWeight: 500, borderBottom: "1px solid #ffffff08", fontFamily: "inherit", fontSize: 10 }}>Entry</th>
              <th style={{ padding: "7px 9px", textAlign: "right", color: "#64748B", fontWeight: 500, borderBottom: "1px solid #ffffff08", fontFamily: "inherit", fontSize: 10 }}>SL</th>
              <th style={{ padding: "7px 9px", textAlign: "right", color: "#64748B", fontWeight: 500, borderBottom: "1px solid #ffffff08", fontFamily: "inherit", fontSize: 10 }}>TP</th>
              <th style={{ padding: "7px 9px", textAlign: "right", color: "#64748B", fontWeight: 500, borderBottom: "1px solid #ffffff08", fontFamily: "inherit", fontSize: 10 }}>Current</th>
              <th style={{ padding: "7px 9px", textAlign: "right", color: "#64748B", fontWeight: 500, borderBottom: "1px solid #ffffff08", fontFamily: "inherit", fontSize: 10 }}>Profit</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((p, i) => { const pl = fmtPL(p.profit); return (
              <tr key={p.ticket || i} style={{ borderBottom: i === visible.length - 1 ? "none" : "1px solid #ffffff04" }}>
                <td style={{ padding: "8px 9px", color: "#E2E8F0" }}>{p.symbol || "—"}</td>
                <td style={{ padding: "8px 9px", color: "#94A3B8" }}>{p.ticket || "—"}</td>
                <td style={{ padding: "8px 9px", color: "#94A3B8" }}>{fmtTime(p.time)}</td>
                <td style={{ padding: "8px 9px" }}><span style={{ color: p.type === "buy" ? "#22D3EE" : "#EF4444", fontWeight: 600 }}>{p.type || "—"}</span></td>
                <td style={{ padding: "8px 9px", textAlign: "right", color: "#CBD5E1" }}>{p.volume ? Number(p.volume).toFixed(2) : "—"}</td>
                <td style={{ padding: "8px 9px", textAlign: "right", color: "#CBD5E1" }}>{fmtPrice(p.openPrice)}</td>
                <td style={{ padding: "8px 9px", textAlign: "right", color: p.stopLoss ? "#CBD5E1" : "#475569" }}>{p.stopLoss ? fmtPrice(p.stopLoss) : "—"}</td>
                <td style={{ padding: "8px 9px", textAlign: "right", color: p.takeProfit ? "#CBD5E1" : "#475569" }}>{p.takeProfit ? fmtPrice(p.takeProfit) : "—"}</td>
                <td style={{ padding: "8px 9px", textAlign: "right", color: "#CBD5E1" }}>{fmtPrice(p.currentPrice)}</td>
                <td style={{ padding: "8px 9px", textAlign: "right", color: pl.color, fontWeight: 600 }}>{pl.text}</td>
              </tr>
            ); })}
          </tbody>
        </table>
      </div>

      <div style={{ padding: "8px 14px", borderTop: "1px solid #ffffff06", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#0A0A12", flexWrap: "wrap", gap: 8 }}>
        <span style={{ color: "#64748B", fontSize: 10 }}>Showing {visible.length} of {total} positions</span>
        <span style={{ color: "#64748B", fontSize: 10 }}>Total P/L: <span style={{ color: fmtPL(totalPL).color, fontWeight: 600 }}>{fmtPL(totalPL).text}</span></span>
      </div>
    </div>
  );
}

function HomePage({ user, onNavigate, onSystemDisconnect, onChangeMT5, onStartConnect, toggleSL, toggleTP }) {
  const [cm, setCm] = useState(null); const accounts = user?.accounts || [];
  const ac = accounts.filter(a => a.slEnabled || a.tpEnabled).length;
  const tp = accounts.reduce((s, a) => s + (a.sync?.positions || 0), 0);
  const ts = accounts.reduce((s, a) => s + (a.sync?.totalSynced || 0), 0);
  const PC = { website: "#8B5CF6", mql5: "#FFB800", admin: "#22D3EE" };
  const PN = { website: "Website", mql5: "MQL5", admin: "Admin Gift" };

  const hcm = async (cid, lid) => { if (!window.confirm("Change MT5 account? This will fully disconnect the current MT5.")) return; setCm(cid); try { await onChangeMT5(cid, lid); } catch (e) { alert(e.message || "Failed."); } setCm(null); };

  return (
    <div style={{ animation: "fadeUp 0.4s ease" }}>
      <div style={{ padding: "16px 20px", borderRadius: 12, marginBottom: 24, background: "linear-gradient(135deg, #0891B208, #22D3EE05)", border: "1px solid #22D3EE15", display: "flex", alignItems: "center", gap: 14 }}><span style={{ fontSize: 22 }}>☁️</span><div><div style={{ color: "#22D3EE", fontSize: 13, fontWeight: 600 }}>Cloud SL/TP Sync — 1 License = 1 Active Connection</div><div style={{ color: "#475569", fontSize: 12, marginTop: 2 }}>Change your MT5 anytime. Need 2 simultaneous? Use 2 license keys.</div></div></div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 24 }}>
        {[{ label: "Licenses", value: accounts.length, color: "#22D3EE" }, { label: "Active Systems", value: ac, color: "#22C55E" }, { label: "Positions", value: accounts.reduce((s, a) => s + (a.breakdown?.total || a.sync?.positions || 0), 0), color: "#A78BFA" }, { label: "No SL", value: accounts.reduce((s, a) => s + (a.breakdown?.noSL || 0), 0), color: accounts.reduce((s, a) => s + (a.breakdown?.noSL || 0), 0) > 0 ? "#EF4444" : "#475569" }].map((s, i) => <div key={i} style={{ padding: 16, borderRadius: 12, textAlign: "center", background: "#0A0A12", border: "1px solid #ffffff08" }}><div style={{ fontSize: 26, fontWeight: 800, color: s.color }}>{s.value}</div><div style={{ color: "#475569", fontSize: 11, marginTop: 4, fontWeight: 500 }}>{s.label}</div></div>)}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}><h2 style={{ fontSize: 18, fontWeight: 700, color: "#F1F5F9" }}>License Slots</h2><button onClick={() => onNavigate("activate")} style={{ padding: "8px 18px", borderRadius: 8, background: "linear-gradient(135deg, #0891B2, #22D3EE)", border: "none", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>+ Add License</button></div>

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {accounts.map((acc, idx) => {
          const l = acc.license, sy = acc.sync, hc = acc.broker && acc.server;
          const exp = l?.expiry && new Date(l.expiry).getTime() <= Date.now();
          const daysLeft = l?.expiry ? Math.ceil((new Date(l.expiry).getTime() - Date.now()) / 86400000) : null;
          const showWarning = !exp && daysLeft !== null && daysLeft <= 7;
          return (
            <div key={idx} style={{ padding: 24, borderRadius: 16, background: "#0A0A12", border: `1px solid ${exp ? "#EF444430" : hc ? "#22D3EE10" : "#ffffff08"}` }}>
              {showWarning && (
                <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 8, background: daysLeft <= 1 ? "#EF444410" : daysLeft <= 3 ? "#F9731610" : "#F59E0B10", border: `1px solid ${daysLeft <= 1 ? "#EF444430" : daysLeft <= 3 ? "#F9731630" : "#F59E0B30"}`, display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 18 }}>{daysLeft <= 1 ? "🚨" : daysLeft <= 3 ? "⚠️" : "⏰"}</span>
                  <div>
                    <div style={{ color: daysLeft <= 1 ? "#FCA5A5" : daysLeft <= 3 ? "#FDBA74" : "#FCD34D", fontSize: 13, fontWeight: 700 }}>
                      {daysLeft <= 1 ? "License expires TOMORROW!" : `License expires in ${daysLeft} days`}
                    </div>
                    <div style={{ color: "#94A3B8", fontSize: 11, marginTop: 2 }}>SL/TP sync will stop automatically on expiry. Contact support to renew.</div>
                  </div>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div><div style={{ fontSize: 16, fontWeight: 700, color: "#F1F5F9", display: "flex", alignItems: "center", gap: 8 }}>{hc ? <>MT5 #{acc.mt5Account} <span style={{ padding: "2px 8px", borderRadius: 4, background: "#22D3EE10", color: "#22D3EE", fontSize: 11, fontWeight: 600 }}>{acc.broker}</span></> : <span style={{ color: "#475569" }}>No MT5 Connected</span>}</div><div style={{ color: "#475569", fontSize: 12, marginTop: 2 }}>{hc ? acc.server : "Connect an MT5 account to get started"}</div></div>
                <div style={{ display: "flex", gap: 8 }}><span style={{ padding: "3px 10px", borderRadius: 5, background: (PC[l?.platform] || "#475569") + "15", color: PC[l?.platform] || "#475569", fontSize: 11, fontWeight: 700 }}>{PN[l?.platform] || "—"}</span><span style={{ padding: "3px 10px", borderRadius: 5, background: (PLANS[l?.plan]?.color || "#475569") + "15", color: PLANS[l?.plan]?.color || "#475569", fontSize: 11, fontWeight: 700 }}>{PLANS[l?.plan]?.label || "—"}</span></div>
              </div>
              <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}><StatusDot on={hc} label="MT5 Connection" color="#22D3EE" /><StatusDot on={acc.slEnabled} label="SL System" color="#22C55E" /><StatusDot on={acc.tpEnabled} label="TP System" color="#A78BFA" /></div>
              <div style={{ marginBottom: 16, padding: "14px 18px", borderRadius: 10, background: "#ffffff03", border: "1px solid #ffffff06" }}><div style={{ color: "#64748B", fontSize: 11, fontWeight: 500, marginBottom: 8 }}>LICENSE TIME REMAINING</div><CountdownTimer expiryDate={l?.expiry} /></div>
              {hc && <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 8, marginBottom: 16 }}>
                {(() => { const bd = acc.breakdown || {}; return [{ label: "Positions", value: bd.total || sy?.positions || 0, color: "#22D3EE" }, { label: "SL Set", value: bd.slSynced || 0, color: "#F59E0B" }, { label: "SL at BE", value: bd.slBE || 0, color: "#22C55E" }, { label: "No SL", value: bd.noSL || 0, color: bd.noSL > 0 ? "#EF4444" : "#475569" }, { label: "TP Set", value: bd.tpSet || 0, color: "#A78BFA" }, { label: "No TP", value: bd.noTP || 0, color: bd.noTP > 0 ? "#F59E0B" : "#475569" }].map((s, i) => <div key={i} style={{ padding: "10px 8px", borderRadius: 10, textAlign: "center", background: "#0A0A12", border: "1px solid #ffffff08" }}><div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div><div style={{ color: "#475569", fontSize: 10, marginTop: 2, fontWeight: 500 }}>{s.label}</div></div>); })()}
              </div>}
              {hc && <LivePositionsTable positions={acc.positions || []} /> }
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: "#22D3EE60", letterSpacing: 1, marginBottom: 16 }}>{l?.key}</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {!hc && !exp && <button onClick={() => onStartConnect(l.id)} style={{ padding: "8px 16px", borderRadius: 8, background: "linear-gradient(135deg, #0891B2, #22D3EE)", border: "none", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Login MT5</button>}
                {hc && !exp && <>
                  <button onClick={() => toggleSL(l.id)} style={{ padding: "8px 16px", borderRadius: 8, background: acc.slEnabled ? "#22C55E15" : "#1E293B", border: `1px solid ${acc.slEnabled ? "#22C55E30" : "#ffffff0a"}`, color: acc.slEnabled ? "#22C55E" : "#94A3B8", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>{acc.slEnabled ? "Stop SL" : "Start SL"}</button>
                  <button onClick={() => toggleTP(l.id)} style={{ padding: "8px 16px", borderRadius: 8, background: acc.tpEnabled ? "#A78BFA15" : "#1E293B", border: `1px solid ${acc.tpEnabled ? "#A78BFA30" : "#ffffff0a"}`, color: acc.tpEnabled ? "#A78BFA" : "#94A3B8", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>{acc.tpEnabled ? "Stop TP" : "Start TP"}</button>
                </>}
                {hc && (acc.slEnabled || acc.tpEnabled) && <button onClick={() => onSystemDisconnect(l.id)} style={{ padding: "8px 16px", borderRadius: 8, background: "#EF444410", border: "1px solid #EF444420", color: "#EF4444", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Disconnect</button>}
                {hc && acc.connectionId && <button onClick={() => hcm(acc.connectionId, l.id)} disabled={cm === acc.connectionId} style={{ padding: "8px 16px", borderRadius: 8, background: "#F59E0B10", border: "1px solid #F59E0B20", color: "#F59E0B", fontSize: 12, fontWeight: 600, cursor: cm === acc.connectionId ? "wait" : "pointer", fontFamily: "inherit", opacity: cm === acc.connectionId ? 0.5 : 1 }}>{cm === acc.connectionId ? "Changing..." : "Change MT5"}</button>}
              </div>
            </div>
          );
        })}
        {accounts.length === 0 && <div style={{ padding: 48, borderRadius: 16, textAlign: "center", background: "#0A0A12", border: "1px dashed #ffffff10" }}><div style={{ color: "#475569", fontSize: 14, marginBottom: 12 }}>No licenses yet. Activate a key to get started.</div><button onClick={() => onNavigate("activate")} style={{ padding: "10px 24px", borderRadius: 8, background: "linear-gradient(135deg, #0891B2, #22D3EE)", border: "none", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Activate License Key →</button></div>}
      </div>
    </div>
  );
}

// ============================================================
// HISTORY PAGE — Full trade journal with P/L tracking
// ============================================================
function HistoryPage({ session }) {
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [startDate, setStartDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().split("T")[0]; });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [eventFilter, setEventFilter] = useState("");
  const [symbolFilter, setSymbolFilter] = useState("");
  const [exporting, setExporting] = useState(false);

  const fetchHistory = async (newOffset = 0) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: perPage, offset: newOffset });
      if (startDate) params.append("startDate", new Date(startDate).toISOString());
      if (endDate) params.append("endDate", new Date(endDate + "T23:59:59").toISOString());
      if (eventFilter) params.append("eventType", eventFilter);
      if (symbolFilter) params.append("symbol", symbolFilter);

      const res = await fetch(`${API_URL}/api/history?${params}`, { headers: { Authorization: `Bearer ${session.access_token}` } });
      const data = await res.json();
      setEvents(data.events || []);
      setTotal(data.total || 0);
      setOffset(newOffset);
    } catch (e) { console.error("History fetch failed:", e); }
    setLoading(false);
  };

  const fetchSummary = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("startDate", new Date(startDate).toISOString());
      if (endDate) params.append("endDate", new Date(endDate + "T23:59:59").toISOString());

      const res = await fetch(`${API_URL}/api/history/summary?${params}`, { headers: { Authorization: `Bearer ${session.access_token}` } });
      const data = await res.json();
      setSummary(data.summary || null);
    } catch (e) { console.error("Summary fetch failed:", e); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("startDate", new Date(startDate).toISOString());
      if (endDate) params.append("endDate", new Date(endDate + "T23:59:59").toISOString());

      const res = await fetch(`${API_URL}/api/history/export?${params}`, { headers: { Authorization: `Bearer ${session.access_token}` } });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `trade_history_${startDate}_${endDate}.csv`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error("Export failed:", e); }
    setExporting(false);
  };

  useEffect(() => { fetchHistory(0); fetchSummary(); }, [startDate, endDate, eventFilter, symbolFilter, perPage]);

  const dailyPL = events.filter(e => e.event_type === "position_close").reduce((sum, e) => sum + (e.position_profit || 0), 0);
  const closedCount = events.filter(e => e.event_type === "position_close").length;
  const uniqueSymbols = [...new Set(events.map(e => e.symbol).filter(Boolean))];

  const EB = { position_open: { label: "open", bg: "#22D3EE15", color: "#22D3EE" }, position_close: { label: "close", bg: "#EF444415", color: "#EF4444" }, sl_sync: { label: "sl sync", bg: "#22C55E15", color: "#22C55E" }, tp_sync: { label: "tp sync", bg: "#A78BFA15", color: "#A78BFA" }, sl_modify_user: { label: "sl user", bg: "#F59E0B15", color: "#F59E0B" }, tp_modify_user: { label: "tp user", bg: "#F59E0B15", color: "#F59E0B" }, sl_modify_ea: { label: "sl ea", bg: "#22C55E15", color: "#22C55E" }, tp_modify_ea: { label: "tp ea", bg: "#A78BFA15", color: "#A78BFA" } };

  const fmtTime = (t) => { const d = new Date(t); return d.toLocaleDateString("en-MY", { day: "2-digit", month: "short" }) + " " + d.toLocaleTimeString("en-MY", { hour: "2-digit", minute: "2-digit", second: "2-digit" }); };
  const fmtPL = (v) => { if (!v || v === 0) return { text: "$0.00", color: "#475569" }; return v > 0 ? { text: `+$${v.toFixed(2)}`, color: "#22C55E" } : { text: `-$${Math.abs(v).toFixed(2)}`, color: "#EF4444" }; };
  const fmtPrice = (v) => v ? Number(v).toFixed(2) : "—";

  const ss = { width: "100%", padding: "8px 12px", background: "#ffffff04", border: "1px solid #ffffff0a", borderRadius: 8, color: "#E2E8F0", fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" };

  return (
    <div style={{ animation: "fadeUp 0.4s ease" }}>
      {/* Filters */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ ...ss, width: 145, fontSize: 12 }} />
        <span style={{ color: "#475569", fontSize: 12 }}>to</span>
        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ ...ss, width: 145, fontSize: 12 }} />
        <select value={eventFilter} onChange={e => setEventFilter(e.target.value)} style={{ ...ss, width: 130, fontSize: 12 }}>
          <option value="">All events</option>
          <option value="position_open">Open</option>
          <option value="position_close">Close</option>
          <option value="sl_sync">SL sync</option>
          <option value="tp_sync">TP sync</option>
          <option value="sl_modify_user">SL user</option>
          <option value="tp_modify_user">TP user</option>
        </select>
        {uniqueSymbols.length > 0 && <select value={symbolFilter} onChange={e => setSymbolFilter(e.target.value)} style={{ ...ss, width: 120, fontSize: 12 }}>
          <option value="">All symbols</option>
          {uniqueSymbols.map(s => <option key={s} value={s}>{s}</option>)}
        </select>}
        <button onClick={handleExport} disabled={exporting} style={{ marginLeft: "auto", padding: "8px 16px", borderRadius: 8, background: "#ffffff04", border: "1px solid #ffffff0a", color: "#22D3EE", fontSize: 12, fontWeight: 600, cursor: exporting ? "wait" : "pointer", fontFamily: "inherit" }}>{exporting ? "Exporting..." : "Export CSV"}</button>
      </div>

      {/* Summary Cards */}
      {summary && <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 16 }}>
        {[{ label: "Opened", value: summary.positionsOpened, color: "#22D3EE" }, { label: "Closed", value: summary.positionsClosed, color: "#94A3B8" }, { label: "SL syncs", value: summary.slSyncsPerformed, color: "#22C55E" }, { label: "TP syncs", value: summary.tpSyncsPerformed, color: "#A78BFA" }, { label: "Balance", value: `$${(summary.latestBalance || 0).toFixed(2)}`, color: "#F59E0B" }].map((s, i) => (
          <div key={i} style={{ padding: "12px 14px", borderRadius: 10, background: "#0A0A12", border: "1px solid #ffffff08", textAlign: "center" }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
            <div style={{ color: "#475569", fontSize: 10, marginTop: 2, fontWeight: 500 }}>{s.label}</div>
          </div>
        ))}
      </div>}

      {/* Daily P/L Card */}
      <div style={{ padding: "14px 20px", borderRadius: 12, background: "#0A0A12", border: `1px solid ${dailyPL >= 0 ? "#22C55E15" : "#EF444415"}`, marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ color: "#94A3B8", fontSize: 12, fontWeight: 500 }}>Daily P/L — {startDate === endDate ? new Date(startDate).toLocaleDateString("en-MY", { day: "2-digit", month: "short", year: "numeric" }) : `${startDate} to ${endDate}`}</div>
          <div style={{ color: "#475569", fontSize: 11, marginTop: 2 }}>{closedCount} positions closed</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 26, fontWeight: 800, color: dailyPL >= 0 ? "#22C55E" : "#EF4444" }}>{dailyPL >= 0 ? "+" : "-"}${Math.abs(dailyPL).toFixed(2)}</div>
          <div style={{ fontSize: 11, color: dailyPL >= 0 ? "#22C55E" : "#EF4444" }}>{dailyPL >= 0 ? "Profit" : "Loss"}</div>
        </div>
      </div>

      {/* Events Table */}
      <div style={{ borderRadius: 12, border: "1px solid #ffffff08", overflow: "hidden", marginBottom: 12 }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse", minWidth: 700 }}>
            <thead>
              <tr style={{ background: "#0A0A12" }}>
                {["Time", "Event", "Ticket", "Symbol", "Dir", "Entry", "SL", "TP", "P/L", "Balance"].map(h => (
                  <th key={h} style={{ textAlign: h === "P/L" || h === "Balance" || h === "Entry" || h === "SL" || h === "TP" ? "right" : "left", padding: "10px 8px", fontWeight: 600, color: "#475569", fontSize: 11, borderBottom: "1px solid #ffffff08", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} style={{ padding: 32, textAlign: "center", color: "#475569" }}>Loading...</td></tr>
              ) : events.length === 0 ? (
                <tr><td colSpan={10} style={{ padding: 32, textAlign: "center", color: "#475569" }}>No events found for this period.</td></tr>
              ) : events.map((e, i) => {
                const eb = EB[e.event_type] || { label: e.event_type, bg: "#ffffff08", color: "#94A3B8" };
                const pl = fmtPL(e.position_profit);
                return (
                  <tr key={e.id || i} style={{ borderTop: "1px solid #ffffff06" }}
                    onMouseEnter={ev => ev.currentTarget.style.background = "#ffffff04"}
                    onMouseLeave={ev => ev.currentTarget.style.background = "transparent"}>
                    <td style={{ padding: "8px", color: "#64748B", fontSize: 11, whiteSpace: "nowrap" }}>{fmtTime(e.created_at)}</td>
                    <td style={{ padding: "8px" }}><span style={{ background: eb.bg, color: eb.color, padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 600, whiteSpace: "nowrap" }}>{eb.label}</span></td>
                    <td style={{ padding: "8px", fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: "#94A3B8" }}>{e.ticket || "—"}</td>
                    <td style={{ padding: "8px", color: "#CBD5E1", fontSize: 11 }}>{e.symbol || "—"}</td>
                    <td style={{ padding: "8px", color: e.direction === "buy" ? "#22C55E" : e.direction === "sell" ? "#EF4444" : "#475569", fontSize: 11, fontWeight: 600 }}>{e.direction ? e.direction.toUpperCase() : "—"}</td>
                    <td style={{ padding: "8px", textAlign: "right", color: "#CBD5E1", fontSize: 11 }}>{fmtPrice(e.entry_price)}</td>
                    <td style={{ padding: "8px", textAlign: "right", color: e.sl_after && e.sl_after !== e.sl_before ? eb.color : "#64748B", fontSize: 11 }}>{fmtPrice(e.sl_after)}</td>
                    <td style={{ padding: "8px", textAlign: "right", color: e.tp_after && e.tp_after !== e.tp_before ? eb.color : "#64748B", fontSize: 11 }}>{fmtPrice(e.tp_after)}</td>
                    <td style={{ padding: "8px", textAlign: "right", color: pl.color, fontSize: 11, fontWeight: e.event_type === "position_close" ? 700 : 400 }}>{pl.text}</td>
                    <td style={{ padding: "8px", textAlign: "right", color: "#CBD5E1", fontSize: 11 }}>{e.balance ? `$${Number(e.balance).toFixed(2)}` : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: "#64748B" }}>
        <span>Showing {total === 0 ? 0 : offset + 1}–{Math.min(offset + perPage, total)} of {total} events</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <select value={perPage} onChange={e => { setPerPage(parseInt(e.target.value)); setOffset(0); }} style={{ ...ss, width: 70, fontSize: 11, padding: "4px 8px" }}>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span style={{ color: "#334155" }}>per page</span>
          <button onClick={() => fetchHistory(Math.max(0, offset - perPage))} disabled={offset === 0} style={{ padding: "4px 12px", borderRadius: 6, background: "#ffffff04", border: "1px solid #ffffff0a", color: offset === 0 ? "#334155" : "#94A3B8", fontSize: 11, cursor: offset === 0 ? "default" : "pointer", fontFamily: "inherit" }}>Prev</button>
          <button onClick={() => fetchHistory(offset + perPage)} disabled={offset + perPage >= total} style={{ padding: "4px 12px", borderRadius: 6, background: "#ffffff04", border: "1px solid #ffffff0a", color: offset + perPage >= total ? "#334155" : "#94A3B8", fontSize: 11, cursor: offset + perPage >= total ? "default" : "pointer", fontFamily: "inherit" }}>Next</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// AGREEMENT PAGE — Full scrollable agreement with 3 tick boxes
// Must scroll to bottom before ticks are enabled.
// ============================================================
function AgreementPage({ onAccept, onDecline }) {
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [tick1, setTick1] = useState(false); // 18+
  const [tick2, setTick2] = useState(false); // read & understood
  const [tick3, setTick3] = useState(false); // agree & acknowledge
  const [accepting, setAccepting] = useState(false);
  const scrollRef = useState(null);

  const handleScroll = (e) => {
    const el = e.target;
    const threshold = 50;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
      setScrolledToBottom(true);
    }
  };

  const allTicked = tick1 && tick2 && tick3;

  const handleAccept = async () => {
    if (!allTicked) return;
    setAccepting(true);
    try { await onAccept(); } catch (e) { console.error(e); }
    setAccepting(false);
  };

  const Tick = ({ checked, onChange, disabled, label }) => (
    <div onClick={() => !disabled && onChange(!checked)} style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: disabled ? "not-allowed" : "pointer", userSelect: "none", padding: "10px 0", opacity: disabled ? 0.35 : 1, transition: "opacity 0.3s" }}>
      <div style={{ width: 22, height: 22, borderRadius: 6, flexShrink: 0, marginTop: 1, background: checked ? "#22D3EE" : "transparent", border: `2px solid ${checked ? "#22D3EE" : disabled ? "#334155" : "#475569"}`, display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s" }}>
        {checked && <span style={{ color: "#06060B", fontSize: 13, fontWeight: 900, lineHeight: 1 }}>✓</span>}
      </div>
      <span style={{ color: disabled ? "#475569" : "#CBD5E1", fontSize: 13, lineHeight: 1.6 }}>{label}</span>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#06060B", color: "#E2E8F0", fontFamily: "'Outfit', 'DM Sans', sans-serif", display: "flex", flexDirection: "column", alignItems: "center", padding: "24px 16px" }}>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />

      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
          <WolfLogo size={70} />
        </div>
        <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: 2, color: "#F1F5F9", marginBottom: 6 }}>LASKY SL/TP SYNC</div>
        <p style={{ color: "#475569", fontSize: 13, fontWeight: 300, margin: 0 }}>User Agreement, Terms of Service & Privacy Policy</p>
      </div>

      {/* Scroll indicator */}
      {!scrolledToBottom && (
        <div style={{ padding: "8px 16px", borderRadius: 8, marginBottom: 12, background: "#F59E0B08", border: "1px solid #F59E0B20", display: "flex", alignItems: "center", gap: 8, animation: "fadeUp 0.3s ease" }}>
          <span style={{ fontSize: 14 }}>↓</span>
          <span style={{ color: "#F59E0B", fontSize: 12, fontWeight: 600 }}>Please scroll to the bottom to read the entire agreement before accepting.</span>
        </div>
      )}

      {/* Agreement Text Container */}
      <div
        onScroll={handleScroll}
        style={{
          width: "100%", maxWidth: 700, height: "55vh", overflowY: "auto",
          padding: "28px 32px", borderRadius: 12,
          background: "#0A0A12", border: "1px solid #ffffff08",
          fontSize: 14, lineHeight: 1.75, color: "#E2E8F0", fontWeight: 500,
          marginBottom: 20,
        }}
      >
        <h2 style={{ textAlign: "center", color: "#22D3EE", fontSize: 18, fontWeight: 700, marginBottom: 4 }}>SHADOW TECHNOLOGIES</h2>
        <p style={{ textAlign: "center", color: "#CBD5E1", fontSize: 12, marginBottom: 4, fontWeight: 500 }}>LASKY SL/TP SYNC — Software-as-a-Service Platform</p>
        <h3 style={{ textAlign: "center", color: "#F1F5F9", fontSize: 16, fontWeight: 700, marginBottom: 8, paddingBottom: 4 }}>USER AGREEMENT, TERMS OF SERVICE & PRIVACY POLICY</h3>
        <p style={{ textAlign: "center", color: "#CBD5E1", fontSize: 11, marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid #ffffff08", fontWeight: 500 }}>Effective Date: 20 April 2026 &nbsp;•&nbsp; Governed by the Laws of Malaysia</p>

        <p style={{ color: "#EF4444", fontWeight: 700, marginBottom: 16, padding: "10px 14px", borderRadius: 8, background: "#EF444408", border: "1px solid #EF444420" }}>
          IMPORTANT — READ CAREFULLY BEFORE USING THIS SERVICE
        </p>

        <p style={{ color: "#F1F5F9", marginBottom: 16, fontWeight: 600 }}>BY CLICKING "I ACCEPT", CREATING AN ACCOUNT, ACTIVATING A LICENSE KEY, OR USING THE LASKY SL/TP SYNC SERVICE IN ANY MANNER, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE LEGALLY BOUND BY ALL TERMS AND CONDITIONS CONTAINED IN THIS AGREEMENT, INCLUDING THE PRIVACY POLICY SET OUT IN SECTION 8. IF YOU DO NOT AGREE WITH ANY PART OF THIS AGREEMENT, DO NOT USE THE SERVICE AND IMMEDIATELY DISCONTINUE ACCESS.</p>

        <p style={{ color: "#F1F5F9", marginBottom: 16, fontWeight: 600 }}>THIS AGREEMENT CONSTITUTES A LEGALLY BINDING CONTRACT BETWEEN YOU ("USER") AND SHADOW TECHNOLOGIES ("PROVIDER", "WE", "US", "OUR"), GOVERNED BY THE CONTRACTS ACT 1950, THE PERSONAL DATA PROTECTION ACT 2010 ("PDPA"), THE COMMUNICATIONS AND MULTIMEDIA ACT 1998 ("CMA"), THE COMPUTER CRIMES ACT 1997 ("CCA"), AND ALL OTHER APPLICABLE LAWS OF MALAYSIA. YOUR USE OF THE SERVICE IS ENTIRELY AT YOUR OWN RISK.</p>

        <p style={{ color: "#F1F5F9", marginBottom: 24, fontWeight: 600 }}>YOU MUST BE AT LEAST EIGHTEEN (18) YEARS OF AGE OR THE AGE OF LEGAL MAJORITY IN YOUR JURISDICTION, WHICHEVER IS HIGHER, TO USE THIS SERVICE. BY ACCEPTING THIS AGREEMENT, YOU CONFIRM THAT YOU MEET THIS REQUIREMENT. IF YOU ARE UNDER THE REQUIRED AGE, YOU ARE STRICTLY PROHIBITED FROM USING THIS SERVICE.</p>

        {/* ============ SECTION 1 ============ */}
        <h4 style={{ color: "#22D3EE", fontSize: 14, fontWeight: 700, marginBottom: 12 }}>1. DEFINITIONS & INTERPRETATION</h4>
        <p>In this Agreement, unless the context otherwise requires, the following terms shall have the meanings ascribed to them below:</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>"Service"</strong> means the LASKY SL/TP SYNC software-as-a-service platform, including all associated cloud infrastructure, application programming interfaces (APIs), web dashboards, synchronisation engines, and any updates, patches, or modifications thereto.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>"Provider"</strong> means Shadow Technologies, a business entity registered under the laws of Malaysia, operating the LASKY SL/TP SYNC platform.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>"User" or "You"</strong> means any individual, trader, or entity who creates an account, activates a license key, or accesses the Service in any capacity.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>"MT5 Account"</strong> means the User's MetaTrader 5 trading account with any third-party broker, including but not limited to the login credentials, master password, investor password, and all associated trading data.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>"SL/TP Sync"</strong> means the automated process of detecting Stop Loss and/or Take Profit modifications on one trading position and synchronising the same values across all other positions of the same trading symbol within the User's MT5 Account.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>"License Key"</strong> means the unique alphanumeric code issued by the Provider that grants the User access to the Service for the duration and terms specified by the applicable Subscription Plan.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>"Subscription Plan"</strong> means the selected tier of access (including but not limited to Starter, Basic, and Popular subscription tiers, and Lifetime access where applicable) that determines the duration and scope of the User's access to the Service.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>"MetaAPI"</strong> means the third-party cloud service provided by Agilium Labs Ltd that facilitates the connection between the Service and the User's MT5 Account. MetaAPI is an independent third-party provider and is not owned, operated, or controlled by the Provider.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>"Personal Data"</strong> has the meaning given to it in the Personal Data Protection Act 2010 of Malaysia, namely any information in respect of commercial transactions that relates directly or indirectly to a data subject who is identified or identifiable.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>"PDPA"</strong> means the Personal Data Protection Act 2010 of Malaysia and all subsidiary regulations made thereunder, as amended from time to time.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>"Processing"</strong> means any operation or set of operations performed on Personal Data, including collecting, recording, holding, storing, organising, adapting, altering, retrieving, using, disclosing, transferring, or destroying.</p>

        {/* ============ SECTION 2 ============ */}
        <h4 style={{ color: "#22D3EE", fontSize: 14, fontWeight: 700, marginTop: 24, marginBottom: 12 }}>2. NATURE OF SERVICE & SCOPE</h4>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>2.1 What the Service Does</strong></p>
        <p>The Service provides automated Stop Loss (SL) and/or Take Profit (TP) synchronisation across multiple trading positions of the same symbol within a single MT5 Account. When a User modifies the SL or TP value on any one position, the Service detects this change and applies the same SL/TP value to all other open positions of the same symbol.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>2.2 What the Service Does NOT Do</strong></p>
        <p>The Service expressly DOES NOT:</p>
        <p style={{ paddingLeft: 20 }}>(a) Execute, open, close, or modify any trading positions, orders, or pending orders beyond SL/TP synchronisation.<br/>(b) Provide trading signals, recommendations, market analysis, investment advice, or financial advice of any kind.<br/>(c) Act as a broker, dealer, money manager, investment advisor, fund manager, capital market services licence holder, or financial intermediary under the Capital Markets and Services Act 2007 or the Financial Services Act 2013.<br/>(d) Guarantee any trading profit, prevent any trading loss, or influence market outcomes.<br/>(e) Provide protection against market events including but not limited to margin calls, stop-outs, price spikes, gaps, slippage, or broker execution failures.<br/>(f) Manage, control, or influence the User's trading decisions, strategies, position sizing, or risk management.<br/>(g) Access, withdraw, deposit, or transfer any funds from the User's trading account, broker account, or any bank account.<br/>(h) Collect, hold, or handle any User funds whatsoever.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>2.3 Utility Tool Classification</strong></p>
        <p>The Service is classified strictly as a UTILITY TOOL — a technical convenience tool that automates a repetitive manual task (copying SL/TP values across positions). The Service does not and cannot affect market prices, broker execution, account equity, or trading outcomes. All trading outcomes are solely the result of the User's own trading decisions and prevailing market conditions. The Service is NOT a regulated financial product, financial service, capital market product, or investment scheme under any Malaysian law.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>2.4 No Financial Advice</strong></p>
        <p>NOTHING PROVIDED THROUGH THE SERVICE, ITS WEBSITE, MARKETING MATERIALS, OR ANY COMMUNICATIONS SHALL BE CONSTRUED AS FINANCIAL, INVESTMENT, LEGAL, OR TAX ADVICE. The User is solely responsible for evaluating the suitability of their own trading strategy and is strongly advised to consult licensed professionals where appropriate.</p>

        {/* ============ SECTION 3 ============ */}
        <h4 style={{ color: "#22D3EE", fontSize: 14, fontWeight: 700, marginTop: 24, marginBottom: 12 }}>3. ACCOUNT CREDENTIALS, ACCESS & AUTHORISATION</h4>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>3.1 Voluntary Provision of Credentials</strong></p>
        <p>To use the Service, the User is required to provide their MT5 Account login credentials, including the account number (login ID), master password, and broker server name. The User acknowledges and confirms that:</p>
        <p style={{ paddingLeft: 20 }}>(a) The provision of credentials is entirely VOLUNTARY and based on the User's own free will;<br/>(b) The User provides explicit, informed consent for the Provider to access the User's MT5 Account for the sole purpose of SL/TP synchronisation;<br/>(c) The User is solely responsible for verifying that their use of the Service complies with their broker's terms and conditions;<br/>(d) The User has the legal right and authority to grant such access; and<br/>(e) This consent constitutes lawful authorisation under the Computer Crimes Act 1997 of Malaysia, meaning the Provider's access to the User's MT5 Account is not unauthorised access.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>3.2 Why Master Password Is Required</strong></p>
        <p>The Service requires the User's master password (not investor password) because:</p>
        <p style={{ paddingLeft: 20 }}>(a) An investor password provides READ-ONLY access under MT5 protocol — no values can be modified with it;<br/>(b) The Service requires WRITE ACCESS specifically to modify SL/TP values on positions;<br/>(c) The master password is the ONLY credential enabling write access through the MetaAPI bridge;<br/>(d) The Service was designed for traders who operate via mobile devices and do not have continuous access to a personal computer or laptop;<br/>(e) The master password is used SOLELY for SL/TP modification operations authorised by this Agreement — the Service is technically incapable of withdrawing funds, transferring funds between accounts, or accessing the broker's cash management portal.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>3.3 Technical Limitation on Fund Access</strong></p>
        <p>The User acknowledges that the master password granted to the Service permits only MT5 platform operations. WITHDRAWAL OF FUNDS, TRANSFERS BETWEEN ACCOUNTS, OR MODIFICATION OF BANK ACCOUNT DETAILS ARE PERFORMED EXCLUSIVELY THROUGH THE BROKER'S CLIENT PORTAL using separate broker-level credentials which the Service NEVER collects, accesses, or stores. The Service is technically and architecturally incapable of such actions.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>3.4 Encryption & Security Measures</strong></p>
        <p>The Provider employs commercially reasonable security measures in accordance with Section 9 of the PDPA (Security Principle), including but not limited to:</p>
        <p style={{ paddingLeft: 20 }}>(a) AES-256 encryption at rest for all stored credentials (envelope encryption with per-user unique keys);<br/>(b) HTTPS/TLS encryption in transit for all data transmissions;<br/>(c) Row-Level Security (RLS) policies at the database layer;<br/>(d) Authentication tokens with expiration and revocation;<br/>(e) Rate limiting, brute-force protection, and request throttling;<br/>(f) Segregated service-role keys protected in secure environment variables;<br/>(g) Audit logging of all privileged operations.</p>
        <p>Notwithstanding the above, no system is 100% secure, and the Provider does not guarantee absolute security against all forms of cyberattack, breach, or unauthorised access. The User acknowledges this inherent risk.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>3.5 Limited Scope of Use</strong></p>
        <p>Credentials are used SOLELY for: (a) establishing the MetaAPI connection; (b) reading position data (symbol, volume, entry price, SL, TP); (c) modifying SL/TP values as triggered by User actions. The Provider DOES NOT use credentials to execute new trades, access funds, disclose to unauthorised third parties, or for any purpose not described in this Agreement.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>3.6 User Responsibility for Credential Security</strong></p>
        <p>The User is solely responsible for: (a) maintaining the confidentiality of their account login to the Service; (b) not sharing their Service login with third parties; (c) periodically changing their MT5 master password; (d) immediately revoking Service access and changing credentials if they suspect any compromise; (e) verifying that their broker permits the use of third-party trade management tools.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>3.7 Broker Terms Compliance</strong></p>
        <p>The Provider shall NOT be held responsible if the User's account is suspended, blocked, banned, or subjected to adverse action by their broker for any reason, including violation of broker terms of service, prohibition of third-party automation, hedging restrictions, or regulatory action. It is the User's sole responsibility to ensure their use of the Service complies with their broker's terms.</p>

        {/* ============ SECTION 4 ============ */}
        <h4 style={{ color: "#22D3EE", fontSize: 14, fontWeight: 700, marginTop: 24, marginBottom: 12 }}>4. TRADING RISK DISCLAIMER & LIMITATION OF LIABILITY</h4>
        <p style={{ color: "#CBD5E1" }}>TRADING IN FOREX, CONTRACTS FOR DIFFERENCE (CFDs), PRECIOUS METALS, INDICES, AND OTHER FINANCIAL INSTRUMENTS CARRIES A HIGH LEVEL OF RISK AND MAY NOT BE SUITABLE FOR ALL INVESTORS. THE POSSIBILITY EXISTS THAT YOU COULD SUSTAIN A LOSS OF SOME OR ALL OF YOUR INITIAL INVESTMENT. YOU SHOULD NOT TRADE WITH MONEY YOU CANNOT AFFORD TO LOSE.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>4.1 All Trading Decisions Are the User's</strong></p>
        <p>All trading decisions — including entry, exit, position size, instrument selection, and risk management — are made SOLELY by the User. The User assumes FULL RESPONSIBILITY for all trading outcomes including profits, losses, margin calls, and account liquidation. The Provider is NOT a financial advisor, and nothing related to the Service shall be construed as financial advice, investment recommendation, or solicitation.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>4.2 Exclusions of Liability</strong></p>
        <p>The Provider bears NO LIABILITY whatsoever for, and the User expressly waives any claim arising from:</p>
        <p style={{ paddingLeft: 20 }}>(a) Margin calls, stop-outs, or account liquidation;<br/>(b) Price spikes, market gaps, slippage, or requotes;<br/>(c) Delayed, failed, or partial SL/TP synchronisation due to MetaAPI outages, broker server issues, internet disruptions, or force majeure events;<br/>(d) Incorrect SL/TP values set by the User;<br/>(e) Loss of profits, loss of opportunity, loss of data, or any indirect, consequential, special, incidental, or punitive damages;<br/>(f) Emotional distress, mental anguish, reputational harm, or any non-pecuniary damages;<br/>(g) Actions or omissions of third parties including but not limited to brokers, MetaAPI, payment processors, cloud infrastructure providers, and telecommunications providers.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>4.3 Maximum Liability Cap</strong></p>
        <p style={{ color: "#CBD5E1" }}>TO THE MAXIMUM EXTENT PERMITTED BY MALAYSIAN LAW, THE PROVIDER'S TOTAL AGGREGATE LIABILITY ARISING OUT OF OR RELATING TO THIS AGREEMENT OR THE SERVICE, WHETHER IN CONTRACT, TORT, NEGLIGENCE, STRICT LIABILITY, OR OTHERWISE, SHALL NOT EXCEED THE TOTAL AMOUNT PAID BY THE USER TO THE PROVIDER IN THE TWELVE (12) MONTHS IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO THE CLAIM.</p>

        {/* ============ SECTION 5 ============ */}
        <h4 style={{ color: "#22D3EE", fontSize: 14, fontWeight: 700, marginTop: 24, marginBottom: 12 }}>5. THIRD-PARTY SERVICES & DEPENDENCIES</h4>
        <p>The Service operates through integration with and dependence upon the following independent third parties, over which the Provider has no operational or security control:</p>
        <p style={{ paddingLeft: 20 }}>(a) <strong style={{ color: "#22D3EE", fontWeight: 700 }}>MetaAPI (Agilium Labs Ltd)</strong> — provides the technical bridge between the Service and MT5 broker servers;<br/>(b) <strong style={{ color: "#22D3EE", fontWeight: 700 }}>Supabase</strong> — provides secure database hosting and authentication services;<br/>(c) <strong style={{ color: "#22D3EE", fontWeight: 700 }}>Railway</strong> — provides backend API hosting and runtime infrastructure;<br/>(d) <strong style={{ color: "#22D3EE", fontWeight: 700 }}>Vercel</strong> — provides frontend hosting and content delivery;<br/>(e) <strong style={{ color: "#22D3EE", fontWeight: 700 }}>Cloudflare</strong> — provides DNS and domain registration services;<br/>(f) <strong style={{ color: "#22D3EE", fontWeight: 700 }}>Resend.com</strong> — provides transactional email delivery;<br/>(g) <strong style={{ color: "#22D3EE", fontWeight: 700 }}>The User's chosen broker</strong> — provides the underlying trading account and order execution.</p>
        <p>The User acknowledges that service availability, security, and performance are dependent on these third parties. The Provider is NOT responsible for outages, breaches, data loss, or service degradation originating from third-party providers. Each third party maintains its own privacy policy and terms of service which the User may review independently.</p>

        {/* ============ SECTION 6 ============ */}
        <h4 style={{ color: "#22D3EE", fontSize: 14, fontWeight: 700, marginTop: 24, marginBottom: 12 }}>6. INTELLECTUAL PROPERTY & PROPRIETARY RIGHTS</h4>
        <p>All intellectual property rights in and to the Service, including but not limited to source code, algorithms, synchronisation engine, user interface, trade marks, service marks, logos, and documentation, are and shall remain the exclusive property of Shadow Technologies, protected under the Copyright Act 1987 and the Trade Marks Act 2019 of Malaysia.</p>
        <p>The User receives a limited, non-exclusive, non-transferable, non-sublicensable, revocable license to use the Service solely for the User's own trading activities during the validity of their active Subscription Plan. The User SHALL NOT: (a) copy, reproduce, republish, or redistribute the Service; (b) reverse engineer, decompile, disassemble, or attempt to derive source code; (c) create derivative works; (d) remove or alter proprietary notices; (e) use the Service to build a competing product; or (f) resell, sublicense, rent, or lease access. Violation constitutes a material breach, and the Provider may pursue all legal and equitable remedies including damages, injunctive relief, and criminal complaint under applicable laws.</p>

        {/* ============ SECTION 7 ============ */}
        <h4 style={{ color: "#22D3EE", fontSize: 14, fontWeight: 700, marginTop: 24, marginBottom: 12 }}>7. PAYMENT, REFUND & CANCELLATION POLICY</h4>
        <p style={{ color: "#EF4444", fontWeight: 600 }}>ALL SALES ARE FINAL. NO REFUNDS SHALL BE ISSUED UNDER ANY CIRCUMSTANCES.</p>
        <p>The no-refund policy applies without exception to: (a) change of mind; (b) trading losses of any magnitude; (c) temporary technical issues, outages, or degraded performance; (d) User's own hardware, software, or connectivity issues; (e) account termination due to violation of this Agreement; (f) broker-side issues unrelated to the Service.</p>
        <p>The no-refund policy is justified because the Service provides immediate access to proprietary cloud technology upon License Key activation. The User acknowledges that any refund demand, payment reversal, or chargeback filed with a payment processor after consuming the Service constitutes a breach of this Agreement, and the Provider reserves the right to: (i) immediately block the License Key and terminate access; (ii) pursue recovery of fees plus administrative costs; (iii) report the User to relevant fraud databases; (iv) pursue all legal remedies available under Malaysian law.</p>

        {/* ============ SECTION 8 ============ */}
        <h4 style={{ color: "#22D3EE", fontSize: 14, fontWeight: 700, marginTop: 24, marginBottom: 12 }}>8. PRIVACY POLICY & PERSONAL DATA PROTECTION</h4>
        <p style={{ color: "#F1F5F9", marginBottom: 12, padding: "10px 14px", borderRadius: 8, background: "#22D3EE08", border: "1px solid #22D3EE25", fontWeight: 600 }}>This Section 8 constitutes the Provider's Privacy Notice issued in compliance with Section 7 of the Personal Data Protection Act 2010 (Notice and Choice Principle) and forms an integral part of this Agreement.</p>

        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>8.1 Data Controller Identity</strong></p>
        <p>The data controller responsible for the Processing of your Personal Data is Shadow Technologies, a business entity registered in Malaysia. For any data protection enquiries, please contact: <strong style={{ color: "#22D3EE" }}>shadowtechsystem@gmail.com</strong>.</p>

        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>8.2 Categories of Personal Data Collected</strong></p>
        <p>The Provider collects and processes the following categories of Personal Data:</p>
        <p style={{ paddingLeft: 20 }}>(a) <strong style={{ color: "#22D3EE", fontWeight: 700 }}>Identity Data:</strong> full name, email address, phone number (if provided);<br/>(b) <strong style={{ color: "#22D3EE", fontWeight: 700 }}>Authentication Data:</strong> hashed account password, session tokens;<br/>(c) <strong style={{ color: "#22D3EE", fontWeight: 700 }}>Trading Credentials:</strong> MT5 login (account number), MT5 master password (encrypted with AES-256), broker name, broker server name;<br/>(d) <strong style={{ color: "#22D3EE", fontWeight: 700 }}>Trading Data:</strong> open positions, symbols, volumes, entry prices, SL/TP values, profit/loss, balance, equity, margin;<br/>(e) <strong style={{ color: "#22D3EE", fontWeight: 700 }}>Technical Data:</strong> IP address, device information, browser type, operating system, timestamps of access;<br/>(f) <strong style={{ color: "#22D3EE", fontWeight: 700 }}>Usage Data:</strong> actions performed within the dashboard, audit logs of sync events;<br/>(g) <strong style={{ color: "#22D3EE", fontWeight: 700 }}>Communication Data:</strong> support enquiries, feedback, correspondence with the Provider;<br/>(h) <strong style={{ color: "#22D3EE", fontWeight: 700 }}>Transaction Data:</strong> License Key, Subscription Plan, activation timestamp, payment platform used (Shopee/TikTok identifiers — the Provider does NOT collect or store credit card numbers or bank details).</p>

        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>8.3 Purposes of Processing</strong></p>
        <p>Your Personal Data is processed for the following lawful purposes:</p>
        <p style={{ paddingLeft: 20 }}>(a) To establish and authenticate your account and License Key;<br/>(b) To operate the SL/TP synchronisation engine (the core Service);<br/>(c) To display your trading status and dashboard information;<br/>(d) To maintain audit logs for security, fraud prevention, and compliance;<br/>(e) To respond to support requests and communicate service-related notices;<br/>(f) To comply with legal and regulatory obligations under Malaysian law;<br/>(g) To protect the rights, property, or safety of the Provider, the User, or others;<br/>(h) To detect, prevent, and investigate security incidents and fraudulent activity.</p>

        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>8.4 Legal Basis for Processing</strong></p>
        <p>The Provider relies on the following legal bases under the PDPA: (a) your explicit consent given upon acceptance of this Agreement; (b) performance of this contract (Service delivery); (c) compliance with legal obligations; (d) the Provider's legitimate interests in operating, securing, and improving the Service, balanced against your rights and freedoms.</p>

        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>8.5 Disclosure to Third Parties</strong></p>
        <p>Your Personal Data may be disclosed to the following categories of third parties, all of whom are bound by confidentiality obligations and process data solely to provide their respective services:</p>
        <p style={{ paddingLeft: 20 }}>(a) Cloud infrastructure providers (Supabase, Railway, Vercel, Cloudflare);<br/>(b) Trading bridge service (MetaAPI / Agilium Labs Ltd);<br/>(c) Email delivery service (Resend.com);<br/>(d) Payment platforms (Shopee, TikTok Shop, or others as applicable);<br/>(e) Professional advisors (legal, accounting, audit) under strict confidentiality;<br/>(f) Law enforcement, courts, or regulators where disclosure is compelled by law, court order, or lawful investigation;<br/>(g) Successors in the event of a merger, acquisition, or sale of business assets (subject to equivalent privacy protections).</p>
        <p>The Provider SHALL NOT sell, rent, lease, or trade your Personal Data for marketing purposes to any third party.</p>

        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>8.6 International Data Transfer</strong></p>
        <p>The Provider uses cloud service providers whose servers may be located outside Malaysia, including in the United States, European Union, Singapore, and other jurisdictions. By accepting this Agreement, you expressly CONSENT under Section 129 of the PDPA to the transfer of your Personal Data to such jurisdictions for the purposes described in this Agreement. The Provider takes reasonable steps to ensure that cross-border transfers are subject to comparable protection, including through provider-level security certifications (SOC 2, ISO 27001 where applicable).</p>

        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>8.7 Data Retention & Email Reservation Policy</strong></p>
        <p>The Provider applies an indefinite retention policy for account records, authorized under Section 39 of the PDPA (Exemptions — Prevention, Detection of Crime, Defence of Legal Claims, and Compliance with Legal Obligations):</p>
        <p style={{ paddingLeft: 20 }}>(a) <strong style={{ color: "#22D3EE", fontWeight: 700 }}>Active accounts:</strong> all Personal Data retained for the duration of the account's existence plus the periods specified below after deletion;<br/>(b) <strong style={{ color: "#22D3EE", fontWeight: 700 }}>Deleted accounts:</strong> Personal Data (including email address, encrypted MT5 credentials, account history, audit logs, trade events, sync records, and all associated metadata) is <strong style={{ color: "#FCA5A5" }}>retained indefinitely</strong> after account deletion for the following purposes authorized under PDPA 2010 Section 39: (i) defence of actual or anticipated legal claims (including the six-year limitation period under the Limitation Act 1953 for contract disputes, and the three-year period for tort claims); (ii) fraud prevention and detection; (iii) compliance with financial services regulatory obligations; (iv) prevention of account re-registration abuse; and (v) preservation of cryptographic audit integrity. Encrypted credentials remain cryptographically isolated per user (AES-256 envelope encryption) and access is revoked upon account deletion via connection termination and authentication ban;<br/>(c) <strong style={{ color: "#FCA5A5" }}>Email address permanent reservation:</strong> your email address is <strong style={{ color: "#FCA5A5" }}>permanently reserved</strong> upon signup and remains reserved indefinitely even after account deletion or email change. This email cannot be used to register a new account at LASKY SL/TP SYNC by you or any other person, with no exceptions and no administrative override. This policy ensures the integrity of our audit trail for legal defence purposes.</p>
        <p>By accepting this Agreement, you acknowledge, understand, and give your explicit informed consent to this retention and email reservation policy. You agree that such retention is necessary, proportionate, and lawful under Section 39 of the PDPA.</p>

        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>8.8 Your Data Subject Rights</strong></p>
        <p>Under the PDPA, you are entitled to the following rights in respect of your Personal Data:</p>
        <p style={{ paddingLeft: 20 }}>(a) <strong style={{ color: "#22D3EE", fontWeight: 700 }}>Right of Access (Section 30):</strong> request confirmation of whether the Provider processes your data and a copy of such data;<br/>(b) <strong style={{ color: "#22D3EE", fontWeight: 700 }}>Right to Correction (Section 34):</strong> request correction of inaccurate or incomplete data;<br/>(c) <strong style={{ color: "#22D3EE", fontWeight: 700 }}>Right to Withdraw Consent (Section 38):</strong> withdraw your consent at any time, noting that such withdrawal will terminate the Service;<br/>(d) <strong style={{ color: "#22D3EE", fontWeight: 700 }}>Right to Prevent Processing (Section 42):</strong> request the Provider to cease processing where it causes substantial damage or distress;<br/>(e) <strong style={{ color: "#22D3EE", fontWeight: 700 }}>Right to Prevent Processing for Direct Marketing (Section 43):</strong> opt out of direct marketing communications;<br/>(f) <strong style={{ color: "#22D3EE", fontWeight: 700 }}>Right to Data Portability:</strong> request a copy of your data in a commonly used machine-readable format;<br/>(g) <strong style={{ color: "#22D3EE", fontWeight: 700 }}>Right to Lodge Complaint:</strong> file a complaint with the Personal Data Protection Commissioner of Malaysia if you believe your rights under the PDPA have been infringed.</p>
        <p>To exercise these rights, contact <strong style={{ color: "#22D3EE" }}>shadowtechsystem@gmail.com</strong>. The Provider will respond within twenty-one (21) days as required by the PDPA. The Provider may charge a nominal fee for access requests in accordance with the Personal Data Protection Regulations. Note that the email reservation policy (Section 8.7(d)) is not subject to consent withdrawal as it operates under the statutory exemption in Section 39 of the PDPA.</p>

        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>8.9 Data Security & Breach Notification</strong></p>
        <p>The Provider has implemented appropriate technical and organisational measures to protect Personal Data as detailed in Section 3.4. In the event of a Personal Data breach that is likely to result in significant harm to affected data subjects, the Provider shall:</p>
        <p style={{ paddingLeft: 20 }}>(a) Investigate and contain the breach without undue delay;<br/>(b) Notify affected Users via email within seventy-two (72) hours of discovery, describing the nature of the breach, categories of data involved, likely consequences, and mitigation measures taken;<br/>(c) Notify the Personal Data Protection Commissioner where required by law;<br/>(d) Document the breach, its effects, and remedial actions in an internal incident register;<br/>(e) Cooperate fully with any regulatory investigation.</p>

        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>8.10 Children's Data</strong></p>
        <p>The Service is not directed at and shall not be used by individuals under eighteen (18) years of age. The Provider does not knowingly collect Personal Data from minors. If it comes to the Provider's attention that a minor has provided Personal Data, such data shall be promptly deleted.</p>

        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>8.11 Cookies and Similar Technologies</strong></p>
        <p>The Service uses essential browser storage (such as localStorage) for authentication sessions, "remember me" functionality, and session continuity. These are strictly necessary for the Service to function. The Provider does not use third-party advertising cookies or tracking pixels.</p>

        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>8.12 PDPA Data User Registration</strong></p>
        <p>Where required under the Personal Data Protection (Class of Data Users) Order, the Provider is registered or will be registered as a Data User with the Personal Data Protection Commissioner of Malaysia.</p>

        {/* ============ SECTION 9 ============ */}
        <h4 style={{ color: "#22D3EE", fontSize: 14, fontWeight: 700, marginTop: 24, marginBottom: 12 }}>9. SERVICE AVAILABILITY & WARRANTY DISCLAIMER</h4>
        <p style={{ color: "#CBD5E1" }}>THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE. TO THE MAXIMUM EXTENT PERMITTED BY MALAYSIAN LAW, THE PROVIDER DISCLAIMS ALL IMPLIED WARRANTIES INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.</p>
        <p>The Provider does not warrant that: (a) the Service will be uninterrupted, error-free, or continuously available; (b) the Service will meet the User's specific requirements; (c) defects will be corrected within any particular timeframe; (d) the Service is free from viruses or harmful components. The Provider reserves the right to perform scheduled maintenance, modify features, add or remove functionality, or discontinue the Service entirely upon reasonable notice.</p>

        {/* ============ SECTION 10 ============ */}
        <h4 style={{ color: "#22D3EE", fontSize: 14, fontWeight: 700, marginTop: 24, marginBottom: 12 }}>10. INDEMNIFICATION</h4>
        <p>The User agrees to indemnify, defend, and hold harmless the Provider, its directors, employees, agents, contractors, and affiliates from and against any and all claims, liabilities, damages, losses, costs, and expenses (including reasonable legal fees) arising out of or relating to: (a) the User's use or misuse of the Service; (b) the User's trading activities and their outcomes; (c) the User's violation of this Agreement or applicable law; (d) the User's violation of their broker's terms of service; (e) fraudulent refund claims or chargebacks; (f) any claim that the User's content, conduct, or credentials submitted caused damage to a third party. This indemnification obligation survives termination of this Agreement.</p>

        {/* ============ SECTION 11 ============ */}
        <h4 style={{ color: "#22D3EE", fontSize: 14, fontWeight: 700, marginTop: 24, marginBottom: 12 }}>11. TERMINATION</h4>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>11.1 Termination by User:</strong> The User may terminate at any time by disconnecting their MT5 Account from the Service and/or requesting account deletion. No refund is provided for unused Subscription Plan periods.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>11.2 Termination by Provider:</strong> The Provider may suspend or terminate the User's access, with or without notice, for: (a) material breach of this Agreement; (b) fraudulent activity or chargebacks; (c) reverse engineering or circumvention attempts; (d) conduct that harms the Service, other Users, or third parties; (e) legal or regulatory requirement; (f) prolonged inactivity.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>11.3 Effect of Termination:</strong> Upon termination, the User's right to access the Service ceases immediately. Sections 4 (Liability), 6 (IP), 7 (No Refund), 8 (Privacy — surviving obligations), 10 (Indemnification), 12 (Governing Law), and 13 (General Provisions) shall survive termination.</p>

        {/* ============ SECTION 12 ============ */}
        <h4 style={{ color: "#22D3EE", fontSize: 14, fontWeight: 700, marginTop: 24, marginBottom: 12 }}>12. GOVERNING LAW & DISPUTE RESOLUTION</h4>
        <p>This Agreement shall be governed by and construed in accordance with the laws of Malaysia, without regard to its conflict of laws principles. The User and the Provider irrevocably submit to the exclusive jurisdiction of the courts of Malaysia for any disputes arising out of or in connection with this Agreement. International Users expressly consent to Malaysian jurisdiction and waive any objection based on forum non conveniens.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>12.1 Negotiation:</strong> Before initiating any legal proceedings, the parties shall attempt in good faith to resolve the dispute through direct negotiation for a period of not less than thirty (30) days.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>12.2 Mediation:</strong> If negotiation fails, the parties shall attempt mediation administered by the Asian International Arbitration Centre (AIAC) in Kuala Lumpur before resorting to litigation.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>12.3 Class Action Waiver:</strong> TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE USER WAIVES ANY RIGHT TO PARTICIPATE IN A CLASS ACTION, REPRESENTATIVE ACTION, OR CONSOLIDATED PROCEEDING AGAINST THE PROVIDER.</p>

        {/* ============ SECTION 13 ============ */}
        <h4 style={{ color: "#22D3EE", fontSize: 14, fontWeight: 700, marginTop: 24, marginBottom: 12 }}>13. GENERAL PROVISIONS</h4>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>13.1 Entire Agreement:</strong> This Agreement, together with any policies and documents referenced herein, constitutes the entire agreement between the parties and supersedes all prior or contemporaneous agreements.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>13.2 Amendments:</strong> The Provider may modify this Agreement at any time. Material changes will be notified via email or in-dashboard notice with at least fourteen (14) days' advance notice. Continued use after the effective date constitutes acceptance. If the User does not agree, they must discontinue use.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>13.3 Severability:</strong> If any provision of this Agreement is held invalid or unenforceable, the remaining provisions shall continue in full force and effect.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>13.4 Waiver:</strong> No waiver by the Provider of any breach shall be deemed a waiver of any subsequent breach.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>13.5 Force Majeure:</strong> The Provider is not liable for delays or failures caused by events beyond reasonable control, including acts of God, war, terrorism, cyberattacks, internet outages, governmental action, pandemics, or third-party service failures.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>13.6 Assignment:</strong> The User may not assign any rights or obligations under this Agreement. The Provider may assign or transfer this Agreement without restriction.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>13.7 Notices & Contact:</strong> All legal notices, data protection enquiries, and formal correspondence to the Provider shall be sent to <strong style={{ color: "#22D3EE" }}>shadowtechsystem@gmail.com</strong> (Company / Legal / Data Protection). For technical support, service enquiries, and assistance with the LASKY SL/TP SYNC platform, contact <strong style={{ color: "#22D3EE" }}>laskysystem@gmail.com</strong>. Notices to the User shall be sent to the email address on file.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>13.8 Language:</strong> This Agreement is executed in the English language. Any translation is provided for convenience only; the English version shall prevail in case of conflict.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>13.9 No Agency:</strong> Nothing in this Agreement creates any agency, partnership, joint venture, employment, or franchise relationship between the parties.</p>
        <p><strong style={{ color: "#22D3EE", fontWeight: 700 }}>13.10 Headings:</strong> Section headings are for convenience only and do not affect interpretation.</p>

        {/* Acknowledgment */}
        <div style={{ marginTop: 28, padding: "16px 20px", borderRadius: 10, background: "#22D3EE08", border: "1px solid #22D3EE20" }}>
          <h4 style={{ color: "#22D3EE", fontSize: 14, fontWeight: 700, marginBottom: 12 }}>USER ACKNOWLEDGMENT</h4>
          <p style={{ fontSize: 12, lineHeight: 1.8 }}>
            1. I have read and understood this entire Agreement, including the Privacy Policy in Section 8, in full.<br/>
            2. I agree to be bound by all terms and conditions contained herein under the laws of Malaysia.<br/>
            3. I understand the Service is a utility tool for SL/TP synchronisation only and is not a financial service, investment advice, or regulated financial product.<br/>
            4. I accept full responsibility for all my trading decisions and outcomes.<br/>
            5. I understand and accept the strict no-refund policy.<br/>
            6. I voluntarily provide my MT5 credentials with explicit informed consent, understanding why master password is required and that the Service cannot access my broker's fund withdrawal portal.<br/>
            7. I consent under the PDPA to the collection, processing, disclosure to named third parties, and international transfer of my Personal Data as described in Section 8.<br/>
            8. I understand my rights as a data subject under the PDPA and know how to exercise them.<br/>
            9. I will not hold the Provider liable for trading losses, market events, or broker-related issues.<br/>
            10. The Provider does not guarantee profits or prevent losses, and makes no representations about trading outcomes.<br/>
            11. The Service may experience interruptions, and I accept this risk.<br/>
            12. I am at least 18 years of age or the age of legal majority in my jurisdiction, whichever is higher.<br/>
            13. I am solely responsible for ensuring my use of the Service complies with my broker's terms of service.<br/>
            14. I waive any right to class action or representative proceedings against the Provider.
          </p>
        </div>

        <p style={{ textAlign: "center", color: "#CBD5E1", fontSize: 11, marginTop: 24, fontWeight: 500 }}>© 2026 Shadow Technologies. All Rights Reserved.</p>
        <p style={{ textAlign: "center", color: "#CBD5E1", fontSize: 11, fontWeight: 500 }}>LASKY SL/TP SYNC — Cloud SL/TP Synchronisation Service</p>
        <p style={{ textAlign: "center", color: "#CBD5E1", fontSize: 11, fontWeight: 500 }}>Company / Legal / Data Protection: shadowtechsystem@gmail.com</p>
        <p style={{ textAlign: "center", color: "#CBD5E1", fontSize: 11, fontWeight: 500 }}>LASKY SL/TP SYNC Support: laskysystem@gmail.com</p>
        <p style={{ textAlign: "center", color: "#CBD5E1", fontSize: 11, marginBottom: 0, fontWeight: 500 }}>Governed by the laws of Malaysia &nbsp;•&nbsp; PDPA 2010 Compliant</p>
      </div>

      {/* Tick Boxes + Buttons */}
      <div style={{ width: "100%", maxWidth: 700, padding: "24px 28px", borderRadius: 12, background: "#0A0A12", border: "1px solid #ffffff08" }}>

        {!scrolledToBottom && (
          <p style={{ color: "#FCD34D", fontSize: 12, textAlign: "center", marginBottom: 12, fontWeight: 600 }}>↓ Scroll to the bottom of the agreement to enable acceptance.</p>
        )}

        <Tick
          checked={tick1} onChange={setTick1} disabled={!scrolledToBottom}
          label="I confirm that I am at least 18 years of age or the age of legal majority in my jurisdiction, whichever is higher, and that I have the legal capacity to enter into this Agreement."
        />
        <Tick
          checked={tick2} onChange={setTick2} disabled={!scrolledToBottom}
          label="I have read, understood, and agree to be bound by the Terms of Service, Trading Risk Disclaimer, No-Refund Policy, and all other clauses of this Agreement under the laws of Malaysia."
        />
        <Tick
          checked={tick3} onChange={setTick3} disabled={!scrolledToBottom}
          label="I explicitly consent under the Personal Data Protection Act 2010 (PDPA) to the collection, processing, disclosure to named third parties, and cross-border transfer of my Personal Data (including encrypted MT5 credentials) as described in Section 8 — Privacy Policy."
        />

        <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
          <button onClick={onDecline} style={{ flex: 1, padding: "14px", borderRadius: 10, background: "#ffffff04", border: "1px solid #ffffff0a", color: "#94A3B8", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
            Decline
          </button>
          <button
            onClick={handleAccept} disabled={!allTicked || accepting}
            style={{
              flex: 2, padding: "14px", borderRadius: 10,
              background: allTicked ? "linear-gradient(135deg, #0891B2, #22D3EE)" : "#1E293B",
              border: "none", color: allTicked ? "#fff" : "#475569",
              fontSize: 14, fontWeight: 700, cursor: allTicked ? "pointer" : "not-allowed",
              fontFamily: "inherit", opacity: allTicked ? 1 : 0.5, transition: "all 0.3s",
            }}
          >
            {accepting ? "Creating Account..." : "Accept & Create Account →"}
          </button>
        </div>
      </div>

      <style>{`@keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}

// NOTE: RenewPage component removed on 20 Apr 2026 (C1 security fix).
// Previous component called onRenew() → handleRenew() → /api/license/renew (buggy).
// Will be rebuilt with proper payment gateway integration post-bank-account.
// For now, users renew by purchasing new license keys via Shopee/TikTok.

// ============================================================
// LOGIN 2FA CHALLENGE — shown after password login if 2FA enabled
// ============================================================
function Login2FAPage({ userName, onVerify, onVerifyBackup, onCancel }) {
  const [mode, setMode] = useState("totp"); // 'totp' | 'backup'
  const [code, setCode] = useState("");
  const [backupCode, setBackupCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submitTOTP = async () => {
    if (code.length !== 6) { setError("Enter the 6-digit code from your authenticator app."); return; }
    setBusy(true); setError("");
    try { await onVerify(code); }
    catch (e) {
      // Soft/hard lock errors bubble up as "SOFT_LOCKED"/"HARD_LOCKED" — UI handled by parent
      if (e.message !== "SOFT_LOCKED" && e.message !== "HARD_LOCKED") {
        setError(e.message || "Invalid code.");
      }
      setCode("");
    }
    setBusy(false);
  };

  const submitBackup = async () => {
    const normalized = backupCode.trim().toUpperCase();
    if (!/^[A-F0-9]{4}-[A-F0-9]{4}$/.test(normalized)) {
      setError("Enter a valid backup code in the format XXXX-XXXX.");
      return;
    }
    setBusy(true); setError("");
    try {
      const result = await onVerifyBackup(normalized);
      if (result && typeof result.remaining === "number" && result.remaining <= 3) {
        // Warning shown on next login or profile (non-blocking here)
      }
    }
    catch (e) {
      if (e.message !== "SOFT_LOCKED" && e.message !== "HARD_LOCKED") {
        setError(e.message || "Invalid backup code.");
      }
      setBackupCode("");
    }
    setBusy(false);
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setError("");
    setCode("");
    setBackupCode("");
  };

  return (
    <div style={{ minHeight: "100vh", background: "#06060B", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Outfit', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;700&display=swap" rel="stylesheet" />
      <div style={{ position: "fixed", top: "30%", left: "50%", transform: "translate(-50%,-50%)", width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle, #22D3EE06 0%, transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "relative", width: 420, padding: 36, background: "#0A0A12", border: "1px solid #ffffff08", borderRadius: 16, animation: "fadeUp 0.5s ease" }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>{mode === "totp" ? "🔐" : "🎫"}</div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: "#F1F5F9", margin: "0 0 6px 0" }}>{mode === "totp" ? "Two-Factor Authentication" : "Use Backup Code"}</h2>
          <p style={{ color: "#CBD5E1", fontSize: 13, margin: 0, lineHeight: 1.5 }}>
            {mode === "totp"
              ? <>Hi {userName}, enter the 6-digit code from your authenticator app.</>
              : <>Hi {userName}, enter one of your saved backup codes (format: <span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#22D3EE" }}>XXXX-XXXX</span>). Each code is one-time use only.</>
            }
          </p>
        </div>

        {mode === "totp" ? (
          <>
            <div style={{ marginBottom: 16 }}>
              <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" autoFocus
                onKeyDown={e => e.key === "Enter" && submitTOTP()}
                style={{ width: "100%", padding: "18px 20px", background: "#ffffff04", border: "1px solid #ffffff0a", borderRadius: 12, color: "#22D3EE", fontSize: 28, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, letterSpacing: "0.4em", outline: "none", boxSizing: "border-box", textAlign: "center" }} />
            </div>
            {error && (<div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 14, background: "#EF444410", border: "1px solid #EF444420", color: "#FCA5A5", fontSize: 13 }}>{error}</div>)}
            <button onClick={submitTOTP} disabled={busy || code.length !== 6}
              style={{ width: "100%", padding: 14, background: (busy || code.length !== 6) ? "#1E293B" : "linear-gradient(135deg, #0891B2, #22D3EE)", border: "none", borderRadius: 10, color: "#fff", fontSize: 15, fontWeight: 700, cursor: (busy || code.length !== 6) ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
              {busy ? "Verifying..." : "Verify →"}
            </button>
            <button onClick={() => switchMode("backup")} style={{ width: "100%", marginTop: 14, padding: 10, background: "none", border: "1px solid #ffffff10", borderRadius: 8, color: "#22D3EE", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              Lost access? Use backup code →
            </button>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 16 }}>
              <input value={backupCode} onChange={e => {
                // Auto-format: uppercase + auto-insert dash after 4 chars
                let v = e.target.value.replace(/[^A-F0-9]/gi, "").toUpperCase().slice(0, 8);
                if (v.length > 4) v = v.slice(0, 4) + "-" + v.slice(4);
                setBackupCode(v);
              }} placeholder="XXXX-XXXX" autoFocus
                onKeyDown={e => e.key === "Enter" && submitBackup()}
                style={{ width: "100%", padding: "18px 20px", background: "#ffffff04", border: "1px solid #ffffff0a", borderRadius: 12, color: "#22D3EE", fontSize: 24, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, letterSpacing: "0.15em", outline: "none", boxSizing: "border-box", textAlign: "center" }} />
            </div>
            {error && (<div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 14, background: "#EF444410", border: "1px solid #EF444420", color: "#FCA5A5", fontSize: 13 }}>{error}</div>)}
            <button onClick={submitBackup} disabled={busy || backupCode.length !== 9}
              style={{ width: "100%", padding: 14, background: (busy || backupCode.length !== 9) ? "#1E293B" : "linear-gradient(135deg, #0891B2, #22D3EE)", border: "none", borderRadius: 10, color: "#fff", fontSize: 15, fontWeight: 700, cursor: (busy || backupCode.length !== 9) ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
              {busy ? "Verifying..." : "Verify Backup Code →"}
            </button>
            <button onClick={() => switchMode("totp")} style={{ width: "100%", marginTop: 14, padding: 10, background: "none", border: "1px solid #ffffff10", borderRadius: 8, color: "#22D3EE", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              ← Back to authenticator code
            </button>
          </>
        )}

        <button onClick={onCancel} style={{ width: "100%", marginTop: 12, padding: 10, background: "none", border: "none", color: "#94A3B8", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>← Cancel & return to login</button>
        <p style={{ color: "#475569", fontSize: 11, textAlign: "center", marginTop: 20, fontWeight: 400, lineHeight: 1.6 }}>Lost your device and backup codes? Contact support at laskysystem@gmail.com</p>
      </div>
    </div>
  );
}

// ============================================================
// S2 PHASE 1 — LOCKOUT POPUPS (23 Apr 2026)
// ============================================================

// SoftLockPopup — 15 min temporary lock with live countdown
function SoftLockPopup({ remainingSeconds, onDismiss, onResetPassword }) {
  const [secondsLeft, setSecondsLeft] = useState(remainingSeconds);

  useEffect(() => {
    if (secondsLeft <= 0) { onDismiss(); return; }
    const id = setInterval(() => {
      setSecondsLeft(s => {
        if (s <= 1) { clearInterval(id); onDismiss(); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [secondsLeft, onDismiss]);

  const mm = Math.floor(secondsLeft / 60);
  const ss = secondsLeft % 60;
  const timeStr = `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, fontFamily: "'Outfit', sans-serif", animation: "fadeIn 0.25s ease" }}>
      <div style={{ width: 440, maxWidth: "calc(100% - 32px)", padding: 32, background: "#0A0A12", border: "1px solid #F59E0B40", borderRadius: 16, animation: "fadeUp 0.35s ease" }}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>🔒</div>
          <h3 style={{ color: "#F59E0B", fontSize: 20, fontWeight: 700, margin: "0 0 8px 0" }}>Account Temporarily Locked</h3>
          <p style={{ color: "#CBD5E1", fontSize: 14, lineHeight: 1.6, margin: 0 }}>
            Too many failed login attempts. Your account is locked for 15 minutes as a security precaution.
          </p>
        </div>

        <div style={{ textAlign: "center", padding: "20px 16px", background: "#F59E0B10", border: "1px solid #F59E0B25", borderRadius: 12, marginBottom: 20 }}>
          <div style={{ color: "#F1F5F9", fontSize: 12, fontWeight: 600, marginBottom: 8, letterSpacing: "0.05em", textTransform: "uppercase" }}>Time Remaining</div>
          <div style={{ color: "#F59E0B", fontSize: 42, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, letterSpacing: "0.1em" }}>{timeStr}</div>
        </div>

        <p style={{ color: "#E2E8F0", fontSize: 13, lineHeight: 1.6, margin: "0 0 16px 0" }}>
          If this wasn't you, someone may be trying to access your account. Reset your password immediately.
        </p>

        <div style={{ padding: "12px 14px", background: "#0EA5E910", border: "1px solid #0EA5E930", borderRadius: 8, marginBottom: 20 }}>
          <p style={{ color: "#7DD3FC", fontSize: 12, lineHeight: 1.6, margin: 0 }}>
            📬 <strong style={{ color: "#BAE6FD" }}>We've sent a security alert to your email.</strong> If you don't see it in your inbox, please check <strong>Spam</strong>, <strong>Promotions</strong>, or <strong>Trash</strong> folders.
          </p>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onResetPassword} style={{ flex: 1, padding: 12, background: "linear-gradient(135deg, #0891B2, #22D3EE)", border: "none", borderRadius: 10, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            Reset Password
          </button>
          <button onClick={onDismiss} style={{ padding: "12px 18px", background: "#ffffff04", border: "1px solid #ffffff10", borderRadius: 10, color: "#E2E8F0", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
            OK, I'll wait
          </button>
        </div>
      </div>
    </div>
  );
}

// HardLockPopup — permanent lock until password reset (for failed login/2FA)
// For license_abuse reason: shows Contact Support flow instead
function HardLockPopup({ reason, onResetPassword, session, email }) {
  const [view, setView] = useState("locked"); // "locked" | "form" | "thankyou"
  const [licenseKey, setLicenseKey] = useState("");
  const [description, setDescription] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const isLicenseAbuse = reason === "license_abuse";
  // V6.3 (1 May 2026): fallback chain — session.user.email (post-login) → email prop
  // (login rejected, no session) → empty. Ensures ticket form autofill works even
  // when login was rejected at /api/auth/login (session=null).
  const userEmail = session?.user?.email || email || "";

  const handleSend = async () => {
    if (!licenseKey.trim() || !description.trim() || description.trim().length < 10) {
      setSendError("Please fill in all fields (description min 10 chars)."); return;
    }
    setSending(true); setSendError("");
    try {
      // V6.3 (1 May 2026): Use public no-auth endpoint when session is null
      // (login was rejected — user has no access_token). Backend verifies email
      // is in license_abuse lock state before processing (anti-abuse).
      const hasSession = !!session?.access_token;
      const url = hasSession ? `${API_URL}/api/license/abuse-ticket` : `${API_URL}/api/license/abuse-ticket-public`;
      const headers = { "Content-Type": "application/json" };
      if (hasSession) headers.Authorization = `Bearer ${session.access_token}`;
      const body = hasSession
        ? { licenseKeyTried: licenseKey.trim(), description: description.trim() }
        : { email: userEmail, licenseKeyTried: licenseKey.trim(), description: description.trim() };

      const r = await fetch(url, { method: "POST", headers, body: JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) { setSendError(d.error || "Failed to send."); setSending(false); return; }
      setView("thankyou");
    } catch { setSendError("Network error. Please try again."); setSending(false); }
  };

  const inputStyle = { width: "100%", padding: "11px 14px", background: "#ffffff04", border: "1px solid #ffffff0a", borderRadius: 10, color: "#E2E8F0", fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, fontFamily: "'Outfit', sans-serif", animation: "fadeIn 0.25s ease" }}>
      <div style={{ width: 460, maxWidth: "calc(100% - 32px)", padding: 32, background: "#0A0A12", border: "1px solid #EF444440", borderRadius: 16, animation: "fadeUp 0.35s ease" }}>

        {/* LICENSE ABUSE FLOW */}
        {isLicenseAbuse && view === "locked" && (<>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>⛔</div>
            <h3 style={{ color: "#EF4444", fontSize: 20, fontWeight: 700, margin: "0 0 8px 0" }}>Account Locked — Action Required</h3>
            <p style={{ color: "#94A3B8", fontSize: 13, lineHeight: 1.6, margin: 0 }}>license_abuse</p>
          </div>
          <div style={{ padding: "14px 16px", background: "#EF444410", border: "1px solid #EF444425", borderRadius: 10, marginBottom: 20 }}>
            <p style={{ color: "#FCA5A5", fontSize: 13, lineHeight: 1.6, margin: 0 }}>Your account has been locked due to too many failed license activation attempts. Contact support to unlock.</p>
          </div>
          <button onClick={() => setView("form")} style={{ width: "100%", padding: 14, background: "linear-gradient(135deg, #0891B2, #22D3EE)", border: "none", borderRadius: 10, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            ✉ Contact Support
          </button>
        </>)}

        {isLicenseAbuse && view === "form" && (<>
          <div style={{ textAlign: "center", marginBottom: 18 }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>📋</div>
            <h3 style={{ color: "#F1F5F9", fontSize: 18, fontWeight: 700, margin: "0 0 6px 0" }}>Submit Support Ticket</h3>
            <p style={{ color: "#94A3B8", fontSize: 12, margin: 0 }}>We'll contact you by email within 24 hours.</p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <label style={{ color: "#CBD5E1", fontSize: 12, fontWeight: 600, display: "block", marginBottom: 5 }}>Your Email (auto-filled)</label>
              <input type="text" value={userEmail} readOnly style={{ ...inputStyle, color: "#94A3B8", cursor: "not-allowed" }} />
            </div>
            <div>
              <label style={{ color: "#CBD5E1", fontSize: 12, fontWeight: 600, display: "block", marginBottom: 5 }}>License Key You Tried</label>
              <input type="text" value={licenseKey} onChange={e => setLicenseKey(e.target.value.toUpperCase())} placeholder="AD-XXXX-XXXX-XXXX-XXXX" style={{ ...inputStyle, fontFamily: "monospace", letterSpacing: 2 }} />
            </div>
            <div>
              <label style={{ color: "#CBD5E1", fontSize: 12, fontWeight: 600, display: "block", marginBottom: 5 }}>Description</label>
              <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Explain your situation..." rows={3} style={{ ...inputStyle, resize: "vertical" }} />
            </div>
            {sendError && <div style={{ padding: "8px 12px", borderRadius: 8, background: "#EF444410", color: "#FCA5A5", fontSize: 12 }}>{sendError}</div>}
            <button onClick={handleSend} disabled={sending} style={{ padding: 13, borderRadius: 10, background: sending ? "#1E293B" : "linear-gradient(135deg, #0891B2, #22D3EE)", color: sending ? "#475569" : "#fff", fontSize: 14, fontWeight: 700, border: "none", cursor: sending ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
              {sending ? "Sending..." : "Send Ticket →"}
            </button>
          </div>
        </>)}

        {isLicenseAbuse && view === "thankyou" && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 14 }}>✅</div>
            <h3 style={{ color: "#F1F5F9", fontSize: 18, fontWeight: 700, marginBottom: 10 }}>Ticket Submitted</h3>
            <p style={{ color: "#CBD5E1", fontSize: 13, lineHeight: 1.7, marginBottom: 24 }}>Thank you. We will contact you by email. Please check your email for any update from us.</p>
            <button onClick={onResetPassword} style={{ width: "100%", padding: 13, borderRadius: 10, background: "#1E293B", border: "1px solid #334155", color: "#E2E8F0", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>OKAY</button>
          </div>
        )}

        {/* NON-LICENSE-ABUSE FLOW (failed login/2FA) */}
        {!isLicenseAbuse && (<>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>⛔</div>
            <h3 style={{ color: "#EF4444", fontSize: 20, fontWeight: 700, margin: "0 0 8px 0" }}>Account Locked — Action Required</h3>
            <p style={{ color: "#CBD5E1", fontSize: 14, lineHeight: 1.6, margin: 0 }}>
              {reason || "Multiple failed login attempts detected. Your account has been locked."}
            </p>
          </div>
          <div style={{ padding: "14px 16px", background: "#EF444410", border: "1px solid #EF444425", borderRadius: 10, marginBottom: 20 }}>
            <p style={{ color: "#FCA5A5", fontSize: 13, lineHeight: 1.6, margin: 0 }}>
              <strong style={{ color: "#F87171" }}>To regain access</strong>, reset your password via email. Your 2FA settings will remain active.
            </p>
          </div>
          <div style={{ padding: "12px 14px", background: "#0EA5E910", border: "1px solid #0EA5E930", borderRadius: 8, marginBottom: 20 }}>
            <p style={{ color: "#7DD3FC", fontSize: 12, lineHeight: 1.6, margin: 0 }}>📬 <strong style={{ color: "#BAE6FD" }}>Can't find the email?</strong> Check Spam, Promotions, or Trash folders.</p>
          </div>
          <button onClick={onResetPassword} style={{ width: "100%", padding: 14, background: "linear-gradient(135deg, #DC2626, #EF4444)", border: "none", borderRadius: 10, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            Reset Password via Email
          </button>
          <p style={{ color: "#94A3B8", fontSize: 11, textAlign: "center", marginTop: 16 }}>Need urgent help? Contact <span style={{ color: "#22D3EE" }}>laskysystem@gmail.com</span></p>
        </>)}

      </div>
    </div>
  );
}

// BackupCodesModal — displays 10 one-time backup codes ONCE after 2FA setup.
// User MUST check the confirmation box before codes can be submitted for storage.
function BackupCodesModal({ codes, onConfirm, onCancel, busy }) {
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyAll = () => {
    const text = codes.join("\n");
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  const downloadTxt = () => {
    const content = `LASKY SL/TP SYNC — 2FA Backup Codes\n${"=".repeat(40)}\n\nGenerated: ${new Date().toUTCString()}\n\nEach code can only be used ONCE.\nStore this file in a safe, private location.\nIf you lose both your authenticator app AND these codes,\nyou will be locked out of your account.\n\n${"=".repeat(40)}\n\n${codes.map((c, i) => `${String(i + 1).padStart(2, "0")}.  ${c}`).join("\n")}\n\n${"=".repeat(40)}\n`;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `laskysync-backup-codes-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000, fontFamily: "'Outfit', sans-serif", animation: "fadeIn 0.25s ease", padding: 16 }}>
      <div style={{ width: 520, maxWidth: "100%", maxHeight: "90vh", overflowY: "auto", padding: 32, background: "#0A0A12", border: "1px solid #22D3EE30", borderRadius: 16, animation: "fadeUp 0.35s ease" }}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>🎫</div>
          <h3 style={{ color: "#22D3EE", fontSize: 20, fontWeight: 700, margin: "0 0 8px 0" }}>Save Your Backup Codes</h3>
          <p style={{ color: "#E2E8F0", fontSize: 14, lineHeight: 1.6, margin: 0 }}>
            These 10 codes let you sign in if you lose your authenticator device. Each code works <strong style={{ color: "#F1F5F9" }}>only once</strong>.
          </p>
        </div>

        <div style={{ padding: "14px 16px", background: "#F59E0B12", border: "1px solid #F59E0B30", borderRadius: 10, marginBottom: 20 }}>
          <p style={{ color: "#FCD34D", fontSize: 13, lineHeight: 1.6, margin: 0 }}>
            ⚠️ <strong>This is your only chance to save these codes.</strong> Store them in a password manager, print them, or save the file somewhere safe.
          </p>
        </div>

        <div style={{ background: "#06060B", border: "1px solid #ffffff10", borderRadius: 12, padding: 20, marginBottom: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 16px", fontFamily: "'JetBrains Mono', monospace" }}>
            {codes.map((code, idx) => (
              <div key={idx} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "#ffffff04", borderRadius: 6 }}>
                <span style={{ color: "#64748B", fontSize: 11, fontWeight: 500, minWidth: 18 }}>{String(idx + 1).padStart(2, "0")}</span>
                <span style={{ color: "#22D3EE", fontSize: 14, fontWeight: 700, letterSpacing: "0.05em" }}>{code}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
          <button onClick={copyAll} style={{ flex: 1, padding: 11, background: copied ? "#22C55E15" : "#ffffff04", border: `1px solid ${copied ? "#22C55E40" : "#ffffff12"}`, borderRadius: 10, color: copied ? "#22C55E" : "#E2E8F0", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
            {copied ? "✓ Copied!" : "📋 Copy All"}
          </button>
          <button onClick={downloadTxt} style={{ flex: 1, padding: 11, background: "#ffffff04", border: "1px solid #ffffff12", borderRadius: 10, color: "#E2E8F0", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
            ⬇️ Download .txt
          </button>
        </div>

        <div onClick={() => setSaved(!saved)} style={{ display: "flex", alignItems: "center", gap: 12, padding: 14, background: saved ? "#22C55E08" : "#ffffff02", border: `1px solid ${saved ? "#22C55E30" : "#ffffff10"}`, borderRadius: 10, cursor: "pointer", userSelect: "none", marginBottom: 20 }}>
          <div style={{ width: 20, height: 20, borderRadius: 5, flexShrink: 0, background: saved ? "#22C55E" : "transparent", border: `1.5px solid ${saved ? "#22C55E" : "#475569"}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {saved && <span style={{ color: "#06060B", fontSize: 12, fontWeight: 900, lineHeight: 1 }}>✓</span>}
          </div>
          <span style={{ color: "#E2E8F0", fontSize: 13, fontWeight: 500, lineHeight: 1.5 }}>
            I have saved my backup codes in a secure location. I understand that losing both my authenticator and these codes will lock me out of my account.
          </span>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={() => onConfirm(codes)} disabled={!saved || busy} style={{ flex: 1, padding: 13, background: (!saved || busy) ? "#1E293B" : "linear-gradient(135deg, #0891B2, #22D3EE)", border: "none", borderRadius: 10, color: (!saved || busy) ? "#64748B" : "#fff", fontSize: 14, fontWeight: 700, cursor: (!saved || busy) ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
            {busy ? "Enabling 2FA..." : "Continue & Enable 2FA →"}
          </button>
          <button onClick={onCancel} disabled={busy} style={{ padding: "13px 18px", background: "#ffffff04", border: "1px solid #ffffff10", borderRadius: 10, color: "#94A3B8", fontSize: 13, fontWeight: 600, cursor: busy ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: busy ? 0.5 : 1 }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// SECURITY REMINDER POPUP — shown after login if 2FA disabled
// ------------------------------------------------------------
// Every button click calls backend /api/auth/twofa-reminder-decision
// to create a timestamped audit log entry (IP + user agent captured
// server-side). This provides legal proof that the user was
// warned about 2FA being OFF and made an informed choice.
//
// Popup has NO close-X and NO click-outside dismiss — user MUST
// pick one of the two buttons to proceed.
// ============================================================
// ============================================================
// LICENSE HARD LOCK MODAL — V6.0 (cannot dismiss)
// Shows when user's license is expired.
// User can only contact support — cannot access dashboard.
// ============================================================
function LicenseHardLockModal({ license, onContactSupport }) {
  const [showSupport, setShowSupport] = useState(false);
  const planLabel = { starter_7d: 'Starter (7-day)', basic_30d: 'Basic (30-day)', popular_3m: 'Popular (3-month)', lifetime: 'Lifetime' }[license?.plan] || license?.plan || '—';
  const expiryStr = license?.expiry ? new Date(license.expiry).toLocaleDateString('en-MY', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';

  return (
    <>
      <div style={{
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999,
        background: "rgba(0,0,0,0.95)", backdropFilter: "blur(12px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        fontFamily: "'Outfit', sans-serif",
      }}>
        <div style={{
          width: "100%", maxWidth: 480, padding: 36,
          background: "#0A0A12", border: "1px solid #EF444430",
          borderRadius: 16, textAlign: "center",
          boxShadow: "0 0 60px rgba(239,68,68,0.15)",
        }}>
          {/* Wolf logo */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
            <WolfLogo size={64} />
          </div>

          {/* Lock icon */}
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>

          <h2 style={{ fontSize: 22, fontWeight: 800, color: "#F1F5F9", margin: "0 0 8px 0" }}>
            License Expired
          </h2>
          <p style={{ color: "#94A3B8", fontSize: 14, margin: "0 0 24px 0", lineHeight: 1.6 }}>
            Your SL/TP sync has been stopped. Purchase a new license to regain access.
          </p>

          {/* License details */}
          <div style={{
            padding: 16, background: "#06060B",
            border: "1px solid #1E293B", borderRadius: 10, marginBottom: 24, textAlign: "left",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ color: "#64748B", fontSize: 12 }}>Plan</span>
              <span style={{ color: "#E2E8F0", fontSize: 12, fontWeight: 600 }}>{planLabel}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ color: "#64748B", fontSize: 12 }}>Expired on</span>
              <span style={{ color: "#EF4444", fontSize: 12, fontWeight: 600 }}>{expiryStr}</span>
            </div>
            <div style={{ paddingTop: 8, borderTop: "1px solid #1E293B" }}>
              <span style={{ color: "#64748B", fontSize: 11 }}>License Key</span>
              <div style={{ color: "#22D3EE", fontSize: 11, fontFamily: "monospace", marginTop: 2, wordBreak: "break-all" }}>{license?.key || '—'}</div>
            </div>
          </div>

          {/* Contact Support button */}
          <button
            onClick={() => setShowSupport(true)}
            style={{
              width: "100%", padding: "14px",
              background: "linear-gradient(135deg, #0891B2, #22D3EE)",
              border: "none", borderRadius: 10,
              color: "#fff", fontSize: 15, fontWeight: 700,
              cursor: "pointer", fontFamily: "inherit", marginBottom: 12,
            }}
          >
            ✉ Contact Support to Renew
          </button>

          <p style={{ color: "#334155", fontSize: 11, margin: 0 }}>
            Email: <a href="mailto:laskysystem@gmail.com" style={{ color: "#22D3EE" }}>laskysystem@gmail.com</a>
          </p>
        </div>
      </div>

      {/* Public Support Modal triggered from Hard Lock */}
      {showSupport && (
        <PublicSupportModal isOpen={showSupport} onClose={() => {
          setShowSupport(false);
          onContactSupport?.();
        }} />
      )}
    </>
  );
}

function SecurityReminderPopup({ session, onEnable, onLater }) {
  const [busy, setBusy] = useState(null); // 'accepted' | 'declined' | null

  const logDecision = async (decision) => {
    try {
      await fetch(`${API_URL}/api/auth/twofa-reminder-decision`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ decision }),
      });
    } catch (e) {
      // Non-blocking: log failure but still let user proceed
      console.warn("[2FA Reminder] Failed to log decision:", e.message);
    }
  };

  const handleEnable = async () => {
    if (busy) return;
    setBusy("accepted");
    await logDecision("accepted");
    onEnable();
  };

  const handleLater = async () => {
    if (busy) return;
    setBusy("declined");
    await logDecision("declined");
    onLater();
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, animation: "fadeIn 0.25s ease" }}>
      <div style={{ width: 440, maxWidth: "calc(100% - 32px)", padding: 28, background: "#0A0A12", border: "1px solid #F59E0B30", borderRadius: 16, fontFamily: "'Outfit', sans-serif", animation: "fadeUp 0.35s ease" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 16 }}>
          <div style={{ fontSize: 28, flexShrink: 0 }}>⚠️</div>
          <div>
            <h3 style={{ color: "#F59E0B", fontSize: 17, fontWeight: 700, margin: "0 0 6px 0" }}>Your account is not fully secured</h3>
            <p style={{ color: "#CBD5E1", fontSize: 13, lineHeight: 1.6, margin: 0 }}>We strongly recommend enabling Two-Factor Authentication (2FA) to protect your account and trading data from unauthorized access.</p>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button onClick={handleEnable} disabled={!!busy} style={{ flex: 1, padding: 12, background: busy === "accepted" ? "#1E293B" : "linear-gradient(135deg, #F59E0B, #EAB308)", border: "none", borderRadius: 10, color: busy === "accepted" ? "#94A3B8" : "#06060B", fontSize: 13, fontWeight: 700, cursor: busy ? "wait" : "pointer", fontFamily: "inherit", opacity: busy && busy !== "accepted" ? 0.5 : 1 }}>{busy === "accepted" ? "..." : "Go to Profile & Enable 2FA"}</button>
          <button onClick={handleLater} disabled={!!busy} style={{ padding: "12px 18px", background: "#ffffff04", border: "1px solid #ffffff10", borderRadius: 10, color: "#94A3B8", fontSize: 13, fontWeight: 500, cursor: busy ? "wait" : "pointer", fontFamily: "inherit", opacity: busy && busy !== "declined" ? 0.5 : 1 }}>{busy === "declined" ? "..." : "Maybe Later"}</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// POST-CONNECT REMINDER POPUP — Step 2 MetaAPI Leak Fix
// Mandatory dormant policy reminder shown after MT5 connect/swap success
// Behavior:
//   - 15-second mandatory window (cannot close immediately)
//   - Close button appears at 10-second mark (5-sec bypass after)
//   - Close prevented until either 15sec OR user clicks "I Understand"
// ============================================================
function PostConnectReminderPopup({ onClose }) {
  const [secondsLeft, setSecondsLeft] = useState(15);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [secondsLeft]);

  const canClose = secondsLeft <= 10; // close button appears after 5 seconds (15 - 10 = 5 sec mandatory lock initial)
  const canForceClose = secondsLeft <= 0;

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", backdropFilter: "blur(8px)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1500,
      animation: "fadeIn 0.25s ease", padding: 20,
    }}>
      <div style={{
        maxWidth: 520, width: "100%",
        background: "#0A0A12", border: "1px solid #F59E0B30", borderRadius: 16,
        padding: 32, position: "relative",
        animation: "fadeUp 0.35s ease",
        boxShadow: "0 20px 60px rgba(245, 158, 11, 0.1)",
      }}>
        {/* Close button (only visible after 5sec) */}
        {canClose && (
          <button
            onClick={onClose}
            style={{
              position: "absolute", top: 16, right: 16, background: "none", border: "none",
              color: "#F1F5F9", fontSize: 20, cursor: "pointer", fontFamily: "inherit",
              width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center",
              borderRadius: 6, opacity: canForceClose ? 1 : 0.5, transition: "opacity 0.3s",
            }}
            title={canForceClose ? "Close" : `Force close available in ${secondsLeft}s`}
          >✕</button>
        )}

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
          <div style={{
            width: 52, height: 52, borderRadius: 12,
            background: "#F59E0B15", border: "1px solid #F59E0B30",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 26,
          }}>⚠️</div>
          <div>
            <h2 style={{ margin: 0, fontSize: 19, fontWeight: 700, color: "#F1F5F9" }}>
              Important: Daily Activity Policy
            </h2>
            <p style={{ margin: "4px 0 0 0", color: "#CBD5E1", fontSize: 13, fontWeight: 500 }}>
              Please read carefully before trading
            </p>
          </div>
        </div>

        {/* Intro */}
        <p style={{ color: "#F1F5F9", fontSize: 14, lineHeight: 1.6, margin: "0 0 18px 0" }}>
          Your MT5 account has been connected successfully. To protect your trading operations, please understand our automated monitoring policy:
        </p>

        {/* Bullet points */}
        <div style={{
          padding: "16px 18px", background: "#06060B", borderRadius: 12, border: "1px solid #1E293B",
          marginBottom: 18,
        }}>
          <ul style={{ margin: 0, paddingLeft: 22, color: "#E2E8F0", fontSize: 13.5, lineHeight: 1.75 }}>
            <li style={{ marginBottom: 10 }}>
              <strong style={{ color: "#F1F5F9" }}>Daily dashboard check</strong> — Verify sync status on <span style={{ color: "#22D3EE", fontWeight: 600 }}>laskysync.com</span> before opening new trades.
            </li>
            <li style={{ marginBottom: 10 }}>
              <strong style={{ color: "#F1F5F9" }}>24-hour activity rule</strong> — MT5 accounts with zero trading activity (no new entry, SL/TP modification) within a 24-hour window will be auto-disconnected.
            </li>
            <li style={{ marginBottom: 10 }}>
              <strong style={{ color: "#F1F5F9" }}>Daily scan at 12:00 AM MYT</strong> — The system automatically purges dormant accounts every day at midnight Malaysia time.
            </li>
            <li style={{ marginBottom: 10 }}>
              <strong style={{ color: "#F59E0B" }}>Multi-account independence</strong> — Each MT5 account is monitored <strong style={{ color: "#F59E0B" }}>separately</strong>. Activity on one account does <strong style={{ color: "#EF4444" }}>not</strong> protect your other accounts.
            </li>
            <li>
              <strong style={{ color: "#F1F5F9" }}>Reconnect anytime</strong> — If your account is disconnected, simply reconnect from your dashboard.
            </li>
          </ul>
        </div>

        {/* Warning strip */}
        <div style={{
          padding: "12px 14px", background: "#EF444410", border: "1px solid #EF444430",
          borderRadius: 10, marginBottom: 20,
          display: "flex", alignItems: "flex-start", gap: 10,
        }}>
          <span style={{ color: "#EF4444", fontSize: 16, flexShrink: 0 }}>⚠</span>
          <div style={{ color: "#FCA5A5", fontSize: 12.5, fontWeight: 600, lineHeight: 1.5 }}>
            Trading without verifying sync status may result in SL/TP not propagating to other positions. Always confirm your account is <strong style={{ color: "#F1F5F9" }}>ACTIVE</strong> before opening trades.
          </div>
        </div>

        {/* Action button */}
        <button
          onClick={onClose}
          disabled={!canClose}
          style={{
            width: "100%", padding: "16px", borderRadius: 12,
            background: canClose
              ? "linear-gradient(135deg, #0891B2, #22D3EE)"
              : "#1E293B",
            border: "none",
            color: canClose ? "#fff" : "#CBD5E1",
            fontSize: 15, fontWeight: 700, fontFamily: "inherit",
            cursor: canClose ? "pointer" : "not-allowed",
            transition: "all 0.3s",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}
        >
          {canClose
            ? "✓  I Understand — Proceed to Dashboard"
            : `Please read the policy (${secondsLeft - 10}s)...`}
        </button>

        {/* Countdown indicator */}
        <div style={{ marginTop: 10, textAlign: "center", color: "#CBD5E1", fontSize: 11, fontWeight: 500 }}>
          {canClose
            ? canForceClose
              ? "You may close this notice now."
              : `Quick-close available in ${secondsLeft}s`
            : "Please take a moment to read before dismissing"}
        </div>
      </div>
      <style>{`@keyframes fadeIn{from{opacity:0}to{opacity:1}} @keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}

// ============================================================
// SHARED STYLES (used by ProfilePage and SupportPage)
// ============================================================
const _inputStyle = { width: "100%", padding: "12px 16px", background: "#ffffff04", border: "1px solid #ffffff0a", borderRadius: 10, color: "#E2E8F0", fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box" };
const _labelStyle = { color: "#94A3B8", fontSize: 12, marginBottom: 6, display: "block", fontWeight: 500 };
const _cardStyle = { padding: 24, borderRadius: 14, background: "#0A0A12", border: "1px solid #ffffff08", marginBottom: 16 };
const _btnPrimary = (loading) => ({ padding: "12px 20px", background: loading ? "#1E293B" : "linear-gradient(135deg, #0891B2, #22D3EE)", border: "none", borderRadius: 10, color: "#fff", fontSize: 14, fontWeight: 700, cursor: loading ? "wait" : "pointer", fontFamily: "inherit" });
const _btnSecondary = { padding: "10px 18px", background: "#ffffff04", border: "1px solid #ffffff10", borderRadius: 10, color: "#94A3B8", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" };
const _btnDanger = (loading) => ({ padding: "12px 20px", background: loading ? "#1E293B" : "linear-gradient(135deg, #DC2626, #EF4444)", border: "none", borderRadius: 10, color: "#fff", fontSize: 14, fontWeight: 700, cursor: loading ? "wait" : "pointer", fontFamily: "inherit" });

function _SectionHeader({ icon, title, subtitle }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <span style={{ fontSize: 18 }}>{icon}</span>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: "#F1F5F9", margin: 0 }}>{title}</h3>
      </div>
      {subtitle && <p style={{ color: "#64748B", fontSize: 12, margin: 0, paddingLeft: 28 }}>{subtitle}</p>}
    </div>
  );
}

function _Alert({ type, children }) {
  const colors = { error: { bg: "#EF444410", border: "#EF444420", text: "#FCA5A5" }, success: { bg: "#22C55E10", border: "#22C55E20", text: "#86EFAC" }, warning: { bg: "#F59E0B10", border: "#F59E0B25", text: "#FCD34D" }, info: { bg: "#22D3EE10", border: "#22D3EE25", text: "#67E8F9" } };
  const c = colors[type] || colors.info;
  return <div style={{ padding: "10px 14px", borderRadius: 8, background: c.bg, border: `1px solid ${c.border}`, color: c.text, fontSize: 13, lineHeight: 1.5, marginTop: 12 }}>{children}</div>;
}

// ============================================================
// PROFILE PAGE (with sections A-G)
// ============================================================
function ProfilePage({ session, user, onLogout, scrollTo2FA, onScrollConsumed }) {
  const [profile, setProfile] = useState(null);
  const [securityScore, setSecurityScore] = useState({ score: 0, label: "Weak", color: "#EF4444" });
  const [loading, setLoading] = useState(true);

  const authFetch = async (path, options = {}) => {
    const res = await fetch(`${API_URL}${path}`, { ...options, headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}`, ...(options.headers || {}) } });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
    return data;
  };

  const loadProfile = async () => {
    try { const d = await authFetch("/api/user/profile"); setProfile(d.profile); setSecurityScore(d.securityScore); }
    catch (e) { console.error("Load profile failed:", e); }
    setLoading(false);
  };

  useEffect(() => { loadProfile(); }, []);

  // Auto-scroll to 2FA section when user arrives via "Enable 2FA Now" popup button
  useEffect(() => {
    if (!loading && scrollTo2FA) {
      const el = document.getElementById("twofa-section");
      if (el) el.scrollIntoView({ behavior: "auto", block: "start" });
      if (onScrollConsumed) onScrollConsumed();
    }
  }, [loading, scrollTo2FA]);

  if (loading) return <div style={{ textAlign: "center", padding: 48, color: "#475569", fontSize: 14 }}>Loading profile...</div>;

  return (
    <div style={{ animation: "fadeUp 0.4s ease", maxWidth: 760, margin: "0 auto" }}>
      <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4, color: "#F1F5F9" }}>Profile & Security</h2>
      <p style={{ color: "#94A3B8", fontSize: 14, marginBottom: 28, fontWeight: 300 }}>Manage your account, security settings, and data privacy.</p>

      <_AccountInfoSection profile={profile} authFetch={authFetch} onUpdated={loadProfile} />
      <_SecurityScoreSection score={securityScore} profile={profile} />
      <_TwoFactorSection profile={profile} authFetch={authFetch} onUpdated={loadProfile} />
      <_ChangePasswordSection profile={profile} authFetch={authFetch} />
      <_ChangeEmailSection profile={profile} authFetch={authFetch} onLogout={onLogout} />
      <_ExportDataSection authFetch={authFetch} />
      <_DeleteAccountSection profile={profile} authFetch={authFetch} onDeleted={onLogout} />

      <div style={{ ..._cardStyle, background: "#F59E0B08", border: "1px solid #F59E0B25", marginTop: 24 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <span style={{ fontSize: 20, flexShrink: 0 }}>⚠️</span>
          <div>
            <h4 style={{ color: "#F59E0B", fontSize: 14, fontWeight: 700, margin: "0 0 8px 0" }}>Security Disclaimer</h4>
            <p style={{ color: "#CBD5E1", fontSize: 13, lineHeight: 1.7, margin: 0 }}>Never share your login credentials or 2FA codes with anyone. If 2FA is not enabled, your account is at higher risk. In the event of unauthorized access due to disabled 2FA or compromised credentials shared by you, we will not be held responsible for resulting damages. We have implemented industry-standard security measures — your safety also depends on your own security practices.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- A. Account Info ----
function _AccountInfoSection({ profile, authFetch, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(profile?.displayName || "");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  useEffect(() => { setName(profile?.displayName || ""); }, [profile]);

  const save = async () => {
    setMsg(null); setSaving(true);
    try { await authFetch("/api/user/update-profile", { method: "POST", body: JSON.stringify({ displayName: name.trim() }) }); setMsg({ type: "success", text: "Display name updated." }); setEditing(false); onUpdated(); }
    catch (e) { setMsg({ type: "error", text: e.message }); }
    setSaving(false);
  };

  return (
    <div style={_cardStyle}>
      <_SectionHeader icon="👤" title="Account Info" subtitle="Your basic account details" />
      <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: "14px 20px", alignItems: "center" }}>
        <span style={{ color: "#64748B", fontSize: 13 }}>Display Name</span>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {editing ? (<>
            <input value={name} onChange={e => setName(e.target.value)} style={{ ..._inputStyle, flex: 1 }} maxLength={60} />
            <button onClick={save} disabled={saving} style={_btnPrimary(saving)}>{saving ? "..." : "Save"}</button>
            <button onClick={() => { setEditing(false); setName(profile?.displayName || ""); }} style={_btnSecondary}>Cancel</button>
          </>) : (<>
            <span style={{ color: "#E2E8F0", fontSize: 14, fontWeight: 500 }}>{profile?.displayName || "(not set)"}</span>
            <button onClick={() => setEditing(true)} style={{ ..._btnSecondary, padding: "6px 12px", fontSize: 12 }}>Edit</button>
          </>)}
        </div>
        <span style={{ color: "#64748B", fontSize: 13 }}>Email</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: "#E2E8F0", fontSize: 14 }}>{profile?.email}</span>
          {profile?.emailVerified && (<span style={{ padding: "3px 10px", borderRadius: 6, background: "#22C55E15", border: "1px solid #22C55E30", color: "#22C55E", fontSize: 11, fontWeight: 600 }}>✓ Verified</span>)}
        </div>
        <span style={{ color: "#64748B", fontSize: 13 }}>Account Created</span>
        <span style={{ color: "#94A3B8", fontSize: 13 }}>{profile?.accountCreated ? new Date(profile.accountCreated).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "—"}</span>
        <span style={{ color: "#64748B", fontSize: 13 }}>Last Login</span>
        <span style={{ color: "#94A3B8", fontSize: 13 }}>{profile?.lastLoginAt ? new Date(profile.lastLoginAt).toLocaleString("en-GB") : "First login"}</span>
      </div>
      {msg && <_Alert type={msg.type}>{msg.text}</_Alert>}
    </div>
  );
}

// ---- B. Security Score ----
function _SecurityScoreSection({ score, profile }) {
  return (
    <div style={_cardStyle}>
      <_SectionHeader icon="🛡️" title="Security Strength" subtitle="How well protected your account is" />
      <div style={{ marginTop: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <span style={{ color: score.color, fontSize: 18, fontWeight: 700 }}>{score.label}</span>
          <span style={{ color: "#94A3B8", fontSize: 14, fontWeight: 600 }}>{score.score}%</span>
        </div>
        <div style={{ height: 8, borderRadius: 4, background: "#1E293B", overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${score.score}%`, background: score.color, transition: "width 0.6s ease", borderRadius: 4 }} />
        </div>
        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 6 }}>
          <_ScoreItem done={true} label="Password set" points="+30%" />
          <_ScoreItem done={profile?.emailVerified} label="Email verified" points="+30%" />
          <_ScoreItem done={profile?.twofaEnabled} label="Two-factor authentication enabled" points="+40%" />
        </div>
      </div>
    </div>
  );
}
function _ScoreItem({ done, label, points }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ color: done ? "#22C55E" : "#475569", fontSize: 16 }}>{done ? "✓" : "○"}</span>
        <span style={{ color: done ? "#CBD5E1" : "#64748B", fontSize: 13 }}>{label}</span>
      </div>
      <span style={{ color: done ? "#22C55E" : "#475569", fontSize: 11, fontWeight: 600 }}>{points}</span>
    </div>
  );
}

// ---- C. Two-Factor Authentication ----
function _TwoFactorSection({ profile, authFetch, onUpdated }) {
  const [step, setStep] = useState("idle");
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [secret, setSecret] = useState(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  // S2 Phase 1: Store pending backup codes between TOTP verify and DB save
  const [pendingBackupCodes, setPendingBackupCodes] = useState(null);
  const [showBackupModal, setShowBackupModal] = useState(false);
  const isEnabled = !!profile?.twofaEnabled;

  const startSetup = async () => {
    setMsg(null); setBusy(true);
    try {
      const d = await authFetch("/api/user/2fa/setup", { method: "POST" });
      setQrDataUrl(d.qrDataUrl); setSecret(d.secret);
      // S2 Phase 1: Backend returns 10 backup codes with /setup. Hold them
      // in state — we'll show modal after TOTP verifies successfully.
      setPendingBackupCodes(Array.isArray(d.backupCodes) ? d.backupCodes : []);
      setStep("verify");
    }
    catch (e) { setMsg({ type: "error", text: e.message }); }
    setBusy(false);
  };

  // Step A: Verify TOTP first (don't enable yet) — if valid, show backup codes
  const verifyTOTP = async () => {
    setMsg(null); setBusy(true);
    try {
      // S2 Phase 1: To use the backup-code-storage flow, we must submit TOTP
      // + backupCodes together in ONE request. So instead of verifying early,
      // we pop the backup codes modal first, user saves them, then we submit.
      // Here we just pre-validate TOTP format + open the modal.
      if (!/^\d{6}$/.test(code)) throw new Error("Please enter a valid 6-digit code.");
      if (!pendingBackupCodes || pendingBackupCodes.length !== 10) {
        throw new Error("Backup codes missing. Please restart 2FA setup.");
      }
      // Open backup codes modal — actual /verify call happens on confirm
      setShowBackupModal(true);
    }
    catch (e) { setMsg({ type: "error", text: e.message }); }
    setBusy(false);
  };

  // Step B: User has acknowledged backup codes — submit to backend now
  const confirmSetupWithBackupCodes = async (backupCodes) => {
    setBusy(true); setMsg(null);
    try {
      await authFetch("/api/user/2fa/verify", {
        method: "POST",
        body: JSON.stringify({ code, backupCodes }),
      });
      setMsg({ type: "success", text: "2FA enabled successfully! Backup codes saved." });
      setStep("idle"); setCode(""); setQrDataUrl(null); setSecret(null);
      setPendingBackupCodes(null); setShowBackupModal(false);
      onUpdated();
    }
    catch (e) {
      setMsg({ type: "error", text: e.message });
      setShowBackupModal(false); // close modal on error so user sees message
    }
    setBusy(false);
  };

  const cancelBackupModal = () => {
    setShowBackupModal(false);
    // Keep pending codes in state — user can retry verify without re-scanning QR
  };

  const cancelSetup = () => {
    setStep("idle"); setCode(""); setQrDataUrl(null);
    setSecret(null); setPendingBackupCodes(null); setShowBackupModal(false);
  };

  const confirmDisable = async () => {
    setMsg(null); setBusy(true);
    try { await authFetch("/api/user/2fa/disable", { method: "POST", body: JSON.stringify({ password, code }) }); setMsg({ type: "success", text: "2FA has been disabled." }); setStep("idle"); setCode(""); setPassword(""); onUpdated(); }
    catch (e) { setMsg({ type: "error", text: e.message }); }
    setBusy(false);
  };

  return (
    <div id="twofa-section" style={_cardStyle}>
      <_SectionHeader icon="🔐" title="Two-Factor Authentication (2FA)" subtitle="Uses Google Authenticator, Authy, 1Password, or any TOTP app" />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderRadius: 10, background: isEnabled ? "#22C55E08" : "#ffffff02", border: `1px solid ${isEnabled ? "#22C55E25" : "#ffffff08"}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: isEnabled ? "#22C55E" : "#475569", boxShadow: isEnabled ? "0 0 8px #22C55E60" : "none" }} />
          <span style={{ color: isEnabled ? "#22C55E" : "#E2E8F0", fontSize: 14, fontWeight: 600 }}>{isEnabled ? "2FA is ENABLED" : "2FA is OFF"}</span>
        </div>
        {step === "idle" && (isEnabled ? (<button onClick={() => setStep("disable")} style={_btnSecondary}>Disable 2FA</button>) : (<button onClick={startSetup} disabled={busy} style={_btnPrimary(busy)}>{busy ? "..." : "Enable 2FA"}</button>))}
      </div>

      {step === "verify" && qrDataUrl && (
        <div style={{ marginTop: 16, padding: 20, borderRadius: 12, background: "#06060B", border: "1px solid #ffffff08" }}>
          <p style={{ color: "#E2E8F0", fontSize: 13, lineHeight: 1.6, marginTop: 0 }}><strong style={{ color: "#F1F5F9" }}>Step 1:</strong> Open your authenticator app and scan this QR code:</p>
          <div style={{ textAlign: "center", padding: 20, background: "#fff", borderRadius: 12, marginBottom: 16 }}>
            <img src={qrDataUrl} alt="2FA QR Code" style={{ width: 200, height: 200 }} />
          </div>
          {secret && (<div style={{ padding: "10px 14px", background: "#ffffff04", border: "1px solid #ffffff08", borderRadius: 8, marginBottom: 16 }}>
            <div style={{ color: "#CBD5E1", fontSize: 11, marginBottom: 4, fontWeight: 500 }}>Can't scan? Enter this secret manually:</div>
            <code style={{ color: "#22D3EE", fontSize: 13, fontFamily: "'JetBrains Mono', monospace", wordBreak: "break-all" }}>{secret}</code>
          </div>)}
          <p style={{ color: "#E2E8F0", fontSize: 13, lineHeight: 1.6 }}><strong style={{ color: "#F1F5F9" }}>Step 2:</strong> Enter the 6-digit code from your app:</p>
          <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" style={{ ..._inputStyle, letterSpacing: "0.3em", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 18, fontWeight: 700 }} />
          <p style={{ color: "#CBD5E1", fontSize: 12, lineHeight: 1.6, marginTop: 14, marginBottom: 0 }}>
            <strong style={{ color: "#22D3EE" }}>Next:</strong> You'll be shown 10 one-time backup codes. Save them securely before 2FA is enabled.
          </p>
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <button onClick={verifyTOTP} disabled={busy || code.length !== 6} style={{ ..._btnPrimary(busy), flex: 1, opacity: code.length !== 6 ? 0.5 : 1 }}>{busy ? "Verifying..." : "Verify & Continue →"}</button>
            <button onClick={cancelSetup} style={_btnSecondary}>Cancel</button>
          </div>
        </div>
      )}

      {step === "disable" && (
        <div style={{ marginTop: 16, padding: 20, borderRadius: 12, background: "#06060B", border: "1px solid #EF444425" }}>
          <p style={{ color: "#FCA5A5", fontSize: 13, lineHeight: 1.6, marginTop: 0 }}>⚠️ Disabling 2FA makes your account less secure. Enter your password and current 2FA code:</p>
          <div style={{ marginBottom: 12 }}><label style={_labelStyle}>Password</label><input type="password" value={password} onChange={e => setPassword(e.target.value)} style={_inputStyle} /></div>
          <div style={{ marginBottom: 16 }}><label style={_labelStyle}>Current 2FA Code</label><input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" style={{ ..._inputStyle, letterSpacing: "0.3em", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }} /></div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={confirmDisable} disabled={busy || !password || code.length !== 6} style={{ ..._btnDanger(busy), flex: 1, opacity: (!password || code.length !== 6) ? 0.5 : 1 }}>{busy ? "Disabling..." : "Disable 2FA"}</button>
            <button onClick={() => { setStep("idle"); setCode(""); setPassword(""); }} style={_btnSecondary}>Cancel</button>
          </div>
        </div>
      )}
      {msg && <_Alert type={msg.type}>{msg.text}</_Alert>}

      {/* S2 Phase 1: Backup Codes Modal */}
      {showBackupModal && pendingBackupCodes && (
        <BackupCodesModal
          codes={pendingBackupCodes}
          onConfirm={confirmSetupWithBackupCodes}
          onCancel={cancelBackupModal}
          busy={busy}
        />
      )}
    </div>
  );
}

// ---- D. Change Password ----
function _ChangePasswordSection({ profile, authFetch }) {
  const [show, setShow] = useState(false);
  const [current, setCurrent] = useState(""); const [newPw, setNewPw] = useState(""); const [confirm, setConfirm] = useState(""); const [twofaCode, setTwofaCode] = useState("");
  const [busy, setBusy] = useState(false); const [msg, setMsg] = useState(null);

  const strength = checkPasswordStrength(newPw);
  const canSubmit = current && strength.allMet && newPw === confirm && (!profile?.twofaEnabled || twofaCode.length === 6);
  const reset = () => { setCurrent(""); setNewPw(""); setConfirm(""); setTwofaCode(""); };

  const submit = async () => {
    setMsg(null);
    if (newPw !== confirm) { setMsg({ type: "error", text: "New passwords do not match." }); return; }
    if (!strength.allMet) {
      const missing = [];
      if (!strength.checks.len) missing.push("8+ characters");
      if (!strength.checks.upper) missing.push("uppercase letter");
      if (!strength.checks.lower) missing.push("lowercase letter");
      if (!strength.checks.number) missing.push("number");
      if (!strength.checks.symbol) missing.push("symbol");
      setMsg({ type: "error", text: `Password must include: ${missing.join(", ")}.` });
      return;
    }
    setBusy(true);
    try { await authFetch("/api/user/change-password", { method: "POST", body: JSON.stringify({ currentPassword: current, newPassword: newPw, twofaCode: twofaCode || undefined }) }); setMsg({ type: "success", text: "Password changed. A confirmation email has been sent." }); reset(); setShow(false); }
    catch (e) { setMsg({ type: "error", text: e.message }); }
    setBusy(false);
  };

  return (
    <div style={_cardStyle}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <_SectionHeader icon="🔑" title="Change Password" subtitle="Update your account password" />
        {!show && <button onClick={() => setShow(true)} style={_btnSecondary}>Change</button>}
      </div>
      {show && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 14 }}>
          <div><label style={_labelStyle}>Current Password</label><input type="password" value={current} onChange={e => setCurrent(e.target.value)} style={_inputStyle} /></div>
          <div>
            <label style={_labelStyle}>New Password</label>
            <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} style={_inputStyle} />
            {newPw && (() => {
              const CheckItem = ({ ok, text }) => (
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: ok ? "#22C55E" : "#64748B", fontWeight: 500 }}>
                  <span style={{ fontSize: 10 }}>{ok ? "✓" : "○"}</span>
                  <span>{text}</span>
                </div>
              );
              return (
                <div style={{ marginTop: 8 }}>
                  <div style={{ height: 4, borderRadius: 2, background: "#1E293B", overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${strength.percent}%`, background: strength.color, transition: "width 0.3s ease" }} />
                  </div>
                  <div style={{ color: strength.color, fontSize: 11, fontWeight: 600, marginTop: 4 }}>Strength: {strength.label}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px", marginTop: 10, padding: "10px 12px", background: "#ffffff03", border: "1px solid #ffffff06", borderRadius: 8 }}>
                    <CheckItem ok={strength.checks.len} text="8+ characters" />
                    <CheckItem ok={strength.checks.upper} text="Uppercase (A-Z)" />
                    <CheckItem ok={strength.checks.lower} text="Lowercase (a-z)" />
                    <CheckItem ok={strength.checks.number} text="Number (0-9)" />
                    <CheckItem ok={strength.checks.symbol} text="Symbol (!@#$...)" />
                  </div>
                </div>
              );
            })()}
          </div>
          <div>
            <label style={_labelStyle}>Confirm New Password</label>
            <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} style={_inputStyle} />
            {confirm && newPw !== confirm && (<div style={{ color: "#EF4444", fontSize: 11, marginTop: 4 }}>Passwords do not match.</div>)}
          </div>
          {profile?.twofaEnabled && (<div><label style={_labelStyle}>Current 2FA Code</label><input value={twofaCode} onChange={e => setTwofaCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" style={{ ..._inputStyle, letterSpacing: "0.3em", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }} /></div>)}
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={submit} disabled={busy || !canSubmit} style={{ ..._btnPrimary(busy), flex: 1, opacity: canSubmit ? 1 : 0.5 }}>{busy ? "Saving..." : "Update Password"}</button>
            <button onClick={() => { setShow(false); reset(); setMsg(null); }} style={_btnSecondary}>Cancel</button>
          </div>
        </div>
      )}
      {msg && <_Alert type={msg.type}>{msg.text}</_Alert>}
    </div>
  );
}

// ---- E. Change Email ----
function _ChangeEmailSection({ profile, authFetch, onLogout }) {
  const [step, setStep] = useState("idle");
  const [password, setPassword] = useState(""); const [newEmail, setNewEmail] = useState(""); const [twofaCode, setTwofaCode] = useState(""); const [otpCode, setOtpCode] = useState("");
  const [tncAccepted, setTncAccepted] = useState(false);
  const [busy, setBusy] = useState(false); const [msg, setMsg] = useState(null); const [sentTo, setSentTo] = useState("");
  const reset = () => { setPassword(""); setNewEmail(""); setTwofaCode(""); setOtpCode(""); setSentTo(""); setMsg(null); setTncAccepted(false); };

  const requestOtp = async () => {
    setMsg(null); setBusy(true);
    try { const d = await authFetch("/api/user/change-email/request", { method: "POST", body: JSON.stringify({ currentPassword: password, newEmail, twofaCode: twofaCode || undefined, tncAccepted: true }) }); setSentTo(d.to); setStep("verify"); }
    catch (e) { setMsg({ type: "error", text: e.message }); }
    setBusy(false);
  };
  const confirmOtp = async () => {
    setMsg(null); setBusy(true);
    try { await authFetch("/api/user/change-email/confirm", { method: "POST", body: JSON.stringify({ code: otpCode }) }); setMsg({ type: "success", text: "Email changed! Please log in again with your new email." }); setTimeout(() => { onLogout?.(); }, 2500); }
    catch (e) { setMsg({ type: "error", text: e.message }); }
    setBusy(false);
  };

  return (
    <div style={_cardStyle}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <_SectionHeader icon="✉️" title="Change Email Address" subtitle="Requires TNC re-signature, password, 2FA (if on), and OTP verification" />
        {step === "idle" && <button onClick={() => setStep("tnc")} style={_btnSecondary}>Change</button>}
      </div>
      {step === "tnc" && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ background: "#0F172A", border: "1px solid #1E293B", borderRadius: 12, padding: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <span style={{ fontSize: 20 }}>📜</span>
              <h4 style={{ color: "#F1F5F9", fontSize: 15, fontWeight: 700, margin: 0 }}>Terms & Conditions Re-Signature</h4>
            </div>
            <p style={{ color: "#E2E8F0", fontSize: 13.5, lineHeight: 1.7, margin: "0 0 12px 0", fontWeight: 500 }}>
              Changing your email address requires a fresh acceptance of our Terms and Conditions for legal audit purposes.
            </p>
            <p style={{ color: "#CBD5E1", fontSize: 13, lineHeight: 1.7, margin: "0 0 12px 0" }}>
              This creates a new signed record in our database with your new email address, providing a complete audit trail. Your previous TNC acceptance with your old email remains permanently on record (immutable).
            </p>
            <p style={{ color: "#CBD5E1", fontSize: 13, lineHeight: 1.7, margin: "0 0 16px 0" }}>
              By proceeding, you re-affirm your acceptance of the LASKY SL/TP SYNC Terms and Conditions (effective 2026-04-20) under your new email address.
            </p>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", padding: 12, background: "#06060B", border: "1px solid #1E293B", borderRadius: 8 }}>
              <input
                type="checkbox"
                checked={tncAccepted}
                onChange={(e) => setTncAccepted(e.target.checked)}
                style={{ marginTop: 3, width: 16, height: 16, accentColor: "#22D3EE", cursor: "pointer" }}
              />
              <span style={{ color: "#E2E8F0", fontSize: 13.5, lineHeight: 1.6, fontWeight: 500 }}>
                I accept the LASKY SL/TP SYNC Terms and Conditions (v2026-04-20) and authorize this email change to be recorded in the audit trail.
              </span>
            </label>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setStep("request")} disabled={!tncAccepted} style={{ ..._btnPrimary(false), flex: 1, opacity: !tncAccepted ? 0.5 : 1 }}>Continue</button>
            <button onClick={() => { setStep("idle"); reset(); }} style={_btnSecondary}>Cancel</button>
          </div>
        </div>
      )}
      {step === "request" && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ background: "#0F172A", border: "1px solid #1E293B", borderRadius: 8, padding: 12 }}>
            <p style={{ color: "#22D3EE", fontSize: 12.5, margin: 0, fontWeight: 500 }}>✓ TNC accepted. Please verify your identity to proceed.</p>
          </div>
          <div><label style={_labelStyle}>Current Password</label><input type="password" value={password} onChange={e => setPassword(e.target.value)} style={_inputStyle} /></div>
          {profile?.twofaEnabled && (<div><label style={_labelStyle}>Current 2FA Code</label><input value={twofaCode} onChange={e => setTwofaCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" style={{ ..._inputStyle, letterSpacing: "0.3em", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }} /></div>)}
          <div><label style={_labelStyle}>New Email Address</label><input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="new@example.com" style={_inputStyle} /></div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={requestOtp} disabled={busy || !password || !newEmail} style={{ ..._btnPrimary(busy), flex: 1, opacity: (!password || !newEmail) ? 0.5 : 1 }}>{busy ? "Sending..." : "Send OTP to New Email"}</button>
            <button onClick={() => { setStep("idle"); reset(); }} style={_btnSecondary}>Cancel</button>
          </div>
        </div>
      )}
      {step === "verify" && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 14 }}>
          <p style={{ color: "#CBD5E1", fontSize: 13, lineHeight: 1.6, margin: 0 }}>We sent a 6-digit code to <strong style={{ color: "#22D3EE" }}>{sentTo}</strong>. A security notice has also been sent to your current email.</p>
          <div><label style={_labelStyle}>Enter 6-digit Code</label><input value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" style={{ ..._inputStyle, letterSpacing: "0.3em", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 18, fontWeight: 700 }} /></div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={confirmOtp} disabled={busy || otpCode.length !== 6} style={{ ..._btnPrimary(busy), flex: 1, opacity: otpCode.length !== 6 ? 0.5 : 1 }}>{busy ? "Verifying..." : "Confirm Email Change"}</button>
            <button onClick={() => { setStep("idle"); reset(); }} style={_btnSecondary}>Cancel</button>
          </div>
        </div>
      )}
      {msg && <_Alert type={msg.type}>{msg.text}</_Alert>}
    </div>
  );
}

// ---- F. Export Data ----
function _ExportDataSection({ authFetch }) {
  const [step, setStep] = useState("idle");
  const [password, setPassword] = useState(""); const [otpCode, setOtpCode] = useState("");
  const [busy, setBusy] = useState(false); const [msg, setMsg] = useState(null); const [sentTo, setSentTo] = useState("");

  const requestOtp = async () => {
    setMsg(null); setBusy(true);
    try { const d = await authFetch("/api/user/export-data/request", { method: "POST", body: JSON.stringify({ currentPassword: password }) }); setSentTo(d.to); setStep("verify"); }
    catch (e) { setMsg({ type: "error", text: e.message }); }
    setBusy(false);
  };
  const confirmDownload = async () => {
    setMsg(null); setBusy(true);
    try {
      const d = await authFetch("/api/user/export-data/confirm", { method: "POST", body: JSON.stringify({ code: otpCode }) });
      const blob = new Blob([JSON.stringify(d.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `laskysync-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
      setMsg({ type: "success", text: "Your data has been downloaded." });
      setStep("idle"); setPassword(""); setOtpCode("");
    } catch (e) { setMsg({ type: "error", text: e.message }); }
    setBusy(false);
  };

  return (
    <div style={_cardStyle}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <_SectionHeader icon="💾" title="Download My Data (PDPA)" subtitle="Redacted JSON of your account data — password + OTP required" />
        {step === "idle" && <button onClick={() => setStep("request")} style={_btnSecondary}>Download</button>}
      </div>
      {step === "request" && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 14 }}>
          <p style={{ color: "#64748B", fontSize: 12, lineHeight: 1.6, margin: 0 }}>Your exported data excludes: passwords, IP addresses, third-party vendor details, and precise timestamps. This complies with PDPA 2010 Section 30A.</p>
          <div><label style={_labelStyle}>Current Password</label><input type="password" value={password} onChange={e => setPassword(e.target.value)} style={_inputStyle} /></div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={requestOtp} disabled={busy || !password} style={{ ..._btnPrimary(busy), flex: 1, opacity: !password ? 0.5 : 1 }}>{busy ? "Sending..." : "Send OTP"}</button>
            <button onClick={() => { setStep("idle"); setPassword(""); }} style={_btnSecondary}>Cancel</button>
          </div>
        </div>
      )}
      {step === "verify" && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 14 }}>
          <p style={{ color: "#CBD5E1", fontSize: 13, margin: 0 }}>OTP sent to <strong style={{ color: "#22D3EE" }}>{sentTo}</strong></p>
          <div><label style={_labelStyle}>Enter 6-digit Code</label><input value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" style={{ ..._inputStyle, letterSpacing: "0.3em", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }} /></div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={confirmDownload} disabled={busy || otpCode.length !== 6} style={{ ..._btnPrimary(busy), flex: 1, opacity: otpCode.length !== 6 ? 0.5 : 1 }}>{busy ? "Preparing..." : "Verify & Download"}</button>
            <button onClick={() => { setStep("idle"); setPassword(""); setOtpCode(""); }} style={_btnSecondary}>Cancel</button>
          </div>
        </div>
      )}
      {msg && <_Alert type={msg.type}>{msg.text}</_Alert>}
    </div>
  );
}

// ---- G. Delete Account ----
function _DeleteAccountSection({ profile, authFetch, onDeleted }) {
  const [step, setStep] = useState("idle");
  const [password, setPassword] = useState(""); const [twofaCode, setTwofaCode] = useState(""); const [otpCode, setOtpCode] = useState(""); const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false); const [msg, setMsg] = useState(null); const [sentTo, setSentTo] = useState("");

  const requestOtp = async () => {
    setMsg(null); setBusy(true);
    try { const d = await authFetch("/api/user/delete-account/request", { method: "POST", body: JSON.stringify({ currentPassword: password, twofaCode: twofaCode || undefined }) }); setSentTo(d.to); setStep("verify"); }
    catch (e) { setMsg({ type: "error", text: e.message }); }
    setBusy(false);
  };
  const confirmDelete = async () => {
    setMsg(null); setBusy(true);
    try { await authFetch("/api/user/delete-account/confirm", { method: "POST", body: JSON.stringify({ code: otpCode, confirmationPhrase: phrase }) }); setMsg({ type: "success", text: "Account deleted. You will be logged out..." }); setTimeout(() => { onDeleted?.(); }, 2500); }
    catch (e) { setMsg({ type: "error", text: e.message }); }
    setBusy(false);
  };

  return (
    <div style={{ ..._cardStyle, background: "#EF444408", border: "1px solid #EF444420" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <_SectionHeader icon="🗑️" title="Delete Account" subtitle="Close your account" />
        {step === "idle" && <button onClick={() => setStep("warning")} style={{ ..._btnSecondary, color: "#EF4444", borderColor: "#EF444430" }}>Delete</button>}
      </div>
      {step === "warning" && (
        <div style={{ marginTop: 12, padding: 20, borderRadius: 12, background: "#06060B", border: "1px solid #EF444425" }}>
          <p style={{ color: "#CBD5E1", fontSize: 14, margin: "0 0 16px 0" }}>
            Delete your account?
          </p>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setStep("request")} style={{ ..._btnDanger(false), flex: 1 }}>Yes</button>
            <button onClick={() => setStep("idle")} style={_btnSecondary}>No</button>
          </div>
        </div>
      )}
      {step === "request" && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 14 }}>
          <div><label style={_labelStyle}>Current Password</label><input type="password" value={password} onChange={e => setPassword(e.target.value)} style={_inputStyle} /></div>
          {profile?.twofaEnabled && (<div><label style={_labelStyle}>Current 2FA Code</label><input value={twofaCode} onChange={e => setTwofaCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" style={{ ..._inputStyle, letterSpacing: "0.3em", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }} /></div>)}
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={requestOtp} disabled={busy || !password} style={{ ..._btnDanger(busy), flex: 1, opacity: !password ? 0.5 : 1 }}>{busy ? "Sending..." : "Send OTP"}</button>
            <button onClick={() => { setStep("idle"); setPassword(""); setTwofaCode(""); }} style={_btnSecondary}>Cancel</button>
          </div>
        </div>
      )}
      {step === "verify" && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 14 }}>
          <p style={{ color: "#FCA5A5", fontSize: 13, margin: 0 }}>Final step. OTP sent to <strong>{sentTo}</strong>.</p>
          <div><label style={_labelStyle}>6-digit Code</label><input value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" style={{ ..._inputStyle, letterSpacing: "0.3em", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }} /></div>
          <div><label style={_labelStyle}>Type "DELETE MY ACCOUNT" to confirm</label><input value={phrase} onChange={e => setPhrase(e.target.value)} placeholder="DELETE MY ACCOUNT" style={_inputStyle} /></div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={confirmDelete} disabled={busy || otpCode.length !== 6 || phrase !== "DELETE MY ACCOUNT"} style={{ ..._btnDanger(busy), flex: 1, opacity: (otpCode.length !== 6 || phrase !== "DELETE MY ACCOUNT") ? 0.5 : 1 }}>{busy ? "Processing..." : "Confirm"}</button>
            <button onClick={() => { setStep("idle"); setPassword(""); setTwofaCode(""); setOtpCode(""); setPhrase(""); }} style={_btnSecondary}>Cancel</button>
          </div>
        </div>
      )}
      {msg && <_Alert type={msg.type}>{msg.text}</_Alert>}
    </div>
  );
}

// ============================================================
// SUPPORT PAGE (FAQ + Submit Ticket + My Tickets)
// ============================================================
const _CATEGORIES = ["MT5 Connection Issue","License / Subscription","Payment / Billing","SL/TP Sync Not Working","Account / Profile","Data Privacy / PDPA Request","Feature Request","Other"];

const _FAQS = [
  {
    id: "getting-started",
    title: "Getting Started",
    icon: "🚀",
    items: [
      {
        id: "gs-1",
        q: "What is LASKY SL/TP SYNC Cloud?",
        a: "LASKY SL/TP SYNC Cloud is an automated stop-loss and take-profit synchronization system. When you change the SL or TP on any one position, our cloud engine instantly detects the change and applies the same SL/TP to all other positions on the same symbol — no EA installation required, everything runs in the cloud 24/7."
      },
      {
        id: "gs-2",
        q: "How does it work?",
        a: "Three simple steps:\n\n1. Activate your license key on the Activate page.\n2. Connect your MT5 account via the Connect MT5 page (we use MetaAPI Cloud).\n3. Toggle SL Sync and/or TP Sync ON from your dashboard.\n\nThat's it — the engine polls your account 24/7 and mirrors any SL/TP change across all positions of the same symbol within milliseconds."
      },
      {
        id: "gs-3",
        q: "What is the difference between SL Sync and TP Sync?",
        a: "SL Sync monitors and synchronizes your Stop Loss across all positions of the same symbol. TP Sync does the same for Take Profit. You can enable or disable each one independently from your dashboard using the Start/Stop buttons."
      },
      {
        id: "gs-4",
        q: "How do I check if my sync is working?",
        a: "On the Home page, look for the status indicator on your account card. A green dot with \"SL Sync ON\" or \"TP Sync ON\" means the engine is running. You can also check the History tab for a full log of all sync events with timestamps."
      }
    ]
  },
  {
    id: "mt5-connection",
    title: "MT5 Connection",
    icon: "🔌",
    items: [
      {
        id: "mt5-1",
        q: "How do I connect my MT5 account?",
        a: "After activating your license key, go to the Connect MT5 page. Enter your MT5 Login (account number), MT5 Password (investor or master), select your broker, then choose your server from the list. If your server is not listed, click \"My server is not in this list\" to type it manually. Always click Validate before connecting."
      },
      {
        id: "mt5-2",
        q: "What password should I use — Investor or Master?",
        a: "You can use either. Investor password gives read-only access to your account data (positions, balance) — the sync engine can still modify SL/TP with investor access. Master password gives full access. Both work for SL/TP synchronization.\n\nFor extra peace of mind, we recommend using the Investor password."
      },
      {
        id: "mt5-3",
        q: "My server is not in the dropdown list. What should I do?",
        a: "Click the \"My server is not in this list — Type manually\" button below the server list. The system will detect the server naming pattern from your broker and suggest the correct format. You can also type the full server name manually. Make sure it is 100% exact — including uppercase/lowercase, dashes, spaces, and numbers."
      },
      {
        id: "mt5-4",
        q: "I entered the wrong server name and validation failed. What now?",
        a: "No worries — just correct the server name and click Validate again. The system checks your credentials with MetaAPI before connecting, so no wrong connection is ever made. Your license is not consumed by failed validation attempts."
      },
      {
        id: "mt5-5",
        q: "Can I swap to a different MT5 account later?",
        a: "Yes. Go to your dashboard, click Disconnect on the current MT5 account, then reconnect with the new MT5 credentials. Your license stays active and is not consumed by the swap. One license = one active connection at a time, but you can swap as many times as you need."
      },
      {
        id: "mt5-6",
        q: "What happens if my broker disconnects or it's the weekend?",
        a: "The sync engine stays online and ready. When the market is closed (weekends/holidays) or your broker temporarily disconnects, no sync events trigger because there are no live positions to monitor. Once the market reopens or your broker reconnects, the engine resumes automatically — no action needed from you."
      }
    ]
  },
  {
    id: "license-plans",
    title: "License & Plans",
    icon: "🔑",
    items: [
      {
        id: "lic-1",
        q: "What plans are available?",
        a: "We offer three plans:\n\n• Starter — 7 Days @ $15 (try us out)\n• Basic — 1 Month @ $55 (most flexible)\n• Popular — 3 Months @ $150 (best value, ~$50/month)\n\nAll plans include full SL Sync + TP Sync, 24/7 cloud engine, unlimited symbol coverage, and full trade history access."
      },
      {
        id: "lic-2",
        q: "Can I use one license on multiple MT5 accounts?",
        a: "No. One license key supports one active MT5 connection at a time. However, you can swap your MT5 account anytime — just disconnect the current account and connect a new one. If you need multiple accounts running simultaneously, you will need a separate license key for each account."
      },
      {
        id: "lic-3",
        q: "What happens when my license expires?",
        a: "When your license expires:\n\n• Your MT5 connection stays in the system but the SL/TP sync engine stops processing new events.\n• Your trade history remains accessible.\n• You can renew at any time and the sync resumes immediately upon renewal.\n\nWe send you reminder notifications before expiry so you never get caught off-guard."
      },
      {
        id: "lic-4",
        q: "How do I renew my license?",
        a: "Renewal will be available directly from your dashboard via the Plans page once payment integration is live. You will be able to extend your existing license without losing your MT5 connection or history. Currently, please contact support to arrange manual renewal."
      }
    ]
  },
  {
    id: "account-security",
    title: "Account Security",
    icon: "🔐",
    items: [
      {
        id: "sec-1",
        q: "Is 2FA required or optional?",
        a: "2FA is strongly recommended but optional. You can enable it from your Profile → Security page. We support TOTP authenticator apps like Google Authenticator, Authy, Microsoft Authenticator, or 1Password.\n\nWhen 2FA is enabled, you will also receive 10 backup codes — store these somewhere safe in case you lose your phone."
      },
      {
        id: "sec-2",
        q: "I lost my phone or 2FA device — what do I do?",
        a: "If you saved your backup codes when you enabled 2FA: use one of those codes on the 2FA challenge screen to log in, then disable 2FA from Profile → Security and re-enable it with your new device.\n\nIf you did not save backup codes: contact support with proof of identity (email verification + license key). Recovery may take 24–48 hours for security verification."
      },
      {
        id: "sec-3",
        q: "How are my MT5 credentials stored?",
        a: "Your MT5 password is encrypted using AES-256 envelope encryption before being stored. Each user has their own unique encryption key. Even our database administrators cannot read your raw MT5 password — it is only decrypted in-memory at the moment of connection to MetaAPI.\n\nWe also recommend using your Investor password instead of Master password for an extra layer of safety."
      },
      {
        id: "sec-4",
        q: "Does sync keep running if I log out?",
        a: "Yes! Once your MT5 account is connected, the SL/TP sync engine runs 24/7 in the cloud. Closing your browser, logging out, or shutting down your computer will NOT stop the sync. Your positions are monitored continuously."
      },
      {
        id: "sec-5",
        q: "I forgot my password — how do I reset?",
        a: "On the login page, click \"Forgot Password\". Enter your registered email and we will send a password reset link. The link expires after 1 hour for security.\n\nIf you do not receive the email within 5 minutes, check your spam folder. The email comes from noreply@laskysync.com."
      }
    ]
  },
  {
    id: "trading-sync",
    title: "Trading & Sync Behavior",
    icon: "📊",
    items: [
      {
        id: "trd-1",
        q: "How fast is the sync?",
        a: "Our engine polls your MT5 account every 500ms. When a SL/TP change is detected on one position, the new value is pushed to all other positions of the same symbol in parallel — typically completing within 1–2 seconds end-to-end.\n\nNote: speed also depends on MetaAPI cloud latency and your broker's order processing time, which we do not control."
      },
      {
        id: "trd-2",
        q: "What is a USC cent account?",
        a: "USC = US Dollar Cent account. Some brokers offer cent accounts where 1 lot = 1,000 units instead of 100,000, and balance/profit are shown in cents (e.g. $1.00 in cent = $0.01 USD).\n\nWe automatically detect cent accounts when you connect, display a purple CENT badge, and divide P/L by 100 for accurate USD display. No action needed from you — it just works."
      },
      {
        id: "trd-3",
        q: "Why is my P/L showing different from my MT5 terminal?",
        a: "A few possible reasons:\n\n• Cent account display normalization (we divide cent values by 100 for USD display).\n• Currency conversion delay if your account is not in USD.\n• MT5 terminal may show floating P/L while our system shows realized + floating combined.\n\nIf the difference is large or persistent, please submit a support ticket with screenshots."
      },
      {
        id: "trd-4",
        q: "Does sync work on all brokers and symbols?",
        a: "We support all brokers compatible with MetaAPI Cloud (200+ supported). The sync works on any symbol where you have multiple open positions — Forex, Gold (XAUUSD), Indices, Crypto CFDs, Stocks, etc.\n\nIf your broker is not listed during connect, please submit a ticket and we will check MetaAPI compatibility."
      },
      {
        id: "trd-5",
        q: "Will sync trigger on partial close or hedging positions?",
        a: "Partial close: yes — if you partially close a position and adjust SL/TP afterwards, the new SL/TP is synced to remaining same-symbol positions.\n\nHedging: yes — both buy and sell positions on the same symbol will receive the synced SL/TP update. If you want different SL/TP for buy vs sell, we recommend pausing sync temporarily during the adjustment."
      }
    ]
  },
  {
    id: "privacy-data",
    title: "Privacy & Data Protection",
    icon: "🛡️",
    items: [
      {
        id: "pdp-1",
        q: "What data do you collect about me?",
        a: "We collect the minimum necessary to operate the service:\n\n• Account: email address, hashed password\n• MT5: login number, encrypted password, broker, server (used for sync only)\n• Trading: position history, SL/TP change events (for your trade journal)\n• Technical: IP address, browser info (for security audit)\n\nFull details available in our Terms & Conditions."
      },
      {
        id: "pdp-2",
        q: "Is my MT5 password safe with you?",
        a: "Yes. Your MT5 password is encrypted with AES-256 envelope encryption using a key unique to your account. The encrypted value is stored in our database and only decrypted in-memory when connecting to MetaAPI.\n\nWe never log, display, or transmit your MT5 password in plaintext. Even our staff cannot read it."
      },
      {
        id: "pdp-3",
        q: "Can I export my data?",
        a: "Yes. Go to Profile → Data Export to download a JSON file containing all your data: account info, MT5 connections, license history, trade events, and audit logs. The export is OTP-verified for security and complies with PDPA 2010 right to data portability."
      },
      {
        id: "pdp-4",
        q: "How do I delete my account permanently?",
        a: "Go to Profile → Delete Account. The flow requires:\n\n1. Email OTP verification\n2. Typing the confirmation phrase \"DELETE MY ACCOUNT\"\n3. Final confirmation\n\nOnce deleted, your active MT5 connections are disconnected, your encryption keys are destroyed, and your account data is purged. Note: certain audit logs and TNC agreements are retained as required by law."
      }
    ]
  },
  {
    id: "history-reports",
    title: "Trade History & Reports",
    icon: "📜",
    items: [
      {
        id: "hist-1",
        q: "Where can I see my past sync events?",
        a: "Click the History tab in the sidebar. You will see a complete log of every sync event triggered by our engine — symbol, position ID, old SL/TP, new SL/TP, timestamp, and which positions were updated. Each event is a permanent snapshot."
      },
      {
        id: "hist-2",
        q: "Can I download my trade history?",
        a: "Yes. On the History page, click the Download CSV button. The CSV includes all sync events filtered by the current view (date range, account, symbol). You can open it in Excel, Google Sheets, or any spreadsheet tool for analysis or tax records."
      },
      {
        id: "hist-3",
        q: "How long is my history kept?",
        a: "Your trade history is retained for the lifetime of your account. Even after license expiry, your past history remains accessible from the History tab. History is only deleted when you delete your account permanently."
      }
    ]
  },
  {
    id: "troubleshooting",
    title: "Troubleshooting",
    icon: "⚠️",
    items: [
      {
        id: "tr-1",
        q: "My MT5 keeps showing \"disconnected\"",
        a: "Common causes:\n\n• Broker server is in maintenance (check broker website)\n• MT5 password recently changed on broker side (reconnect with new password)\n• Broker IP-whitelist enabled (disable IP-whitelist for VPS/cloud access)\n• Server name typo (double-check exact spelling)\n\nIf none of these apply, submit a ticket with your MT5 login number and broker name."
      },
      {
        id: "tr-2",
        q: "Sync not triggering — what to check",
        a: "Checklist:\n\n1. Status badge shows \"SL Sync ON\" or \"TP Sync ON\" (not OFF)\n2. License is still active (not expired)\n3. You have MORE THAN ONE position on the same symbol — sync only works when there are multiple positions to mirror to\n4. The SL/TP value actually changed (no change = no sync needed)\n\nStill stuck? Check History tab — if no events appear, submit a ticket."
      },
      {
        id: "tr-3",
        q: "\"Invalid license key\" error",
        a: "Possible reasons:\n\n• Typo in the license key (copy-paste exactly, including dashes)\n• License already activated on another account (one license = one user)\n• License has been revoked due to abuse or violation of terms\n\nSecurity note: repeated failed license attempts trigger automatic lockout. If you are sure the key is yours, contact support immediately."
      },
      {
        id: "tr-4",
        q: "My account is locked — what does this mean?",
        a: "Account lockouts protect against unauthorized access and license abuse. Reasons include:\n\n• Multiple failed password attempts (5 = temporary cooldown, 10 = hard lock)\n• Multiple invalid license activation attempts\n• Suspicious activity flagged by our security system\n\nTo unlock: submit an unlock request via the lockout screen, or contact support with proof of identity. Most cases are resolved within 24 hours."
      }
    ]
  },
  {
    id: "support-contact",
    title: "Support & Contact",
    icon: "💬",
    items: [
      {
        id: "sup-1",
        q: "How do I submit a ticket?",
        a: "Switch to the Submit Ticket tab above. Choose a category, write your subject and message, attach files if needed, then click Submit. Your ticket will appear immediately in the My Tickets tab where you can track status and reply to our responses."
      },
      {
        id: "sup-2",
        q: "How long until I get a reply?",
        a: "Typical response time: within 24 hours on business days (Mon–Fri, Malaysia time GMT+8). Critical issues (account locked, payment problem, sync failure) are prioritized and usually answered within a few hours.\n\nWe do not currently provide phone support — all communication is via tickets/email for proper documentation."
      },
      {
        id: "sup-3",
        q: "Can I reach you outside the dashboard?",
        a: "For fastest service, please use the Submit Ticket tab — it routes directly to our support system and creates a tracked record.\n\nAlternative channels:\n• Technical Support: laskysystem@gmail.com\n• Company / Legal / Data Protection: shadowtechsystem@gmail.com\n\nAvoid sending sensitive info (passwords, full license keys) via email — use the in-app ticket system for those."
      }
    ]
  }
];

// ============================================================================
// PLANS PAGE — Pricing & License Purchase (F4)
// Display-only UI for bank presentation demo.
// Detects real user.accounts for "Add to existing" dropdown.
// Buy Now → Coming Soon modal (payment gateway pending business bank account).
// ============================================================================
const PRICING_PLANS = [
  { id: "7d", name: "Starter", duration: "7 Days",   days: 7,  priceUSD: 15,  anchorUSD: 20,  discount: null, perMonthUSD: null, badge: null,              color: "#64748B" },
  { id: "1m", name: "Basic",   duration: "1 Month",  days: 30, priceUSD: 55,  anchorUSD: 65,  discount: 15,   perMonthUSD: null, badge: "⭐ STANDARD",     color: "#22D3EE" },
  { id: "3m", name: "Popular", duration: "3 Months", days: 90, priceUSD: 150, anchorUSD: 195, discount: 23,   perMonthUSD: 50,   badge: "🔥 MOST POPULAR", color: "#F59E0B" },
];

function PlansPage({ user }) {
  const [selections, setSelections] = useState({}); // { planId: licenseId | "new" }
  const [comingSoon, setComingSoon] = useState(null); // selected plan for modal

  const existingAccounts = (user?.accounts || []).filter(a => a?.license?.id);
  const hasAccounts = existingAccounts.length > 0;

  const handleBuyNow = (plan) => {
    const sel = selections[plan.id];
    if (!sel) return;
    setComingSoon({ plan, selection: sel });
  };

  const getSelectionLabel = (val) => {
    if (val === "new") return "Create new account";
    const acc = existingAccounts.find(a => a.license.id === val);
    if (!acc) return "—";
    return acc.mt5Account ? `MT5 ${acc.mt5Account}` : `License ${acc.license.key}`;
  };

  return (
    <div style={{ color: "#E2E8F0", fontFamily: "inherit" }}>
      {/* Header */}
      <div style={{ marginBottom: 32, textAlign: "center" }}>
        <h2 style={{ fontSize: 28, fontWeight: 800, color: "#F1F5F9", margin: 0, marginBottom: 8, letterSpacing: "-0.02em" }}>Choose Your Plan</h2>
        <p style={{ color: "#94A3B8", fontSize: 14, margin: 0, fontWeight: 400 }}>1 License = 1 Active MT5 Connection · Swap accounts anytime</p>
      </div>

      {/* LAUNCH PRICING hero banner */}
      <div style={{ maxWidth: 720, margin: "0 auto 28px", padding: "32px 28px", background: "linear-gradient(135deg, rgba(34, 211, 238, 0.08) 0%, rgba(34, 211, 238, 0.04) 100%)", border: "1.5px solid rgba(34, 211, 238, 0.35)", borderRadius: 16, textAlign: "center" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, marginBottom: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 32 }}>🎉</span>
          <h3 style={{ fontSize: 32, fontWeight: 800, color: "#22D3EE", margin: 0, letterSpacing: "0.05em" }}>LAUNCH PRICING</h3>
          <span style={{ fontSize: 32 }}>🎉</span>
        </div>
        <p style={{ fontSize: 15, color: "#E2E8F0", margin: 0, fontWeight: 400 }}>Special pricing for our early supporters</p>
      </div>

      {/* Info banner */}
      <div style={{ padding: "14px 18px", borderRadius: 12, background: "#22D3EE08", border: "1px solid #22D3EE22", marginBottom: 28, display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ fontSize: 20 }}>💡</div>
        <div style={{ fontSize: 13, color: "#CBD5E1", lineHeight: 1.5 }}>
          Extend an existing license to add more time, or create a new license for a parallel MT5 account. Time stacks — buy 3 months on a license with 5 days left = 95 days remaining.
        </div>
      </div>

      {/* Plans grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16, marginBottom: 32 }}>
        {PRICING_PLANS.map(plan => {
          const isPopular = plan.id === "3m";
          const highlighted = isPopular;
          const sel = selections[plan.id];

          return (
            <div key={plan.id} style={{
              position: "relative",
              padding: 24,
              borderRadius: 16,
              background: highlighted ? "#0A0A12" : "#0A0A12",
              border: highlighted ? `2px solid ${plan.color}` : "1px solid #ffffff0F",
              boxShadow: highlighted ? `0 0 24px ${plan.color}22` : "none",
              display: "flex",
              flexDirection: "column",
            }}>
              {/* Badge */}
              {plan.badge && (
                <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", padding: "4px 12px", borderRadius: 12, background: plan.color, color: "#06060B", fontSize: 10, fontWeight: 800, letterSpacing: "0.05em", whiteSpace: "nowrap" }}>
                  {plan.badge}
                </div>
              )}

              {/* Plan name + duration */}
              <div style={{ textAlign: "center", marginBottom: 16, marginTop: plan.badge ? 8 : 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: plan.color, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>{plan.name}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#F1F5F9" }}>{plan.duration}</div>
              </div>

              {/* Price */}
              <div style={{ textAlign: "center", marginBottom: 20 }}>
                {plan.anchorUSD && (
                  <div style={{ color: "#64748B", fontSize: 14, textDecoration: "line-through", marginBottom: 2, fontWeight: 500 }}>${plan.anchorUSD}</div>
                )}
                <div style={{ fontSize: 34, fontWeight: 800, color: "#F1F5F9", letterSpacing: "-0.02em", lineHeight: 1 }}>${plan.priceUSD}</div>
                <div style={{ color: "#64748B", fontSize: 11, marginTop: 4, fontWeight: 500 }}>
                  USD{plan.perMonthUSD && ` · $${plan.perMonthUSD}/month`}
                </div>
                {plan.discount && (
                  <div style={{ marginTop: 10, display: "inline-block", padding: "4px 10px", borderRadius: 8, background: "#EF444415", color: "#EF4444", fontSize: 11, fontWeight: 700, letterSpacing: "0.05em" }}>
                    {plan.discount}% OFF
                  </div>
                )}
              </div>

              {/* Divider */}
              <div style={{ height: 1, background: "#ffffff08", marginBottom: 16 }} />

              {/* Dropdown */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 11, color: "#64748B", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 6 }}>Select Action</label>
                <select
                  value={sel || ""}
                  onChange={(e) => setSelections(s => ({ ...s, [plan.id]: e.target.value }))}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 8,
                    background: "#06060B",
                    border: "1px solid #1E293B",
                    color: sel ? "#F1F5F9" : "#64748B",
                    fontSize: 13,
                    fontFamily: "inherit",
                    cursor: "pointer",
                    outline: "none",
                  }}
                >
                  <option value="" disabled>— Choose one —</option>
                  {existingAccounts.map(acc => (
                    <option key={acc.license.id} value={acc.license.id}>
                      Add to {acc.mt5Account ? `MT5 ${acc.mt5Account}` : `License ${acc.license.key}`}
                    </option>
                  ))}
                  <option value="new">Create new account</option>
                </select>
                {!hasAccounts && (
                  <div style={{ fontSize: 10, color: "#475569", marginTop: 6, fontStyle: "italic" }}>No existing license — purchase creates a new one</div>
                )}
              </div>

              {/* Buy button */}
              <button
                onClick={() => handleBuyNow(plan)}
                disabled={!sel}
                style={{
                  width: "100%",
                  padding: "12px",
                  borderRadius: 8,
                  background: sel ? plan.color : "#1E293B",
                  color: sel ? "#06060B" : "#475569",
                  border: "none",
                  fontSize: 14,
                  fontWeight: 700,
                  fontFamily: "inherit",
                  cursor: sel ? "pointer" : "not-allowed",
                  letterSpacing: "0.02em",
                  transition: "all 0.2s",
                  marginTop: "auto",
                }}
              >
                {sel ? `Buy Now — $${plan.priceUSD}` : "Select action first"}
              </button>
            </div>
          );
        })}
      </div>

      {/* Features list */}
      <div style={{ padding: 20, borderRadius: 12, background: "#0A0A12", border: "1px solid #ffffff08" }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: "#F1F5F9", margin: 0, marginBottom: 12, letterSpacing: "0.05em", textTransform: "uppercase" }}>All Plans Include</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
          {[
            { icon: "⚡", title: "Real-time SL/TP Sync", desc: "500ms detection across positions" },
            { icon: "🔐", title: "Encrypted Credentials", desc: "Your MT5 login stays protected" },
            { icon: "☁️", title: "24/7 Cloud Engine", desc: "Runs without your PC on" },
            { icon: "🔄", title: "Swap MT5 Anytime", desc: "Change connected account freely" },
            { icon: "📊", title: "Full Trade History", desc: "Audit logs & CSV export" },
            { icon: "🇲🇾", title: "Malaysian Support", desc: "PDPA 2010 compliant" },
          ].map((f, i) => (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <div style={{ fontSize: 18 }}>{f.icon}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#E2E8F0" }}>{f.title}</div>
                <div style={{ fontSize: 11, color: "#64748B", marginTop: 2 }}>{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Coming Soon Modal */}
      {comingSoon && (
        <div onClick={() => setComingSoon(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480, width: "100%", padding: 32, borderRadius: 16, background: "#0A0A12", border: "1px solid #22D3EE33", boxShadow: "0 20px 60px rgba(34,211,238,0.15)" }}>
            <div style={{ fontSize: 40, textAlign: "center", marginBottom: 16 }}>🚧</div>
            <h3 style={{ fontSize: 20, fontWeight: 800, color: "#F1F5F9", textAlign: "center", margin: 0, marginBottom: 12 }}>Payment Gateway Coming Soon</h3>
            <p style={{ fontSize: 13, color: "#CBD5E1", lineHeight: 1.6, textAlign: "center", margin: 0, marginBottom: 20 }}>
              Direct online payment will be available once <strong style={{ color: "#22D3EE" }}>Shadow Technologies</strong> business banking is finalized. All transactions will be processed through our official company account — never through personal banking.
            </p>

            <div style={{ padding: 14, borderRadius: 10, background: "#06060B", border: "1px solid #1E293B", marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: "#64748B", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 8 }}>Your Selection</div>
              <div style={{ fontSize: 14, color: "#F1F5F9", fontWeight: 600 }}>{comingSoon.plan.duration} — ${comingSoon.plan.priceUSD}</div>
              <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 4 }}>→ {getSelectionLabel(comingSoon.selection)}</div>
            </div>

            <div style={{ fontSize: 12, color: "#94A3B8", lineHeight: 1.6, marginBottom: 20 }}>
              <div style={{ fontWeight: 600, color: "#E2E8F0", marginBottom: 6 }}>Meanwhile, get your license key from:</div>
              <div style={{ paddingLeft: 12 }}>
                <div style={{ marginBottom: 4 }}>🛒 <strong style={{ color: "#22D3EE", fontWeight: 700 }}>Shopee</strong> Official Store</div>
                <div style={{ marginBottom: 4 }}>🎵 <strong style={{ color: "#22D3EE", fontWeight: 700 }}>TikTok</strong> Shop</div>
                <div>💬 <strong style={{ color: "#22D3EE", fontWeight: 700 }}>Telegram</strong> Channel (coming soon)</div>
              </div>
            </div>

            <button
              onClick={() => setComingSoon(null)}
              style={{ width: "100%", padding: "12px", borderRadius: 8, background: "#22D3EE", color: "#06060B", border: "none", fontSize: 14, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}
            >
              OK, Got It
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SupportPage({ session }) {
  const [tab, setTab] = useState("faq");
  return (
    <div style={{ animation: "fadeUp 0.4s ease", maxWidth: 760, margin: "0 auto" }}>
      <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 6, color: "#F1F5F9" }}>Support</h2>
      <p style={{ color: "#94A3B8", fontSize: 14, marginBottom: 24, fontWeight: 300 }}>Browse FAQ, submit a ticket, or view your past tickets.</p>
      <div style={{ display: "flex", gap: 4, padding: 4, borderRadius: 12, background: "#ffffff04", marginBottom: 24 }}>
        {[{ id: "faq", label: "FAQ" }, { id: "ticket", label: "Submit Ticket" }, { id: "history", label: "My Tickets" }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ flex: 1, padding: "10px", borderRadius: 8, background: tab === t.id ? "#1E293B" : "transparent", border: "none", color: tab === t.id ? "#E2E8F0" : "#64748B", cursor: "pointer", fontSize: 14, fontFamily: "inherit", fontWeight: tab === t.id ? 600 : 400 }}>{t.label}</button>
        ))}
      </div>
      {tab === "faq" && <_FAQTab />}
      {tab === "ticket" && <_TicketTab session={session} onSubmitted={() => setTab("history")} />}
      {tab === "history" && <_TicketHistoryTab session={session} />}
    </div>
  );
}

function _FAQTab() {
  const [query, setQuery] = useState("");
  const [openCategoryId, setOpenCategoryId] = useState(null);
  const [openItemId, setOpenItemId] = useState(null);

  // Search across all categories — flatten + filter
  const isSearching = query.trim().length > 0;
  const searchResults = isSearching
    ? _FAQS.flatMap(cat =>
        cat.items
          .filter(item =>
            item.q.toLowerCase().includes(query.toLowerCase()) ||
            item.a.toLowerCase().includes(query.toLowerCase())
          )
          .map(item => ({ ...item, categoryTitle: cat.title, categoryIcon: cat.icon }))
      )
    : [];

  return (
    <div>
      <div style={{ position: "relative", marginBottom: 16 }}>
        <input
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpenItemId(null); }}
          placeholder="🔍 Search FAQ — e.g. 'server', 'password', 'license'"
          style={{ ..._inputStyle, paddingLeft: 16 }}
        />
        {query && (
          <button
            onClick={() => setQuery("")}
            style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#94A3B8", cursor: "pointer", fontSize: 16 }}
          >✕</button>
        )}
      </div>

      {/* SEARCH MODE — flat list across all categories */}
      {isSearching && (
        <>
          {searchResults.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", borderRadius: 12, background: "#0A0A12", border: "1px dashed #ffffff10" }}>
              <div style={{ color: "#CBD5E1", fontSize: 14, marginBottom: 8 }}>No FAQs match "{query}".</div>
              <div style={{ color: "#94A3B8", fontSize: 12 }}>Try the Submit Ticket tab to ask us directly.</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ color: "#22D3EE", fontSize: 12, fontWeight: 600, marginBottom: 4, letterSpacing: 1, textTransform: "uppercase" }}>
                {searchResults.length} result{searchResults.length === 1 ? "" : "s"}
              </div>
              {searchResults.map(item => {
                const isOpen = openItemId === item.id;
                return (
                  <div key={item.id} style={{ borderRadius: 12, background: isOpen ? "#0A0A12" : "#ffffff02", border: `1px solid ${isOpen ? "#22D3EE15" : "#ffffff08"}`, overflow: "hidden", transition: "all 0.2s" }}>
                    <button
                      onClick={() => setOpenItemId(isOpen ? null : item.id)}
                      style={{ width: "100%", padding: "16px 20px", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, textAlign: "left", fontFamily: "inherit" }}
                    >
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 12 }}>{item.categoryIcon}</span>
                          <span style={{ color: "#22D3EE", fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase" }}>{item.categoryTitle}</span>
                        </div>
                        <span style={{ color: isOpen ? "#F1F5F9" : "#E2E8F0", fontSize: 15, fontWeight: isOpen ? 600 : 500, lineHeight: 1.5 }}>{item.q}</span>
                      </div>
                      <span style={{ color: isOpen ? "#22D3EE" : "#94A3B8", fontSize: 18, flexShrink: 0, transition: "transform 0.2s", transform: isOpen ? "rotate(45deg)" : "rotate(0deg)" }}>+</span>
                    </button>
                    {isOpen && (
                      <div style={{ padding: "0 20px 20px 20px", animation: "fadeUp 0.2s ease" }}>
                        <div style={{ color: "#CBD5E1", fontSize: 14, lineHeight: 1.8, whiteSpace: "pre-line" }}>{item.a}</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* BROWSE MODE — 2-level accordion (category → item → answer) */}
      {!isSearching && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {_FAQS.map(cat => {
            const isCatOpen = openCategoryId === cat.id;
            return (
              <div key={cat.id} style={{ borderRadius: 12, background: isCatOpen ? "#0A0A12" : "#ffffff03", border: `1px solid ${isCatOpen ? "#22D3EE25" : "#ffffff08"}`, overflow: "hidden", transition: "all 0.2s" }}>
                {/* CATEGORY HEADER */}
                <button
                  onClick={() => { setOpenCategoryId(isCatOpen ? null : cat.id); setOpenItemId(null); }}
                  style={{ width: "100%", padding: "18px 20px", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, textAlign: "left", fontFamily: "inherit" }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 14, flex: 1 }}>
                    <span style={{ fontSize: 22, flexShrink: 0 }}>{cat.icon}</span>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <span style={{ color: isCatOpen ? "#22D3EE" : "#F1F5F9", fontSize: 16, fontWeight: 700, letterSpacing: -0.2 }}>{cat.title}</span>
                      <span style={{ color: "#94A3B8", fontSize: 12, fontWeight: 500 }}>{cat.items.length} {cat.items.length === 1 ? "question" : "questions"}</span>
                    </div>
                  </div>
                  <span style={{ color: isCatOpen ? "#22D3EE" : "#94A3B8", fontSize: 20, flexShrink: 0, transition: "transform 0.2s", transform: isCatOpen ? "rotate(45deg)" : "rotate(0deg)", fontWeight: 300 }}>+</span>
                </button>

                {/* ITEMS LIST (when category open) */}
                {isCatOpen && (
                  <div style={{ padding: "0 12px 12px 12px", display: "flex", flexDirection: "column", gap: 6, animation: "fadeUp 0.25s ease" }}>
                    {cat.items.map(item => {
                      const isItemOpen = openItemId === item.id;
                      return (
                        <div key={item.id} style={{ borderRadius: 8, background: isItemOpen ? "#13131F" : "transparent", border: `1px solid ${isItemOpen ? "#22D3EE20" : "#ffffff05"}`, overflow: "hidden", transition: "all 0.2s" }}>
                          <button
                            onClick={() => setOpenItemId(isItemOpen ? null : item.id)}
                            style={{ width: "100%", padding: "12px 16px", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, textAlign: "left", fontFamily: "inherit" }}
                          >
                            <span style={{ color: isItemOpen ? "#F1F5F9" : "#CBD5E1", fontSize: 14, fontWeight: isItemOpen ? 600 : 500, lineHeight: 1.5, flex: 1 }}>{item.q}</span>
                            <span style={{ color: isItemOpen ? "#22D3EE" : "#94A3B8", fontSize: 16, flexShrink: 0, transition: "transform 0.2s", transform: isItemOpen ? "rotate(45deg)" : "rotate(0deg)", fontWeight: 300 }}>+</span>
                          </button>
                          {isItemOpen && (
                            <div style={{ padding: "0 16px 14px 16px", animation: "fadeUp 0.2s ease" }}>
                              <div style={{ color: "#CBD5E1", fontSize: 13.5, lineHeight: 1.8, whiteSpace: "pre-line" }}>{item.a}</div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function _TicketTab({ session, onSubmitted }) {
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => { if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl); };
  }, [imagePreviewUrl]);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) { setImageFile(null); setImagePreviewUrl(""); return; }
    if (file.size > 5 * 1024 * 1024) { setMsg({ type: "error", text: "Image too large. Maximum 5MB allowed." }); e.target.value = ""; return; }
    const allowed = ["image/jpeg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) { setMsg({ type: "error", text: "Only JPEG, PNG, or WebP images allowed." }); e.target.value = ""; return; }
    setMsg(null);
    setImageFile(file);
    setImagePreviewUrl(URL.createObjectURL(file));
  };

  const submit = async () => {
    setMsg(null);
    if (subject.trim().length < 5) return setMsg({ type: "error", text: "Subject must be at least 5 characters." });
    if (description.trim().length < 20) return setMsg({ type: "error", text: "Description must be at least 20 characters." });
    setBusy(true);
    try {
      const formData = new FormData();
      formData.append("subject", subject.trim());
      formData.append("description", description.trim());
      if (imageFile) formData.append("attachment", imageFile);

      const res = await fetch(`${API_URL}/api/support/ticket`, {
        method: "POST",
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: formData,
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Failed to submit ticket.");
      setMsg({ type: "success", text: `Ticket submitted! ID: ${d.ticketId}. Check your email for confirmation.` });
      setSubject(""); setDescription(""); setImageFile(null); setImagePreviewUrl("");
      setTimeout(() => onSubmitted?.(), 2000);
    } catch (e) { setMsg({ type: "error", text: e.message }); }
    setBusy(false);
  };

  return (
    <div style={{ padding: 24, borderRadius: 14, background: "#0A0A12", border: "1px solid #ffffff08" }}>
      <h3 style={{ color: "#F1F5F9", fontSize: 16, fontWeight: 700, marginTop: 0, marginBottom: 20 }}>Submit a Support Ticket</h3>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <label style={_labelStyle}>Subject * (5–200 chars)</label>
          <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Brief summary of your issue" maxLength={200} style={_inputStyle} />
          <div style={{ textAlign: "right", color: "#475569", fontSize: 11, marginTop: 4 }}>{subject.length}/200</div>
        </div>
        <div>
          <label style={_labelStyle}>Description * (20–5000 chars)</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={6} maxLength={5000} placeholder="Please describe your issue in detail. Include MT5 account number, broker, and any error messages if relevant." style={{ ..._inputStyle, resize: "vertical", fontFamily: "inherit", lineHeight: 1.6 }} />
          <div style={{ textAlign: "right", color: description.length < 20 ? "#FCA5A5" : "#475569", fontSize: 11, marginTop: 4 }}>
            {description.length < 20 ? `${20 - description.length} more characters needed` : `${description.length}/5000`}
          </div>
        </div>
        <div>
          <label style={_labelStyle}>Attachment (optional — JPEG / PNG / WebP, max 5MB)</label>
          <div style={{ padding: 12, background: "#ffffff04", border: "1px dashed #ffffff15", borderRadius: 10, fontSize: 13 }}>
            {!imageFile ? (
              <label style={{ display: "flex", alignItems: "center", justifyContent: "center", cursor: busy ? "not-allowed" : "pointer", color: "#CBD5E1", fontWeight: 500, gap: 8 }}>
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImageChange} disabled={busy} style={{ display: "none" }} />
                📎 Click to attach screenshot
              </label>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {imagePreviewUrl && <img src={imagePreviewUrl} alt="preview" style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6, border: "1px solid #1E293B" }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: "#E2E8F0", fontSize: 13, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{imageFile.name}</div>
                  <div style={{ color: "#CBD5E1", fontSize: 11 }}>{(imageFile.size / 1024).toFixed(1)} KB</div>
                </div>
                <button onClick={() => { setImageFile(null); setImagePreviewUrl(""); }} disabled={busy} style={{ background: "transparent", border: "none", color: "#FCA5A5", cursor: "pointer", fontSize: 13, fontFamily: "inherit", fontWeight: 500 }}>Remove</button>
              </div>
            )}
          </div>
        </div>
        <button onClick={submit} disabled={busy} style={{ padding: 14, marginTop: 8, background: busy ? "#1E293B" : "linear-gradient(135deg, #0891B2, #22D3EE)", border: "none", borderRadius: 10, color: "#fff", fontSize: 15, fontWeight: 700, cursor: busy ? "wait" : "pointer", fontFamily: "inherit" }}>{busy ? "Submitting..." : "Submit Ticket →"}</button>
        {msg && <div style={{ padding: "10px 14px", borderRadius: 8, background: msg.type === "error" ? "#EF444410" : "#22C55E10", border: `1px solid ${msg.type === "error" ? "#EF444420" : "#22C55E20"}`, color: msg.type === "error" ? "#FCA5A5" : "#86EFAC", fontSize: 13, lineHeight: 1.5 }}>{msg.text}</div>}
        <p style={{ color: "#475569", fontSize: 11, textAlign: "center", margin: 0, lineHeight: 1.5 }}>We'll respond within 24–48 hours (Malaysia business hours). You'll receive a confirmation email with your ticket ID.</p>
      </div>
    </div>
  );
}

function _TicketHistoryTab({ session }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicketId, setSelectedTicketId] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try { const res = await fetch(`${API_URL}/api/support/tickets/mine`, { headers: { Authorization: `Bearer ${session.access_token}` } }); const d = await res.json(); if (mounted) setTickets(d.tickets || []); }
      catch (e) { console.error(e); }
      if (mounted) setLoading(false);
    })();
    return () => { mounted = false; };
  }, [session]);

  if (selectedTicketId) {
    return <_UserTicketDetailView session={session} ticketId={selectedTicketId} onBack={() => setSelectedTicketId(null)} />;
  }

  if (loading) return <div style={{ padding: 32, textAlign: "center", color: "#475569", fontSize: 13 }}>Loading...</div>;
  if (tickets.length === 0) return (
    <div style={{ padding: 48, textAlign: "center", borderRadius: 12, background: "#0A0A12", border: "1px dashed #ffffff10" }}>
      <div style={{ color: "#64748B", fontSize: 14, marginBottom: 8 }}>No tickets yet.</div>
      <div style={{ color: "#475569", fontSize: 12 }}>Your submitted tickets will appear here.</div>
    </div>
  );

  const statusColor = { inbox: "#F59E0B", read: "#22D3EE", pending: "#A78BFA", solved: "#22C55E" };
  const statusUserLabel = { inbox: "New", read: "Reviewing", pending: "Pending", solved: "Solved" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {tickets.map(t => (
        <div key={t.id}
          onClick={() => setSelectedTicketId(t.ticket_id)}
          style={{ padding: "16px 20px", borderRadius: 12, background: "#0A0A12", border: "1px solid #ffffff08", cursor: "pointer", transition: "border-color 0.2s" }}
          onMouseEnter={e => e.currentTarget.style.borderColor = "#22D3EE20"}
          onMouseLeave={e => e.currentTarget.style.borderColor = "#ffffff08"}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ color: "#F1F5F9", fontSize: 14, fontWeight: 600 }}>{t.subject}</span>
            <span style={{ padding: "3px 10px", borderRadius: 6, background: `${statusColor[t.status] || "#475569"}15`, border: `1px solid ${statusColor[t.status] || "#475569"}30`, color: statusColor[t.status] || "#475569", fontSize: 11, fontWeight: 600, textTransform: "uppercase" }}>
              {statusUserLabel[t.status] || t.status}
            </span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontFamily: "monospace", color: "#22D3EE", fontSize: 11 }}>{t.ticket_id || "—"}</span>
            <span style={{ color: "#64748B", fontSize: 11 }}>{new Date(t.created_at).toLocaleDateString("en-GB")} · tap to view →</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function _UserTicketDetailView({ session, ticketId, onBack }) {
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [replyImage, setReplyImage] = useState(null);
  const [replyImagePreview, setReplyImagePreview] = useState("");
  const [sending, setSending] = useState(false);
  const [replyMsg, setReplyMsg] = useState(null);
  const messagesEndRef = useRef(null);

  const loadDetail = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/support/ticket/${ticketId}`, { headers: { Authorization: `Bearer ${session.access_token}` } });
      const d = await res.json();
      if (res.ok) { setTicket(d.ticket); setMessages(d.messages || []); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { loadDetail(); }, [ticketId]);

  useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleReplyImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) { setReplyImage(null); setReplyImagePreview(""); return; }
    if (file.size > 5 * 1024 * 1024) { setReplyMsg({ type: "error", text: "Image too large (max 5MB)." }); e.target.value = ""; return; }
    if (!["image/jpeg","image/png","image/webp"].includes(file.type)) { setReplyMsg({ type: "error", text: "Only JPEG, PNG, WebP allowed." }); e.target.value = ""; return; }
    setReplyImage(file);
    setReplyImagePreview(URL.createObjectURL(file));
  };

  const handleReply = async () => {
    if (!reply.trim() || sending) return;
    setSending(true); setReplyMsg(null);
    try {
      const formData = new FormData();
      formData.append("message", reply.trim());
      if (replyImage) formData.append("attachment", replyImage);
      const res = await fetch(`${API_URL}/api/support/ticket/${ticketId}/reply`, {
        method: "POST",
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: formData,
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Failed to send reply");
      setReply(""); setReplyImage(null); setReplyImagePreview("");
      setReplyMsg({ type: "success", text: "Reply sent!" });
      loadDetail();
    } catch (e) { setReplyMsg({ type: "error", text: e.message }); }
    setSending(false);
  };

  const statusColor = { inbox: "#F59E0B", read: "#22D3EE", pending: "#A78BFA", solved: "#22C55E" };
  const statusUserLabel = { inbox: "New", read: "Being Reviewed", pending: "Pending Investigation", solved: "Solved" };

  if (loading) return <div style={{ padding: 32, textAlign: "center", color: "#475569", fontSize: 13 }}>Loading ticket...</div>;
  if (!ticket) return (
    <div style={{ padding: 24, textAlign: "center" }}>
      <button onClick={onBack} style={{ background: "none", border: "none", color: "#22D3EE", cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>← Back to My Tickets</button>
    </div>
  );

  return (
    <div style={{ animation: "fadeUp 0.3s ease" }}>
      <button onClick={onBack} style={{ background: "none", border: "none", color: "#22D3EE", cursor: "pointer", fontSize: 13, fontFamily: "inherit", padding: "0 0 12px 0", display: "flex", alignItems: "center", gap: 6 }}>← Back to My Tickets</button>
      <div style={{ padding: "14px 18px", borderRadius: 12, background: "#0A0A12", border: "1px solid #1E293B", marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div>
            <div style={{ color: "#F1F5F9", fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{ticket.subject}</div>
            <span style={{ color: "#22D3EE", fontSize: 11, fontFamily: "monospace" }}>{ticket.ticket_id}</span>
          </div>
          <span style={{ padding: "3px 10px", borderRadius: 6, background: `${statusColor[ticket.status] || "#475569"}15`, border: `1px solid ${statusColor[ticket.status] || "#475569"}30`, color: statusColor[ticket.status] || "#475569", fontSize: 11, fontWeight: 700, textTransform: "uppercase", flexShrink: 0 }}>
            {statusUserLabel[ticket.status] || ticket.status}
          </span>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16, maxHeight: 360, overflowY: "auto" }}>
        {messages.map(m => {
          const isAdmin = m.sender_type === "admin";
          return (
            <div key={m.id} style={{ display: "flex", justifyContent: isAdmin ? "flex-start" : "flex-end" }}>
              <div style={{ maxWidth: "78%", padding: "12px 16px", borderRadius: 12, background: isAdmin ? "#22D3EE10" : "#A78BFA10", border: `1px solid ${isAdmin ? "#22D3EE20" : "#A78BFA20"}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: isAdmin ? "#22D3EE" : "#A78BFA" }}>{isAdmin ? "LASKY Support" : "You"}</span>
                  <span style={{ color: "#334155", fontSize: 10 }}>·</span>
                  <span style={{ color: "#475569", fontSize: 10 }}>{new Date(m.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                </div>
                <div style={{ color: "#E2E8F0", fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{m.message}</div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {ticket.status !== "solved" ? (
        <div style={{ padding: 16, borderRadius: 12, background: "#0A0A12", border: "1px solid #1E293B" }}>
          <div style={{ color: "#CBD5E1", fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Add a reply</div>
          <textarea value={reply} onChange={e => setReply(e.target.value)} rows={4} maxLength={5000}
            placeholder="Type your message to support..."
            style={{ width: "100%", padding: "10px 14px", background: "#ffffff04", border: "1px solid #ffffff0a", borderRadius: 8, color: "#E2E8F0", fontSize: 13, fontFamily: "inherit", outline: "none", resize: "vertical", boxSizing: "border-box" }} />
          {/* Image attachment */}
          <div style={{ marginTop: 8 }}>
            {!replyImage ? (
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: sending ? "not-allowed" : "pointer", color: "#475569", fontSize: 12, fontWeight: 500 }}>
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleReplyImageChange} disabled={sending} style={{ display: "none" }} />
                📎 Attach screenshot (optional, max 5MB)
              </label>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", background: "#ffffff04", borderRadius: 8, border: "1px solid #1E293B" }}>
                {replyImagePreview && <img src={replyImagePreview} alt="preview" style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 4 }} />}
                <div style={{ flex: 1, fontSize: 12, color: "#CBD5E1", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{replyImage.name}</div>
                <button onClick={() => { setReplyImage(null); setReplyImagePreview(""); }} style={{ background: "none", border: "none", color: "#EF4444", cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>✕</button>
              </div>
            )}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
            {replyMsg ? (<span style={{ color: replyMsg.type === "error" ? "#FCA5A5" : "#86EFAC", fontSize: 12 }}>{replyMsg.text}</span>) : <span />}
            <button onClick={handleReply} disabled={sending || !reply.trim()}
              style={{ padding: "10px 24px", borderRadius: 8, background: sending || !reply.trim() ? "#1E293B" : "linear-gradient(135deg, #7C3AED, #A78BFA)", border: "none", color: sending || !reply.trim() ? "#475569" : "#fff", cursor: sending || !reply.trim() ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 600, fontFamily: "inherit" }}>
              {sending ? "Sending..." : "Send →"}
            </button>
          </div>
        </div>
      ) : (
        <div style={{ padding: "12px 16px", borderRadius: 10, background: "#22C55E08", border: "1px solid #22C55E20", color: "#86EFAC", fontSize: 13, textAlign: "center" }}>
          ✓ Ticket solved. Submit a new ticket if you need further help.
        </div>
      )}
    </div>
  );
}
